import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  LinkIcon,
  Squares2X2Icon,
  EnvelopeIcon,
  GiftIcon,
  EyeIcon,
  CheckIcon,
  XMarkIcon,
  ClipboardIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  PaintBrushIcon,
  CloudArrowUpIcon,
  ArrowsUpDownIcon,
  SwatchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  VideoCameraIcon,
  TagIcon,
  CurrencyDollarIcon,
  StarIcon,
  MicrophoneIcon,
  ShareIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  TicketIcon,
  Cog6ToothIcon,
  LightBulbIcon,
  UserCircleIcon,
  CameraIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../services/supabase';
import { uploadImageToWasabi } from '../services/wasabiUpload';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import PrePaidSponsorsModal from './PrePaidSponsorsModal';
import CreatorChat from '../components/CreatorChat';
import CreatorHubDashboard from '../components/CreatorHubDashboard';
import CreatorCourse from '../components/CreatorCourse';
import OrderChat from '../components/OrderChat';
import NetworkTab from '../components/NetworkTab';
import toast from 'react-hot-toast';
import PayoutOnboardingInline from '../components/payout/PayoutOnboardingInline';

// Order types where the creator-customer chat panel makes no sense
// (no delivery to discuss, no follow-up details to collect). All other
// orders — regular shoutouts, social collabs, business orders — get
// the chat so the creator can reply and the customer is notified.
const NO_CHAT_TYPES = new Set(['credit_purchase', 'donation']);

// ShoutOut occasion types exposed in the creator dashboard multiselect.
// Must match the keys that ShoutOutProfilePage.tsx filters against in its
// `occasions` memo (hidden: express/gift/debate/corporate; advice folds
// into encouragement on the public page).
const SHOUTOUT_TYPE_OPTIONS: { key: string; label: string; emoji: string }[] = [
  { key: 'birthday', label: 'Birthday', emoji: '🎂' },
  { key: 'roast', label: 'Roast', emoji: '🔥' },
  { key: 'encouragement', label: 'Advice / Encouragement', emoji: '💪' },
  { key: 'announcement', label: 'Announcement', emoji: '📣' },
  { key: 'celebrate', label: 'Celebrate A Win', emoji: '🏆' },
  { key: 'anniversary', label: 'Anniversary', emoji: '💍' },
];

// Types
interface TalentProfile {
  id: string;
  user_id: string;
  username?: string;
  full_name?: string;
  temp_full_name?: string; // Name stored before onboarding completion
  temp_avatar_url?: string;
  bio?: string;
  social_accounts?: SocialAccount[];
  // Social handles from talent_profiles table (synced from admin)
  twitter_handle?: string;
  instagram_handle?: string;
  facebook_handle?: string;
  tiktok_handle?: string;
  rumble_handle?: string;
  rumble_type?: 'c' | 'user'; // 'c' for channel, 'user' for user profile
  youtube_handle?: string;
  podcast_rss_url?: string;
  podcast_name?: string;
  podcast_platform_links?: Record<string, string>;
  fulfillment_time_hours?: number;
  base_pricing?: number;
  current_pricing?: number;
  pricing?: number;
  pricing_mode?: 'single' | 'tiered';
  audio_shoutout_enabled?: boolean;
  audio_shoutout_price?: number;
  admin_fee_percentage?: number;
  promo_video_url?: string;
  support_enabled?: boolean;
  payout_onboarding_completed?: boolean;
  allow_corporate_pricing?: boolean;
  corporate_pricing?: number;
  express_delivery_enabled?: boolean;
  express_delivery_price?: number;
  auto_apply_sponsors?: boolean;
  collabs_enabled?: boolean;
  selected_shoutout_types?: string[];
}

interface SocialAccount {
  id: string;
  platform: 'twitter' | 'facebook' | 'instagram' | 'tiktok' | 'linkedin' | 'youtube' | 'threads' | 'snapchat' | 'pinterest' | 'rumble';
  handle: string;
  follower_count?: number;
}

interface User {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
}

interface BioSettings {
  id?: string;
  talent_id: string;
  instagram_username?: string;
  one_liner?: string;
  theme: string;
  background_color: string;
  accent_color: string;
  font_family: string;
  show_shoutout_card: boolean;
  show_rumble_card: boolean;
  show_youtube_card?: boolean;
  show_podcast_card?: boolean;
  show_newsletter?: boolean;
  affiliate_section_title?: string;
  is_published: boolean;
  background_type: string;
  gradient_start: string;
  gradient_end: string;
  gradient_direction: string;
  button_style: string;
  button_color: string;
  text_color: string;
  card_style: string;
  card_opacity: number;
  profile_image_url?: string;
  display_name?: string;
  stream_card_order?: string[]; // Order of stream cards: ['rumble', 'youtube', 'podcast']
  analytics_start_at?: string; // When CTR tracking started
}

interface BioLink {
  id?: string;
  talent_id: string;
  link_type: 'basic' | 'grid' | 'newsletter' | 'sponsor' | 'video' | 'affiliate' | 'donate';
  title?: string;
  url?: string;
  icon_url?: string;
  image_url?: string;
  video_url?: string;
  company_name?: string;
  discount_amount?: string;
  discount_code?: string;
  grid_size?: 'small' | 'medium' | 'large';
  display_order: number;
  is_active: boolean;
  grid_columns?: number;
  background_image_url?: string;
  thumbnail_url?: string;
  subtitle?: string;
  button_text?: string;
  is_featured?: boolean;
  link_format?: 'thin' | 'tall' | 'square'; // Link display format
  click_count?: number;
  expires_at?: string | null;
}

interface NewsletterConfig {
  id?: string;
  talent_id: string;
  provider: string;
  api_key?: string | null;
  list_id?: string | null;
  webhook_url?: string | null;
  form_id?: string | null;
  is_active: boolean;
}

interface ServiceOffering {
  id?: string;
  talent_id: string;
  service_type: 'instagram_collab' | 'tiktok_collab' | 'youtube_collab' | 'sponsorship';
  pricing: number; // in cents
  title: string;
  description?: string;
  video_length_seconds: number;
  benefits: string[];
  platforms: string[]; // Which social platforms are included (instagram, tiktok, youtube, twitter, facebook)
  is_active: boolean;
  display_order: number;
  total_followers?: number;
  // Coupon and recurring payment fields
  coupon_code?: string | null;
  coupon_discount_amount?: number | null;
  coupon_discount_type?: 'percentage' | 'fixed';
  is_recurring?: boolean;
  recurring_interval?: 'weekly' | 'biweekly' | 'monthly' | 'quarterly' | 'yearly' | null;
  // Sponsorship contact email
  contact_email?: string | null;
}

interface BioEvent {
  id?: string;
  talent_id: string;
  title: string;
  description?: string;
  event_date?: string;
  event_end_date?: string;
  event_time?: string;
  location?: string;
  registration_url?: string;
  button_text: string;
  image_url?: string;
  source_type: 'manual' | 'ical' | 'rss';
  source_url?: string;
  is_active: boolean;
  display_order: number;
}

// Gradient presets
const GRADIENT_PRESETS = [
  { name: 'Midnight', start: '#0a0a0a', end: '#1a1a2e', direction: 'to-b' },
  { name: 'Ocean', start: '#0c4a6e', end: '#164e63', direction: 'to-br' },
  { name: 'Sunset', start: '#7c2d12', end: '#1c1917', direction: 'to-b' },
  { name: 'Forest', start: '#14532d', end: '#0a0a0a', direction: 'to-b' },
  { name: 'Purple Haze', start: '#581c87', end: '#0a0a0a', direction: 'to-b' },
  { name: 'Rose', start: '#881337', end: '#1c1917', direction: 'to-b' },
  { name: 'Crimson', start: '#991b1b', end: '#0a0a0a', direction: 'to-b' },
  { name: 'Royal Blue', start: '#1e3a8a', end: '#0a0a0a', direction: 'to-b' },
  { name: 'Slate', start: '#334155', end: '#0f172a', direction: 'to-b' },
  { name: 'Warm', start: '#78350f', end: '#1c1917', direction: 'to-br' },
];

const BUTTON_COLORS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'White', value: '#ffffff' },
];

const NEWSLETTER_PROVIDERS = [
  { id: 'mailchimp', name: 'Mailchimp', icon: '📧' },
  { id: 'getresponse', name: 'GetResponse', icon: '📬' },
  { id: 'flodesk', name: 'Flodesk', icon: '💐' },
  { id: 'emailoctopus', name: 'EmailOctopus', icon: '🐙' },
  { id: 'cleverreach', name: 'CleverReach', icon: '🎯' },
  { id: 'activecampaign', name: 'ActiveCampaign', icon: '⚡' },
  { id: 'zapier', name: 'Zapier Webhook', icon: '🔗' },
];

// Social platforms with their URL patterns and SVG icons
const SOCIAL_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', urlPattern: 'instagram.com', baseUrl: 'https://instagram.com/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
  { id: 'twitter', name: 'X (Twitter)', urlPattern: 'twitter.com|x.com', baseUrl: 'https://x.com/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
  { id: 'tiktok', name: 'TikTok', urlPattern: 'tiktok.com', baseUrl: 'https://tiktok.com/@',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg> },
  { id: 'youtube', name: 'YouTube', urlPattern: 'youtube.com|youtu.be', baseUrl: 'https://youtube.com/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
  { id: 'facebook', name: 'Facebook', urlPattern: 'facebook.com|fb.com', baseUrl: 'https://facebook.com/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
  { id: 'linkedin', name: 'LinkedIn', urlPattern: 'linkedin.com', baseUrl: 'https://linkedin.com/in/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
  { id: 'threads', name: 'Threads', urlPattern: 'threads.net', baseUrl: 'https://threads.net/@',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12.186 24h-.007c-3.581-.024-6.334-1.205-8.184-3.509C2.35 18.44 1.5 15.586 1.472 12.01v-.017c.03-3.579.879-6.43 2.525-8.482C5.845 1.205 8.6.024 12.18 0h.014c2.746.02 5.043.725 6.826 2.098 1.677 1.29 2.858 3.13 3.509 5.467l-2.04.569c-1.104-3.96-3.898-5.984-8.304-6.015-2.91.022-5.11.936-6.54 2.717C4.307 6.504 3.616 8.914 3.589 12c.027 3.086.718 5.496 2.057 7.164 1.43 1.783 3.631 2.698 6.54 2.717 2.623-.02 4.358-.631 5.8-2.045 1.647-1.613 1.618-3.593 1.09-4.798-.31-.71-.873-1.3-1.634-1.75-.192 1.352-.622 2.446-1.284 3.272-.886 1.102-2.14 1.704-3.73 1.79-1.202.065-2.361-.218-3.259-.801-1.063-.689-1.685-1.74-1.752-2.96-.065-1.182.408-2.256 1.33-3.022.88-.73 2.108-1.146 3.456-1.17 1.005-.018 1.96.14 2.836.469-.034-.773-.17-1.418-.413-1.911-.351-.714-.964-1.08-1.874-1.12-1.084.007-1.9.6-2.378 1.235l-1.7-1.154c.78-1.104 2.144-1.8 3.943-1.963l.136-.008c1.678-.067 3.056.36 4.1 1.27.962.838 1.574 2.086 1.82 3.713.266-.057.54-.103.82-.138l.468-.06c1.953-.19 3.936.376 5.14 1.556 1.522 1.49 1.973 3.746 1.239 6.2-.49 1.638-1.51 3.07-2.95 4.14-1.617 1.2-3.654 1.832-6.058 1.88h-.007zm-2.21-8.106c-.78.045-1.394.283-1.776.69-.345.368-.516.838-.483 1.325.033.515.266.96.657 1.254.453.34 1.09.5 1.798.454 1.053-.057 1.864-.508 2.412-1.342.396-.604.637-1.4.716-2.364-.93-.252-1.956-.345-3.324-.017z"/></svg> },
  { id: 'snapchat', name: 'Snapchat', urlPattern: 'snapchat.com', baseUrl: 'https://snapchat.com/add/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301.165-.088.344-.104.464-.104.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03c-.135 0-.313-.031-.538-.074-.36-.075-.765-.135-1.273-.135-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884-.314-.045-.629-.074-.928-.074-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42-.061-.192-.09-.389-.135-.567-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226-.031-.063-.052-.15-.055-.225-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809-.121-.029-.24-.074-.346-.119-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z"/></svg> },
  { id: 'pinterest', name: 'Pinterest', urlPattern: 'pinterest.com', baseUrl: 'https://pinterest.com/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z"/></svg> },
  { id: 'spotify', name: 'Spotify', urlPattern: 'spotify.com|open.spotify', baseUrl: 'https://open.spotify.com/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg> },
  { id: 'twitch', name: 'Twitch', urlPattern: 'twitch.tv', baseUrl: 'https://twitch.tv/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/></svg> },
  { id: 'discord', name: 'Discord', urlPattern: 'discord.gg|discord.com', baseUrl: 'https://discord.gg/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/></svg> },
  { id: 'rumble', name: 'Rumble', urlPattern: 'rumble.com', baseUrl: 'https://rumble.com/c/',
    icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6"><path d="M14.4528 13.5458c0.8064 -0.6542 0.9297 -1.8381 0.2756 -2.6445a1.8802 1.8802 0 0 0 -0.2756 -0.2756 21.2127 21.2127 0 0 0 -4.3121 -2.776c-1.066 -0.51 -2.256 0.2 -2.4261 1.414a23.5226 23.5226 0 0 0 -0.14 5.5021c0.116 1.23 1.292 1.964 2.372 1.492a19.6285 19.6285 0 0 0 4.5062 -2.704v-0.008zm6.9322 -5.4002c2.0335 2.228 2.0396 5.637 0.014 7.8723A26.1487 26.1487 0 0 1 8.2946 23.846c-2.6848 0.6713 -5.4168 -0.914 -6.1662 -3.5781 -1.524 -5.2002 -1.3 -11.0803 0.17 -16.3045 0.772 -2.744 3.3521 -4.4661 6.0102 -3.832 4.9242 1.174 9.5443 4.196 13.0764 8.0121v0.002z"/></svg> },
];

// URLs to skip during import (not actual links)
const SKIP_URL_PATTERNS = [
  '/privacy',
  '/terms',
  '/legal',
  '/cookie',
  '/about',
  '/contact',
  '/help',
  '/support',
  '/faq',
  'facebook.com/sharer',
  'twitter.com/intent',
  'twitter.com/share',
  'linkedin.com/sharing',
  'linkedin.com/shareArticle',
  'pinterest.com/pin/create',
  'reddit.com/submit',
  'tumblr.com/share',
  'telegram.me/share',
  't.me/share',
  'wa.me',
  'whatsapp.com/send',
  'line.me/R/share',
  'social-plugins.line.me',
  'story.kakao.com/share',
  'vk.com/share',
  'getpocket.com/save',
  'buffer.com/add',
  'mailto:',
  'tel:',
  'sms:',
];

// Helper to detect if URL is a social platform
const detectSocialPlatform = (url: string): string | null => {
  const lowerUrl = url.toLowerCase();
  for (const platform of SOCIAL_PLATFORMS) {
    const patterns = platform.urlPattern.split('|');
    if (patterns.some(p => lowerUrl.includes(p))) {
      return platform.id;
    }
  }
  return null;
};

// Helper to check if URL should be skipped
const shouldSkipUrl = (url: string): boolean => {
  const lowerUrl = url.toLowerCase();
  return SKIP_URL_PATTERNS.some(pattern => lowerUrl.includes(pattern));
};

const BioDashboard: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [, setUser] = useState<User | null>(null);
  const [talentProfile, setTalentProfile] = useState<TalentProfile | null>(null);
  const [bioSettings, setBioSettings] = useState<BioSettings | null>(null);
  const [links, setLinks] = useState<BioLink[]>([]);
  const [socialLinks, setSocialLinks] = useState<SocialAccount[]>([]);
  const [newsletterConfigs, setNewsletterConfigs] = useState<NewsletterConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLink, setEditingLink] = useState<BioLink | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSponsorsModal, setShowSponsorsModal] = useState(false);
  const [sponsorAdsEnabled, setSponsorAdsEnabled] = useState(false);
  const [hasActiveSponsors, setHasActiveSponsors] = useState(false);
  const [sponsorsChecked, setSponsorsChecked] = useState(false);
  const [sponsorBtnBounce, setSponsorBtnBounce] = useState(false);
  const [sponsorEarningsCents, setSponsorEarningsCents] = useState<number | null>(null);
  const [showNewsletterModal, setShowNewsletterModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showImportFansModal, setShowImportFansModal] = useState(false);
  const [showAddSocialModal, setShowAddSocialModal] = useState(false);
  const [showAddServiceModal, setShowAddServiceModal] = useState(false);
  const [serviceModalType, setServiceModalType] = useState<'instagram_collab' | 'sponsorship' | null>(null);
  const [showStreamChannelModal, setShowStreamChannelModal] = useState<'rumble' | 'youtube' | null>(null);
  const [showPodcastModal, setShowPodcastModal] = useState(false);
  const [serviceOfferings, setServiceOfferings] = useState<ServiceOffering[]>([]);
  const [editingService, setEditingService] = useState<ServiceOffering | null>(null);
  const [showAddEventModal, setShowAddEventModal] = useState(false);
  const [bioEvents, setBioEvents] = useState<BioEvent[]>([]);
  const [authError, setAuthError] = useState<string | null>(null);
  const [mainView, setMainView] = useState<'dashboard' | 'customize' | 'send_updates' | 'orders' | 'welcome' | 'partner' | 'course' | 'payouts' | 'network'>('dashboard');

  // Network tab only enabled for specific test accounts
  const NETWORK_ENABLED_USERNAMES = ['jonathanbodnar2', 'jt'];
  const networkEnabled = talentProfile ? NETWORK_ENABLED_USERNAMES.includes(talentProfile.username?.toLowerCase() || '') : false;
  const [showPayoutOnboarding, setShowPayoutOnboarding] = useState(false);
  const isDemoAccount = (() => { try { return JSON.parse(localStorage.getItem('creator_session') || '{}').email === 'applereview@shoutout.us'; } catch { return false; } })();
  const [fansSubTab, setFansSubTab] = useState<'update' | 'messages' | 'fans'>('messages');
  const [donationMessages, setDonationMessages] = useState<any[]>([]);
  const [donationMessagesLoading, setDonationMessagesLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [fansData, setFansData] = useState<any[]>([]);
  const [fansLoading, setFansLoading] = useState(false);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [emailButtonText, setEmailButtonText] = useState('');
  const [emailButtonUrl, setEmailButtonUrl] = useState('');
  const [emailImageUrl, setEmailImageUrl] = useState('');
  const [emailImageLinkUrl, setEmailImageLinkUrl] = useState(''); // URL the image links to when clicked
  const [emailScheduledDate, setEmailScheduledDate] = useState('');
  const [emailScheduledTime, setEmailScheduledTime] = useState('');
  const [emailDraftId, setEmailDraftId] = useState<string | null>(null);
  const [draftSaveStatus, setDraftSaveStatus] = useState<'saved' | 'saving' | 'idle'>('idle');
  const [randomReview, setRandomReview] = useState<{ rating: number; comment?: string; users?: { full_name: string } } | null>(null);
  const [showButtonFields, setShowButtonFields] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [uploadingEmailImage, setUploadingEmailImage] = useState(false);
  const [importingFans, setImportingFans] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeTab, setActiveTab] = useState<'links' | 'style' | 'shoutouts'>('links');

  // Partner program state
  const [partnerData, setPartnerData] = useState<{ id: string; name: string; email: string; referral_token: string; is_active: boolean; registration_source?: string | null } | null>(null);
  const [scriptCopied, setScriptCopied] = useState(false);
  const [partnerLinkCopied, setPartnerLinkCopied] = useState(false);
  const [partnerProgramEnabled, setPartnerProgramEnabled] = useState(false);
  const [partnerReferrals, setPartnerReferrals] = useState<any[]>([]);
  const [partnerReferralsLoading, setPartnerReferralsLoading] = useState(false);
  const [partnerCopied, setPartnerCopied] = useState(false);
  const [followerModalTalent, setFollowerModalTalent] = useState<any | null>(null);
  const [followerInputs, setFollowerInputs] = useState<Record<string, number>>({});
  const [followerSaving, setFollowerSaving] = useState(false);

  // Design request modal state
  const [showDesignModal, setShowDesignModal] = useState(false);
  const [designLinktree, setDesignLinktree] = useState('');
  const [designOtherLinks, setDesignOtherLinks] = useState('');
  const [designSponsorCards, setDesignSponsorCards] = useState(false);
  const [designSubmitting, setDesignSubmitting] = useState(false);
  const [designAlreadyRequested, setDesignAlreadyRequested] = useState(false);
  const designCarouselRef = useRef<HTMLDivElement>(null);

  // Instagram disclaimer modal state
  const [showIgDisclaimer, setShowIgDisclaimer] = useState(false);
  const [igDisclaimerChecked, setIgDisclaimerChecked] = useState(false);

  // Push notification deep link state
  const [pushDeepLinkChannelId, setPushDeepLinkChannelId] = useState<string | null>(null);
  const [pushOpenChat, setPushOpenChat] = useState(false);

  // Settings modal state
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Admin detection for chat
  const [chatIsAdmin, setChatIsAdmin] = useState(false);
  const [chatAdminUserId, setChatAdminUserId] = useState<string | undefined>(undefined);

  // Profile edit modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileEditName, setProfileEditName] = useState('');
  const [profileEditAvatar, setProfileEditAvatar] = useState('');
  const [profileEditUsername, setProfileEditUsername] = useState('');
  const [profileUsernameAvailable, setProfileUsernameAvailable] = useState<boolean | null>(null);
  const [profileCheckingUsername, setProfileCheckingUsername] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);

  // Admin suggestions state
  const [adminSuggestions, setAdminSuggestions] = useState<{ id: string; title: string; body: string }[]>([]);

  // Talent login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPhone, setLoginPhone] = useState('');
  const [loginOtp, setLoginOtp] = useState('');
  const [loginStep, setLoginStep] = useState<'email' | 'phone' | 'otp' | 'password'>('email');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');

  // Orders state for talent
  const [talentOrders, setTalentOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [uploadingOrderId, setUploadingOrderId] = useState<string | null>(null);

  // Welcome carousel
  const welcomeCarouselRef = useRef<HTMLDivElement>(null);
  const bioCreatorBios = [
    { name: 'Chris Ripa', handle: 'chrisripa' },
    { name: 'Greg On Fire', handle: 'gregonfire' },
    { name: 'Shawn Farash', handle: 'shawnfarash' },
    { name: 'Melonie Mac', handle: 'meloniemac' },
    { name: 'Nick Di Paolo', handle: 'nickdipaolo' },
    { name: 'Hayley Caronia', handle: 'hayleycaronia' },
    { name: 'Lydia Shaffer', handle: 'lydiashaffer' },
    { name: 'Kristin Sokoloff', handle: 'kristinsokoloff' },
  ];

  // Payouts state
  const [payoutsData, setPayoutsData] = useState<{ payouts: any[]; batches: any[]; totalEarnings: number; adRevenueBatches: any[]; accruingAdRevenueCents: number } | null>(null);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState<string | null>(null);

  // Social management modal
  const [showSocialManageModal, setShowSocialManageModal] = useState(false);
  const [showTalentMenu, setShowTalentMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showDonateModal, setShowDonateModal] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [viewStats, setViewStats] = useState<{ total_views: number; unique_views: number; views_last_24h: number } | null>(null);
  const [mobileView, setMobileView] = useState<'manage' | 'preview'>('manage');
  
  // Analytics state for CTR tracking
  const [analyticsStartAt, setAnalyticsStartAt] = useState<string | null>(null);
  const [totalViews, setTotalViews] = useState(0);
  const [linkClicks, setLinkClicks] = useState<Record<string, number>>({});
  const [cardClicks, setCardClicks] = useState<Record<string, number>>({}); // rumble, youtube, podcast, shoutout, collab, sponsorship

  // Rolling 7-day stats for header/floating display
  const [rolling7dViews, setRolling7dViews] = useState(0);
  const [rolling7dClicks, setRolling7dClicks] = useState(0);
  const rolling7dCtr = rolling7dViews > 0 ? ((rolling7dClicks / rolling7dViews) * 100).toFixed(1) : '0.0';

  // Welcome page logic:
  // Enable sponsor ads button when talent has auto_apply_sponsors set
  useEffect(() => {
    if (talentProfile?.auto_apply_sponsors) {
      setSponsorAdsEnabled(true);
    }
  }, [talentProfile?.auto_apply_sponsors]);

  // Refresh partner data when the user self-enrolls from the Creator Hub card,
  // so the Partner tab appears immediately without a page reload.
  useEffect(() => {
    if (!showTalentMenu) return;
    const close = () => setShowTalentMenu(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [showTalentMenu]);

  useEffect(() => {
    if (!talentProfile?.id) return;
    const handler = () => {
      supabase.rpc('get_partner_for_talent', { p_talent_id: talentProfile.id })
        .then(({ data: pData }) => { if (pData) setPartnerData(pData as any); });
    };
    window.addEventListener('partner-enrolled', handler);
    return () => window.removeEventListener('partner-enrolled', handler);
  }, [talentProfile?.id]);

  // 1. First-ever login → always show welcome once, then mark as seen
  // 2. Subsequent visits → only show if creator has none of: bio links, sponsor ads, rumble/youtube/podcast cards
  // Social links don't count (every creator has them automatically)
  // 3. If ?tab=customize is in URL (e.g. from /start onboarding), skip welcome entirely
  useEffect(() => {
    if (!loading && sponsorsChecked && talentProfile?.id) {
      const tabParam = searchParams.get('tab');
      const welcomeKey = `dashboard_welcome_seen_${talentProfile.id}`;
      const hasSeenWelcome = localStorage.getItem(welcomeKey) === 'true';
      const hasStreamCards = bioSettings?.show_rumble_card || bioSettings?.show_youtube_card || bioSettings?.show_podcast_card;
      const hasRealContent = links.length > 0 || hasStreamCards || hasActiveSponsors;

      if (isDemoAccount) {
        localStorage.setItem(welcomeKey, 'true');
        setMainView('customize');
      } else if (tabParam === 'customize') {
        localStorage.setItem(welcomeKey, 'true');
        setMainView('customize');
      } else if (tabParam === 'dashboard') {
        setMainView('dashboard');
      } else if (!hasSeenWelcome) {
        setMainView('welcome');
        localStorage.setItem(welcomeKey, 'true');
      } else if (!hasRealContent) {
        setMainView('welcome');
      } else if (mainView === 'welcome') {
        setMainView('dashboard');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sponsorsChecked, talentProfile?.id, links.length, hasActiveSponsors, bioSettings?.show_rumble_card, bioSettings?.show_youtube_card, bioSettings?.show_podcast_card]);

  // Welcome carousel animation (only runs when welcome page is visible)
  useEffect(() => {
    if (mainView !== 'welcome') return;
    let animationId: number;
    let position = 0;
    const speed = 0.4;
    const animate = () => {
      const carousel = welcomeCarouselRef.current;
      if (!carousel) { animationId = requestAnimationFrame(animate); return; }
      position -= speed;
      const cardWidth = 120 + 12;
      if (Math.abs(position) >= cardWidth * bioCreatorBios.length) position = 0;
      carousel.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainView]);

  // Design modal carousel auto-scroll
  useEffect(() => {
    if (!showDesignModal) return;
    let animationId: number;
    let position = 0;
    const speed = 0.4;
    const cardWidth = 140 + 16; // width + gap
    const animate = () => {
      const el = designCarouselRef.current;
      if (!el) { animationId = requestAnimationFrame(animate); return; }
      position -= speed;
      if (Math.abs(position) >= cardWidth * bioCreatorBios.length) position = 0;
      el.style.transform = `translateX(${position}px)`;
      animationId = requestAnimationFrame(animate);
    };
    animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDesignModal]);

  // Auto-refresh preview
  const refreshPreview = useCallback(() => {
    setPreviewKey(prev => prev + 1);
  }, []);

  // Fetch CTR analytics data
  const fetchAnalytics = useCallback(async () => {
    if (!talentProfile?.id || !analyticsStartAt) return;
    
    try {
      // Get total views since analytics_start_at
      const { count: viewCount } = await supabase
        .from('bio_page_views')
        .select('*', { count: 'exact', head: true })
        .eq('talent_id', talentProfile.id)
        .gte('viewed_at', analyticsStartAt);
      
      setTotalViews(viewCount || 0);
      
      // Get click counts per link since analytics_start_at
      const { data: linkClickData } = await supabase
        .from('bio_link_clicks')
        .select('bio_link_id')
        .eq('talent_id', talentProfile.id)
        .not('bio_link_id', 'is', null)
        .gte('clicked_at', analyticsStartAt);
      
      // Count clicks per link
      const clickCounts: Record<string, number> = {};
      (linkClickData || []).forEach((click: { bio_link_id: string }) => {
        if (click.bio_link_id) {
          clickCounts[click.bio_link_id] = (clickCounts[click.bio_link_id] || 0) + 1;
        }
      });
      setLinkClicks(clickCounts);
      
      // Get click counts per card type since analytics_start_at
      const { data: cardClickData } = await supabase
        .from('bio_link_clicks')
        .select('card_type')
        .eq('talent_id', talentProfile.id)
        .not('card_type', 'is', null)
        .gte('clicked_at', analyticsStartAt);
      
      // Count clicks per card type, merging legacy keys into canonical ones
      const cardCounts: Record<string, number> = {};
      const mergeMap: Record<string, string> = { 'meghan-twc': 'sponsor_ad' };
      (cardClickData || []).forEach((click: { card_type: string }) => {
        if (click.card_type) {
          const key = mergeMap[click.card_type] || click.card_type;
          cardCounts[key] = (cardCounts[key] || 0) + 1;
        }
      });
      setCardClicks(cardCounts);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [talentProfile?.id, analyticsStartAt]);

  // Fetch rolling 7-day stats for header/floating display
  const fetchRolling7dStats = useCallback(async () => {
    if (!talentProfile?.id || !talentProfile?.username) return;
    try {
      const { data } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-ga4-talent-ctr', slug: talentProfile.username },
      });
      if (data?.source === 'ga4') {
        setRolling7dViews(data.views || 0);
        setRolling7dClicks(data.clicks || 0);
      } else {
        const cutoff = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
        const [{ count: views }, { count: clicks }] = await Promise.all([
          supabase.from('bio_page_views').select('*', { count: 'exact', head: true }).eq('talent_id', talentProfile.id).gte('viewed_at', cutoff),
          supabase.from('bio_link_clicks').select('*', { count: 'exact', head: true }).eq('talent_id', talentProfile.id).gte('clicked_at', cutoff),
        ]);
        setRolling7dViews(views || 0);
        setRolling7dClicks(clicks || 0);
      }
    } catch (error) {
      console.error('Error fetching 7-day rolling stats:', error);
    }
  }, [talentProfile?.id, talentProfile?.username]);

  useEffect(() => {
    if (talentProfile?.id) fetchRolling7dStats();
  }, [talentProfile?.id, fetchRolling7dStats]);

  // Set analytics start date when bioSettings loads
  useEffect(() => {
    if (bioSettings?.analytics_start_at) {
      setAnalyticsStartAt(bioSettings.analytics_start_at);
    } else if (bioSettings?.id && talentProfile?.id) {
      // Initialize analytics_start_at if not set
      const now = new Date().toISOString();
      supabase.rpc('update_bio_settings', {
        p_id: bioSettings.id,
        p_updates: { analytics_start_at: now },
      }).then(() => {
          setAnalyticsStartAt(now);
        });
    }
  }, [bioSettings?.analytics_start_at, bioSettings?.id, talentProfile?.id]);

  // Fetch analytics when analyticsStartAt is available
  useEffect(() => {
    if (analyticsStartAt && talentProfile?.id) {
      fetchAnalytics();
    }
  }, [analyticsStartAt, talentProfile?.id, fetchAnalytics]);

  // Auto-save email draft to database
  const saveDraftToDb = useCallback(async () => {
    if (!talentProfile?.id) return;
    
    // Don't save if there's nothing to save
    if (!emailSubject && !emailContent && !emailButtonText && !emailButtonUrl && !emailImageUrl) {
      return;
    }

    setDraftSaveStatus('saving');
    
    try {
      const draftData = {
        talent_id: talentProfile.id,
        subject: emailSubject || null,
        content: emailContent || null,
        button_text: emailButtonText || null,
        button_url: emailButtonUrl || null,
        image_url: emailImageUrl || null,
        image_link_url: emailImageLinkUrl || null,
        scheduled_date: emailScheduledDate || null,
        scheduled_time: emailScheduledTime || null,
        status: 'draft',
        updated_at: new Date().toISOString()
      };

      if (emailDraftId) {
        // Update existing draft
        await supabase
          .from('email_drafts')
          .update(draftData)
          .eq('id', emailDraftId);
      } else {
        // Create new draft
        const { data } = await supabase
          .from('email_drafts')
          .insert([draftData])
          .select()
          .single();
        
        if (data) {
          setEmailDraftId(data.id);
        }
      }
      
      setDraftSaveStatus('saved');
    } catch (error) {
      console.error('Error saving draft:', error);
      setDraftSaveStatus('idle');
    }
  }, [talentProfile?.id, emailDraftId, emailSubject, emailContent, emailButtonText, emailButtonUrl, emailImageUrl, emailImageLinkUrl, emailScheduledDate, emailScheduledTime]);

  // Debounced auto-save effect
  useEffect(() => {
    if (!talentProfile?.id) return;
    
    const timeoutId = setTimeout(() => {
      saveDraftToDb();
    }, 1500); // Save 1.5 seconds after last change

    return () => clearTimeout(timeoutId);
  }, [emailSubject, emailContent, emailButtonText, emailButtonUrl, emailImageUrl, emailImageLinkUrl, emailScheduledDate, emailScheduledTime, saveDraftToDb, talentProfile?.id]);

  // Authenticate user from token
  useEffect(() => {
    const authenticateUser = async () => {
      const token = searchParams.get('token');
      
      // Auto-save creator session from URL params (forwarded from shoutout.us login)
      const urlEmail = searchParams.get('email');
      const urlUserId = searchParams.get('userId');
      if (token && urlEmail && urlUserId) {
        localStorage.setItem('creator_session', JSON.stringify({ userId: urlUserId, email: urlEmail, talentId: token }));
      }
      
      if (!token) {
        setAuthError('No authentication token provided. Please access this page from your ShoutOut dashboard.');
        setLoading(false);
        return;
      }

      try {
        console.log('Bio Dashboard: Authenticating with token:', token);
        
        // First, try to find talent profile directly by ID (for admin access)
        let profile: TalentProfile | null = null;
        let userData: User | null = null;
        
        const { data: directProfile, error: directError } = await supabase
          .from('talent_profiles')
          .select('*')
          .eq('id', token)
          .single();
        
        if (!directError && directProfile) {
          // Token is a talent_profile ID (admin access)
          console.log('Bio Dashboard: Found talent profile directly by ID');
          console.log('Profile data - rumble_handle:', directProfile.rumble_handle, 'rumble_type:', directProfile.rumble_type);
          profile = directProfile;
          
          // Get user data if available
          if (directProfile.user_id) {
            const { data: userFromProfile } = await supabase
              .from('users')
              .select('*')
              .eq('id', directProfile.user_id)
              .single();
            userData = userFromProfile;
          }
        } else {
          // Try token as user ID (normal talent access)
          const { data: userById, error: userError } = await supabase
            .from('users')
            .select('*')
            .eq('id', token)
            .single();

          if (userError || !userById) {
            console.error('Bio Dashboard: User lookup failed:', userError);
            setAuthError(`Invalid authentication token. Please try again from your ShoutOut dashboard.`);
            setLoading(false);
            return;
          }

          userData = userById as User;

          const { data: profileByUser, error: profileError } = await supabase
            .from('talent_profiles')
            .select('*')
            .eq('user_id', userById.id)
            .single();

          if (profileError || !profileByUser) {
            console.error('Bio Dashboard: Talent profile lookup failed:', profileError);
            setAuthError(`No talent profile found. You must be a talent to use ShoutOut Bio.`);
            setLoading(false);
            return;
          }
          
          profile = profileByUser;
          console.log('Profile data - rumble_handle:', profileByUser.rumble_handle, 'rumble_type:', profileByUser.rumble_type);
        }

        if (userData) {
          setUser(userData);
          if ((userData as any).user_type === 'admin') {
            setChatIsAdmin(true);
            setChatAdminUserId(userData.id);
          }
        }

        // Also check creator_session for admin status (when accessing via URL params)
        if (!userData) {
          const sess = localStorage.getItem('creator_session');
          if (sess) {
            try {
              const { userId } = JSON.parse(sess);
              if (userId) {
                const { data: sessUser } = await supabase.from('users').select('id, user_type').eq('id', userId).single();
                if (sessUser?.user_type === 'admin') {
                  setChatIsAdmin(true);
                  setChatAdminUserId(sessUser.id);
                }
              }
            } catch {}
          }
        }
        
        if (!profile) {
          setAuthError(`No talent profile found. You must be a talent to use ShoutOut Bio.`);
          setLoading(false);
          return;
        }

        // Establish a real Supabase auth session so RLS write policies work.
        //
        // IMPORTANT: never sign out the existing session unless we're
        // confident we can replace it. A stale/mismatched session still
        // satisfies `auth.role() = 'authenticated'` (which is what the bulk
        // of creator-write RLS policies check, e.g. bio_links_insert,
        // bio_links_update). An absent session fails EVERY write.
        // Previously we signed out first and then attempted re-auth — if
        // re-auth didn't have an email or the edge function threw, the
        // creator was left session-less and all writes (add link, change
        // style, etc.) silently 403'd.
        const { data: existingSession } = await supabase.auth.getSession();
        const sessionUserId = existingSession?.session?.user?.id;
        const targetUserId = profile.user_id;
        const hasMatchingSession = sessionUserId && targetUserId && sessionUserId === targetUserId;

        if (hasMatchingSession) {
          console.log('✅ Auth session already matches this talent — skipping re-auth');
        } else {
          const sessionEmail = userData?.email || urlEmail || (() => {
            try { return JSON.parse(localStorage.getItem('creator_session') || '{}').email; } catch { return null; }
          })();

          if (sessionEmail && profile.id) {
            let newSessionEstablished = false;
            try {
              const { data: csData } = await supabase.functions.invoke('creator-session', {
                body: { talent_id: profile.id, email: sessionEmail },
              });
              if (csData?.hashed_token) {
                // Sign out the stale session ONLY now that we have a fresh
                // token in hand. verifyOtp itself replaces the session so
                // the explicit signOut is belt-and-suspenders.
                if (sessionUserId) {
                  console.warn('🔄 Auth session mismatch — replacing stale session for', sessionUserId, 'target is', targetUserId);
                  await supabase.auth.signOut();
                }
                const { error: otpErr } = await supabase.auth.verifyOtp({ token_hash: csData.hashed_token, type: 'magiclink' });
                if (otpErr) {
                  console.warn('verifyOtp failed:', otpErr);
                } else {
                  newSessionEstablished = true;
                  console.log('🔑 Supabase auth session established for creator');
                }
              }
            } catch (e) {
              console.warn('Could not establish auth session:', e);
            }

            if (!newSessionEstablished && sessionUserId) {
              // Re-auth failed. Keep the stale session — it's worse than
              // nothing for RLS policies that match `auth.uid() = user_id`,
              // but better than nothing for policies that only check
              // `auth.role() = 'authenticated'`. Writes to bio_links (insert
              // / update) succeed under the latter.
              console.warn('⚠️ Re-auth failed — keeping existing session for authenticated-role RLS');
            }
          } else if (sessionUserId) {
            // No email available to re-auth with. Keep the existing session
            // rather than signing out into a no-auth state.
            console.warn('⚠️ No email to re-auth with — keeping existing session', sessionUserId);
          }
        }

        // Merge user data into profile for fallback name
        // Priority: full_name > temp_full_name (pre-onboarding) > users.full_name > empty
        const profileWithUser = {
          ...profile,
          full_name: profile.full_name || profile.temp_full_name || userData?.full_name || '',
        };

        setTalentProfile(profileWithUser);

        // Push notification registration (native + web)
        const pushLog = (step: string, detail?: string) =>
          supabase.from('push_debug_log').insert({ talent_id: profile!.id, step, detail }).then(() => {});
        const isNative = Capacitor.isNativePlatform();
        const platform = Capacitor.getPlatform();
        pushLog('init', `native=${isNative} platform=${platform}`);
        if (isNative) {
          PushNotifications.requestPermissions().then(perm => {
            pushLog('permission', perm.receive);
            if (perm.receive === 'granted') {
              PushNotifications.register();
              PushNotifications.addListener('registration', (token) => {
                pushLog('token_received', token.value?.slice(0, 40));
                supabase.from('device_tokens').upsert({
                  talent_id: profile!.id,
                  token: token.value,
                  platform,
                  updated_at: new Date().toISOString(),
                }, { onConflict: 'token' }).then(({ error }) => {
                  pushLog(error ? 'upsert_error' : 'upsert_ok', error?.message || 'saved');
                });
              });
              PushNotifications.addListener('registrationError', (err) => {
                pushLog('registration_error', JSON.stringify(err));
              });
              PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
                const data = notification.notification.data;
                if (data?.channel_id) {
                  setPushDeepLinkChannelId(data.channel_id);
                  setPushOpenChat(true);
                }
              });
            }
          }).catch((e) => pushLog('permission_error', String(e)));
        } else {
          import('../pwa').then(({ subscribeToPush }) => subscribeToPush(profile!.id)).catch(() => {});
        }
        supabase.rpc('get_partner_for_talent', { p_talent_id: profile.id })
          .then(({ data: pData }) => { if (pData) setPartnerData(pData as any); });
        supabase.from('design_requests').select('id').eq('talent_id', profile.id).not('status', 'eq', 'completed').maybeSingle()
          .then(({ data: dr }) => { if (dr) setDesignAlreadyRequested(true); });
        supabase.from('admin_suggestions').select('id, title, body').eq('talent_id', profile.id).is('dismissed_at', null).order('created_at', { ascending: false })
          .then(({ data: sug }) => { if (sug) setAdminSuggestions(sug); });

        // Parallel fetch all dashboard data at once
        const pid = profile.id;
        const [
          statsRes, socialRes, servicesRes, eventsRes, subsRes, reviewsRes,
          draftRes, settingsRes, linksRes, newsletterRes,
        ] = await Promise.all([
          supabase.from('bio_page_view_stats').select('*').eq('talent_id', pid).maybeSingle(),
          supabase.from('social_accounts').select('id, platform, handle, follower_count').eq('talent_id', pid),
          supabase.from('service_offerings').select('*').eq('talent_id', pid).order('display_order', { ascending: true }),
          supabase.from('bio_events').select('*').eq('talent_id', pid).order('display_order', { ascending: true }),
          supabase.from('talent_followers').select('*', { count: 'exact', head: true }).eq('talent_id', pid),
          supabase.from('reviews').select('*, users:user_id(full_name)').eq('talent_id', pid).gte('rating', 4).limit(10),
          supabase.from('email_drafts').select('*').eq('talent_id', pid).eq('status', 'draft').maybeSingle(),
          supabase.from('bio_settings').select('*').eq('talent_id', pid).single(),
          supabase.from('bio_links').select('*').eq('talent_id', pid).order('display_order'),
          supabase.from('bio_newsletter_configs').select('*').eq('talent_id', pid),
        ]);

        // Stats
        if (statsRes.data) setViewStats(statsRes.data);

        // Social accounts + handle merge
        const socialData = socialRes.data;
        const handleMappings = [
          { platform: 'twitter', field: 'twitter_handle' },
          { platform: 'instagram', field: 'instagram_handle' },
          { platform: 'facebook', field: 'facebook_handle' },
          { platform: 'tiktok', field: 'tiktok_handle' },
          { platform: 'youtube', field: 'youtube_handle' },
          { platform: 'rumble', field: 'rumble_handle' },
          { platform: 'snapchat', field: 'snapchat_handle' },
        ];
        const combinedSocials: SocialAccount[] = [];
        if (socialData && socialData.length > 0) {
          for (const s of socialData) {
            combinedSocials.push({
              id: s.id,
              platform: s.platform as SocialAccount['platform'],
              handle: s.handle.replace(/^@/, ''),
              follower_count: s.follower_count,
            });
          }
        }
        const profileHandles: { platform: string; handle: string }[] = [];
        for (const mapping of handleMappings) {
          const handle = (profile as any)[mapping.field];
          if (handle) {
            const existsAlready = combinedSocials.some(s => s.platform === mapping.platform);
            if (!existsAlready) {
              combinedSocials.push({
                id: `profile-${mapping.platform}`,
                platform: mapping.platform as SocialAccount['platform'],
                handle: handle.replace(/^@/, ''),
                follower_count: undefined,
              });
              profileHandles.push({ platform: mapping.platform, handle: handle.replace(/^@/, '') });
            }
          }
        }
        setSocialLinks(combinedSocials);

        // Fire-and-forget: sync handles to social_accounts table
        if (profileHandles.length > 0) {
          const validPlatforms = ['twitter','facebook','instagram','tiktok','linkedin','youtube','rumble','snapchat','threads','telegram','truth_social','gettr','substack','discord','twitch','pinterest'];
          const insertData = profileHandles.filter(h => validPlatforms.includes(h.platform)).map(h => ({ talent_id: pid, platform: h.platform, handle: h.handle }));
          if (insertData.length > 0) supabase.from('social_accounts').upsert(insertData, { onConflict: 'talent_id,platform' }).then(() => {});
        }
        // Fire-and-forget: sync youtube/rumble handles back to talent_profiles
        if (socialData && socialData.length > 0) {
          const needsSync: Record<string, string> = {};
          const yt = socialData.find(s => s.platform === 'youtube');
          const rm = socialData.find(s => s.platform === 'rumble');
          if (yt && !profile.youtube_handle) { needsSync.youtube_handle = yt.handle.replace(/^@/, ''); profile.youtube_handle = needsSync.youtube_handle; }
          if (rm && !profile.rumble_handle) { needsSync.rumble_handle = rm.handle.replace(/^@/, ''); profile.rumble_handle = needsSync.rumble_handle; }
          if (Object.keys(needsSync).length > 0) supabase.rpc('update_talent_profile', { p_id: pid, p_updates: needsSync }).then(() => {});
        }

        // Service offerings
        if (servicesRes.data) {
          setServiceOfferings(servicesRes.data.map(s => ({
            ...s,
            benefits: Array.isArray(s.benefits) ? s.benefits : JSON.parse(s.benefits || '[]'),
          })));
        }

        // Events
        if (eventsRes.data) setBioEvents(eventsRes.data);

        // Subscriber count
        setSubscriberCount(subsRes.count || 0);

        // Reviews
        if (reviewsRes.data && reviewsRes.data.length > 0) {
          setRandomReview(reviewsRes.data[Math.floor(Math.random() * reviewsRes.data.length)]);
        }

        // Email draft
        const existingDraft = draftRes.data;
        if (existingDraft) {
          setEmailDraftId(existingDraft.id);
          setEmailSubject(existingDraft.subject || '');
          setEmailContent(existingDraft.content || '');
          setEmailButtonText(existingDraft.button_text || '');
          setEmailButtonUrl(existingDraft.button_url || '');
          setEmailImageUrl(existingDraft.image_url || '');
          setEmailImageLinkUrl(existingDraft.image_link_url || '');
          setEmailScheduledDate(existingDraft.scheduled_date || '');
          setEmailScheduledTime(existingDraft.scheduled_time || '');
          if (existingDraft.button_text || existingDraft.button_url) setShowButtonFields(true);
          if (existingDraft.image_url) setShowImageUpload(true);
        }

        // Bio settings (create defaults if missing)
        let settings = settingsRes.data;
        if (settingsRes.error && settingsRes.error.code === 'PGRST116') {
          const defaultSettings: Partial<BioSettings> = {
            talent_id: pid, theme: 'glass', background_color: '#0a0a0a', accent_color: '#3b82f6',
            font_family: 'Inter', show_shoutout_card: true, is_published: true,
            background_type: 'gradient', gradient_start: '#0a0a0a', gradient_end: '#1a1a2e',
            gradient_direction: 'to-b', button_style: 'rounded', button_color: '#3b82f6',
            text_color: '#ffffff', card_style: 'glass', card_opacity: 0.10,
            display_name: profile.full_name, profile_image_url: profile.temp_avatar_url,
          };
          const { data: newId, error: createError } = await supabase.rpc('create_bio_settings', { p_settings: defaultSettings });
          if (!createError && newId) {
            const { data: created } = await supabase.from('bio_settings').select('*').eq('id', newId).single();
            settings = created;
          }
        }
        setBioSettings(settings);

        // Links & newsletters
        setLinks(linksRes.data || []);
        setNewsletterConfigs(newsletterRes.data || []);

        // Auto-navigate to a view if specified in URL
        const viewParam = searchParams.get('view');
        if (viewParam === 'payouts') {
          setMainView('payouts');
          loadTalentPayouts();
        }

      } catch (error) {
        console.error('Authentication error:', error);
        setAuthError('An error occurred during authentication.');
      } finally {
        setLoading(false);
      }
    };

    authenticateUser();

    supabase
      .from('platform_settings')
      .select('setting_value')
      .eq('setting_key', 'sponsor_ads_enabled')
      .single()
      .then(({ data }) => {
        const globalEnabled = data?.setting_value === 'true';
        // Only show sponsor button if globally enabled AND not a demo account
        const sess = localStorage.getItem('creator_session');
        const isDemoAccount = sess && JSON.parse(sess).email === 'applereview@shoutout.us';
        setSponsorAdsEnabled(globalEnabled && !isDemoAccount);
      })

    // Check partner program setting
    supabase
      .from('platform_settings')
      .select('setting_value')
      .eq('setting_key', 'partner_program_enabled')
      .single()
      .then(({ data }) => {
        setPartnerProgramEnabled(data?.setting_value === 'true');
      })

    const token = searchParams.get('token');
    if (token) {
      supabase
        .from('sponsor_creator_assignments')
        .select('id')
        .eq('talent_id', token)
        .eq('is_active', true)
        .then(({ data }) => {
          const hasSponsors = (data?.length ?? 0) > 0;
          setHasActiveSponsors(hasSponsors);
          setSponsorsChecked(true);
          if (!hasSponsors) setSponsorBtnBounce(true);
        });

      supabase
        .from('sponsor_clicks')
        .select('creator_share_cents')
        .eq('talent_id', token)
        .then(({ data }) => {
          const total = (data ?? []).reduce((sum, row) => sum + (row.creator_share_cents ?? 0), 0);
          setSponsorEarningsCents(total);
        });
    } else {
      setSponsorsChecked(true);
    }

    // Set up auto-refresh for view stats (every 30 seconds)
    const statsInterval = setInterval(async () => {
      if (!talentProfile?.id) return;
      
      const { data: freshStats } = await supabase
        .from('bio_page_view_stats')
        .select('*')
        .eq('talent_id', talentProfile.id)
        .maybeSingle();
      
      if (freshStats) {
        setViewStats(freshStats);
      }
      fetchRolling7dStats();
    }, 30000); // 30 seconds
    
    // Cleanup interval on unmount
    return () => clearInterval(statsInterval);
  }, [searchParams, talentProfile?.id]);

  // Save settings
  const saveSettings = useCallback(async (updates: Partial<BioSettings>) => {
    if (!bioSettings?.id) {
      console.error('No bioSettings.id to save to');
      return;
    }

    console.log('Saving settings:', updates, 'to bioSettings.id:', bioSettings.id);
    setSaving(true);
    try {
      const { error } = await supabase.rpc('update_bio_settings', {
        p_id: bioSettings.id,
        p_updates: updates,
      });

      if (error) {
        console.error('Supabase error saving settings:', error);
        throw error;
      }
      
      console.log('Settings saved successfully');
      setBioSettings({ ...bioSettings, ...updates });
      toast.success('Settings saved!');
      // Auto-refresh preview after saving
      setTimeout(refreshPreview, 500);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [bioSettings, refreshPreview]);

  // Add new link
  const addLink = async (link: Omit<BioLink, 'id' | 'display_order'>) => {
    if (!talentProfile?.id) return;

    try {
      const newLink = {
        ...link,
        talent_id: talentProfile.id,
        display_order: links.length,
      };

      const { data, error } = await supabase
        .from('bio_links')
        .insert([newLink])
        .select()
        .single();

      if (error) throw error;
      setLinks([...links, data]);
      setShowAddModal(false);
      toast.success('Link added!');
      // Auto-refresh preview
      setTimeout(refreshPreview, 500);
    } catch (error) {
      console.error('Error adding link:', error);
      toast.error('Failed to add link');
    }
  };

  // Add multiple links (for grid cards)
  const addMultipleLinks = async (newLinks: Omit<BioLink, 'id' | 'display_order'>[]) => {
    if (!talentProfile?.id) return;

    try {
      const linksToInsert = newLinks.map((link, index) => ({
        ...link,
        talent_id: talentProfile.id,
        display_order: links.length + index,
      }));

      const { data, error } = await supabase
        .from('bio_links')
        .insert(linksToInsert)
        .select();

      if (error) throw error;
      setLinks([...links, ...(data || [])]);
      setShowAddModal(false);
      toast.success(`${data?.length || 0} grid cards added!`);
      // Auto-refresh preview
      setTimeout(refreshPreview, 500);
    } catch (error) {
      console.error('Error adding links:', error);
      toast.error('Failed to add grid cards');
    }
  };

  // Update link
  const updateLink = async (link: BioLink) => {
    if (!link.id) return;

    console.log('updateLink called with:', { id: link.id, link_format: link.link_format, title: link.title });

    try {
      const updateData = {
        title: link.title,
        url: link.url,
        icon_url: link.icon_url,
        image_url: link.image_url,
        grid_size: link.grid_size,
        is_active: link.is_active,
        grid_columns: link.grid_columns,
        background_image_url: link.background_image_url,
        thumbnail_url: link.thumbnail_url,
        subtitle: link.subtitle,
        button_text: link.button_text,
        is_featured: link.is_featured,
        link_format: link.link_format,
        expires_at: link.expires_at ?? null,
        updated_at: new Date().toISOString(),
      };
      console.log('Updating bio_links with:', updateData);
      
      const { error } = await supabase
        .from('bio_links')
        .update(updateData)
        .eq('id', link.id);

      if (error) throw error;
      setLinks(links.map(l => l.id === link.id ? link : l));
      setEditingLink(null);
      toast.success('Link updated!');
      // Auto-refresh preview
      setTimeout(refreshPreview, 500);
    } catch (error) {
      console.error('Error updating link:', error);
      toast.error('Failed to update link');
    }
  };

  // Delete link
  const deleteLink = async (linkId: string) => {
    try {
      const { error } = await supabase
        .from('bio_links')
        .delete()
        .eq('id', linkId);

      if (error) throw error;
      setLinks(links.filter(l => l.id !== linkId));
      toast.success('Link deleted!');
      // Auto-refresh preview
      setTimeout(refreshPreview, 500);
    } catch (error) {
      console.error('Error deleting link:', error);
      toast.error('Failed to delete link');
    }
  };

  // Reorder links
  const moveLink = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= links.length) return;

    const newLinks = [...links];
    const [movedLink] = newLinks.splice(index, 1);
    newLinks.splice(newIndex, 0, movedLink);

    // Update display_order
    const updatedLinks = newLinks.map((link, i) => ({ ...link, display_order: i }));
    setLinks(updatedLinks);

    // Save to database
    try {
      for (const link of updatedLinks) {
        await supabase
          .from('bio_links')
          .update({ display_order: link.display_order })
          .eq('id', link.id);
      }
    } catch (error) {
      console.error('Error reordering links:', error);
    }
  };

  // Partner program functions
  const loadPartnerReferrals = async () => {
    if (!partnerData?.id) return;
    setPartnerReferralsLoading(true);
    try {
      const { data } = await supabase.rpc('get_partner_referrals', { p_partner_id: partnerData.id });
      setPartnerReferrals(data || []);
    } catch (e) { console.error('Referrals load error:', e); }
    finally { setPartnerReferralsLoading(false); }
  };

  const copyPartnerLink = async () => {
    if (!partnerData) return;
    await navigator.clipboard.writeText(`https://shoutout.us/start?ref=${partnerData.referral_token}`);
    setPartnerCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setPartnerCopied(false), 2000);
  };

  const SUGGESTED_SCRIPT = `Hey there, I'm working with this new company that's trying to help free-speech creators earn more, ShoutOut.

It's a link in bio service (like linktree) but you can actually monetize your audience faster with

- pre-paid sponsor ads that go on the link (earn like $600-$1200 per 1000 clicks)
- sell video shoutouts (like cameo)
- and get donations and stuff all through the link

I'm helping them find creators that might benefit. So if you want I'll send you my link to get started?`;

  const copySuggestedScript = async () => {
    await navigator.clipboard.writeText(SUGGESTED_SCRIPT);
    setScriptCopied(true);
    toast.success('Script copied!');
    setTimeout(() => setScriptCopied(false), 2000);
  };

  const copyPartnerLinkSidebar = async () => {
    if (!partnerData) return;
    await navigator.clipboard.writeText(`https://shoutout.us/start?ref=${partnerData.referral_token}`);
    setPartnerLinkCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setPartnerLinkCopied(false), 2000);
  };

  const saveFollowerCounts = async () => {
    if (!partnerData?.id || !followerModalTalent) return;
    setFollowerSaving(true);
    try {
      const cleaned: Record<string, number> = {};
      Object.entries(followerInputs).forEach(([k, v]) => { if (v > 0) cleaned[k] = v; });
      const { data: ok } = await supabase.rpc('partner_update_referral_followers', {
        p_partner_id: partnerData.id,
        p_talent_id: followerModalTalent.id,
        p_follower_counts: cleaned,
      });
      if (ok === false) { toast.error('Failed to save'); return; }
      toast.success('Followers saved!');
      setFollowerModalTalent(null);
      loadPartnerReferrals();
    } catch (err: any) { toast.error(err.message || 'Failed to save'); }
    finally { setFollowerSaving(false); }
  };

  // Auto-load partner referrals when partner data is available
  useEffect(() => {
    if (partnerData?.id) loadPartnerReferrals();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerData?.id]);

  // Auto-load orders on init for badge count
  useEffect(() => {
    if (talentProfile?.id && talentOrders.length === 0) loadTalentOrders();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentProfile?.id]);

  // Load talent orders
  const loadTalentOrders = async () => {
    if (!talentProfile?.id) return;
    setOrdersLoading(true);
    try {
      const { data } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-talent-orders', talentId: talentProfile.id },
      });
      if (data?.orders) setTalentOrders(data.orders);
    } catch (e) { console.log('Orders load error:', e); }
    finally { setOrdersLoading(false); }
  };

  const loadTalentPayouts = async () => {
    if (!talentProfile?.id) return;
    setPayoutsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-talent-payouts', talentId: talentProfile.id },
      });
      console.log('[loadTalentPayouts] lunarpay-api data:', data, 'error:', error);
      if (data) {
        // All-time sponsor ad earnings (for total earned display)
        const { data: sponsorClicks } = await supabase
          .from('sponsor_clicks')
          .select('creator_share_cents')
          .eq('talent_id', talentProfile.id);

        const sponsorEarnings = ((sponsorClicks ?? []) as any[]).reduce(
          (sum, s) => sum + Number(s.creator_share_cents || 0), 0
        ) / 100;

        // Current-month accruing ad revenue (not yet batched — paid out on the 1st)
        const now = new Date();
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1).toISOString();
        const { data: monthClicks } = await supabase
          .from('sponsor_clicks')
          .select('creator_share_cents')
          .eq('talent_id', talentProfile.id)
          .gte('created_at', monthStart)
          .lt('created_at', monthEnd);
        const accruingAdRevenueCents = ((monthClicks ?? []) as any[]).reduce(
          (sum, s) => sum + Number(s.creator_share_cents || 0), 0
        );

        // Past ad-revenue payout batches (prior months already processed)
        const { data: adBatches } = await supabase
          .from('payout_batches')
          .select('*')
          .eq('talent_id', talentProfile.id)
          .eq('kind', 'ad_revenue')
          .order('week_start_date', { ascending: false });

        console.log('[loadTalentPayouts] sponsorEarnings:', sponsorEarnings, 'accruing cents:', accruingAdRevenueCents, 'adBatches:', adBatches?.length);

        setPayoutsData({
          ...data,
          totalEarnings: (data.totalEarnings || 0) + sponsorEarnings,
          adRevenueBatches: adBatches || [],
          accruingAdRevenueCents,
        });
      }
    } catch (e) { console.log('Payouts load error:', e); }
    finally { setPayoutsLoading(false); }
  };

  const uploadVideoToWasabi = async (file: File, opts: { orderId?: string; talentId?: string; uploadType: 'order' | 'promo' }): Promise<string> => {
    const validExts = ['.mp4', '.mov', '.webm', '.avi', '.mkv', '.m4v'];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!file.type.startsWith('video/') && !validExts.includes(ext)) throw new Error('Please select a video file');
    if (file.size > 1000 * 1024 * 1024) throw new Error('Video must be less than 1GB');

    const { data, error } = await supabase.functions.invoke('video-upload', {
      body: { orderId: opts.orderId, talentId: opts.talentId, fileName: file.name, contentType: file.type, uploadType: opts.uploadType },
    });
    if (error || !data?.uploadUrl) throw new Error(error?.message || 'Failed to get upload URL');

    const resp = await fetch(data.uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'video/mp4' },
    });
    if (!resp.ok) throw new Error(`Upload failed (${resp.status})`);
    return data.publicUrl;
  };

  // Handle video upload for order fulfillment
  const handleOrderVideoUpload = async (orderId: string, file: File) => {
    setUploadingOrderId(orderId);
    try {
      const videoUrl = await uploadVideoToWasabi(file, { orderId, uploadType: 'order' });
      const { data, error } = await supabase.functions.invoke('lunarpay-api', {
        body: {
          action: 'update-order',
          orderId,
          updates: { status: 'completed', video_url: videoUrl },
          talentName: talentProfile?.full_name || 'Your talent',
        },
      });
      if (error || !data?.success) throw new Error(error?.message || data?.error || 'Failed to complete order');
      setTalentOrders(prev => prev.map(o => o.id === orderId ? { ...o, status: 'completed', video_url: videoUrl } : o));
      toast.success('Order delivered! Customer has been notified.');
    } catch (e: any) { toast.error(e.message || 'Upload failed'); }
    finally { setUploadingOrderId(null); }
  };

  // Auto-load orders on dashboard init so the badge count is available immediately
  useEffect(() => {
    if (talentProfile?.id && talentOrders.length === 0) {
      loadTalentOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentProfile?.id]);

  const loadDonationMessages = async () => {
    if (!talentProfile?.id) return;
    setDonationMessagesLoading(true);
    try {
      const { data } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-donation-messages', talentId: talentProfile.id },
      });
      if (data?.messages) setDonationMessages(data.messages);
    } catch (e) { console.log('Messages load error:', e); }
    finally { setDonationMessagesLoading(false); }
  };

  const markMessagesRead = async () => {
    if (!talentProfile?.id) return;
    await supabase.functions.invoke('lunarpay-api', {
      body: { action: 'mark-messages-read', talentId: talentProfile.id },
    }).catch(() => {});
    setDonationMessages(prev => prev.map(m => ({ ...m, is_read: true })));
  };

  const handleReply = async (messageId: string) => {
    if (!replyText.trim() || sendingReply) return;
    setSendingReply(true);
    try {
      await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'reply-to-message', messageId, reply: replyText.trim() },
      });
      setDonationMessages(prev => prev.map(m =>
        m.id === messageId ? { ...m, talent_reply: replyText.trim(), talent_reply_at: new Date().toISOString() } : m
      ));
      setReplyText('');
      setReplyingTo(null);
    } catch (e) { console.error('Reply error:', e); }
    finally { setSendingReply(false); }
  };

  const loadFansData = async () => {
    if (!talentProfile?.id) return;
    setFansLoading(true);
    try {
      const { data } = await supabase.functions.invoke('lunarpay-api', {
        body: { action: 'get-talent-fans', talentId: talentProfile.id },
      });
      if (data?.fans) setFansData(data.fans);
    } catch (e) { console.log('Fans load error:', e); }
    finally { setFansLoading(false); }
  };

  // Auto-load donation messages on init for badge count
  useEffect(() => {
    if (talentProfile?.id && donationMessages.length === 0) {
      loadDonationMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentProfile?.id]);

  // Copy bio URL + show Instagram disclaimer if not yet acknowledged
  const copyBioUrl = () => {
    const url = `https://shoutout.fans/${talentProfile?.username || talentProfile?.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Bio URL copied!');
    const acknowledged = localStorage.getItem(`ig_disclaimer_${talentProfile?.id}`);
    if (!acknowledged) {
      setIgDisclaimerChecked(false);
      setShowIgDisclaimer(true);
    }
  };

  // Publish/unpublish
  const togglePublish = async () => {
    await saveSettings({ is_published: !bioSettings?.is_published });
  };

  // Import from URL - Uses a CORS proxy to fetch and parse link-in-bio pages
  const importFromUrl = async (url: string) => {
    if (!talentProfile?.id) return;
    
    toast.loading('Importing links...', { id: 'import' });
    setShowImportModal(false);
    
    try {
      // Use a CORS proxy to fetch the page
      const corsProxies = [
        `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
        `https://corsproxy.io/?${encodeURIComponent(url)}`,
      ];
      
      let html = '';
      let fetchSuccess = false;
      
      for (const proxyUrl of corsProxies) {
        try {
          const response = await fetch(proxyUrl);
          if (response.ok) {
            html = await response.text();
            fetchSuccess = true;
            break;
          }
        } catch (e) {
          console.log('Proxy failed, trying next...', e);
        }
      }
      
      if (!fetchSuccess || !html) {
        throw new Error('Could not fetch the page. Please check the URL and try again.');
      }
      
      // Parse the HTML to extract links
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      const extractedLinks: Array<{
        title: string;
        url: string;
        thumbnail_url?: string;
      }> = [];
      
      // Common selectors for link-in-bio platforms
      const linkSelectors = [
        // Linktree
        'a[data-testid="LinkButton"]',
        'a[data-testid="StyledContainer"]',
        // Beacons
        'a.link-block',
        'a[class*="LinkButton"]',
        // Stan Store
        'a[class*="product-card"]',
        'a[class*="link-card"]',
        // Generic patterns
        'a[href^="http"]:not([href*="facebook.com/sharer"]):not([href*="twitter.com/intent"]):not([href*="linkedin.com/sharing"])',
        // Main content links
        '.links a',
        '.link-list a',
        '[class*="link"] a',
        '[class*="Link"] a',
      ];
      
      // Try each selector
      for (const selector of linkSelectors) {
        try {
          const elements = doc.querySelectorAll(selector);
          elements.forEach((el) => {
            const anchor = el as HTMLAnchorElement;
            const href = anchor.getAttribute('href');
            
            // Skip unwanted URLs (share buttons, privacy, etc.)
            const jsPrefix = 'javascript'; // eslint workaround
            if (!href || 
                href.startsWith('#') || 
                href.startsWith(jsPrefix + ':') ||
                href === url ||
                shouldSkipUrl(href)) {
              return;
            }
            
            // Get the title from various sources
            let title = '';
            const titleEl = anchor.querySelector('h3, h4, p, span, [class*="title"], [class*="Title"]');
            if (titleEl) {
              title = titleEl.textContent?.trim() || '';
            }
            if (!title) {
              title = anchor.textContent?.trim() || '';
            }
            if (!title) {
              title = anchor.getAttribute('aria-label') || '';
            }
            
            // Clean up title - remove extra whitespace
            title = title.replace(/\s+/g, ' ').trim();
            
            // Skip if no meaningful title or too long (probably scraped wrong content)
            if (!title || title.length > 100 || title.length < 2) {
              return;
            }
            
            // Get thumbnail if available
            let thumbnail_url = '';
            const img = anchor.querySelector('img');
            if (img) {
              thumbnail_url = img.getAttribute('src') || img.getAttribute('data-src') || '';
              // Make relative URLs absolute
              if (thumbnail_url && !thumbnail_url.startsWith('http')) {
                try {
                  const baseUrl = new URL(url);
                  thumbnail_url = new URL(thumbnail_url, baseUrl.origin).href;
                } catch {
                  thumbnail_url = '';
                }
              }
            }
            
            // Check for duplicates
            const isDuplicate = extractedLinks.some(
              (l) => l.url === href || l.title === title
            );
            
            if (!isDuplicate && href) {
              extractedLinks.push({
                title,
                url: href,
                thumbnail_url: thumbnail_url || undefined,
              });
            }
          });
        } catch (e) {
          console.log('Selector failed:', selector, e);
        }
      }
      
      // Also try to find links in JSON-LD or meta tags
      const scripts = doc.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach((script) => {
        try {
          const data = JSON.parse(script.textContent || '');
          if (data.mainEntity?.itemListElement) {
            data.mainEntity.itemListElement.forEach((item: any) => {
              if (item.url && item.name) {
                const isDuplicate = extractedLinks.some((l) => l.url === item.url);
                if (!isDuplicate) {
                  extractedLinks.push({
                    title: item.name,
                    url: item.url,
                  });
                }
              }
            });
          }
        } catch {
          // Ignore JSON parse errors
        }
      });
      
      // Separate social links from regular links
      const regularLinks: typeof extractedLinks = [];
      const detectedSocialLinks: Array<{ platform: string; url: string; handle: string }> = [];
      
      for (const link of extractedLinks) {
        const socialPlatform = detectSocialPlatform(link.url);
        if (socialPlatform) {
          // Extract handle from URL
          let handle = link.url;
          try {
            const urlObj = new URL(link.url);
            handle = urlObj.pathname.replace(/^\/+|\/+$/g, '').split('/')[0] || link.title;
            // Remove @ if present
            handle = handle.replace(/^@/, '');
          } catch {
            handle = link.title;
          }
          
          // Check if we already have this social platform
          const existingSocial = detectedSocialLinks.find(s => s.platform === socialPlatform);
          if (!existingSocial) {
            detectedSocialLinks.push({
              platform: socialPlatform,
              url: link.url,
              handle,
            });
          }
        } else {
          regularLinks.push(link);
        }
      }
      
      if (regularLinks.length === 0 && detectedSocialLinks.length === 0) {
        throw new Error('No links found on this page. Try a different URL or add links manually.');
      }
      
      // Limit to 20 regular links max
      const linksToImport = regularLinks.slice(0, 20);
      
      // Save regular links to database
      const newLinks: BioLink[] = [];
      for (let i = 0; i < linksToImport.length; i++) {
        const link = linksToImport[i];
        const newLink = {
          talent_id: talentProfile.id,
          link_type: 'basic' as const,
          title: link.title,
          url: link.url,
          thumbnail_url: link.thumbnail_url,
          display_order: links.length + i,
          is_active: true,
        };
        
        const { data, error } = await supabase
          .from('bio_links')
          .insert([newLink])
          .select()
          .single();
        
        if (!error && data) {
          newLinks.push(data);
        }
      }
      
      // Add detected social links to state (merge with existing)
      if (detectedSocialLinks.length > 0) {
        const newSocialLinks: SocialAccount[] = [];
        for (const social of detectedSocialLinks) {
          // Check if platform already exists
          const exists = socialLinks.some(s => s.platform === social.platform);
          if (!exists) {
            newSocialLinks.push({
              id: `imported-${Date.now()}-${social.platform}`,
              platform: social.platform as SocialAccount['platform'],
              handle: social.handle,
            });
          }
        }
        if (newSocialLinks.length > 0) {
          const updatedSocials = [...socialLinks, ...newSocialLinks];
          setSocialLinks(updatedSocials);
          // Save to talent profile
          await supabase.rpc('update_talent_profile', {
            p_id: talentProfile.id,
            p_updates: { social_accounts: updatedSocials },
          });
        }
      }
      
      const totalImported = newLinks.length + detectedSocialLinks.filter(s => !socialLinks.some(existing => existing.platform === s.platform)).length;
      
      if (totalImported > 0) {
        setLinks([...links, ...newLinks]);
        toast.success(`Imported ${newLinks.length} links${detectedSocialLinks.length > 0 ? ` and ${detectedSocialLinks.length} social profiles` : ''}!`, { id: 'import' });
        // Auto-refresh preview
        setTimeout(refreshPreview, 500);
      } else {
        throw new Error('No new links to import (may already exist).');
      }
      
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error(error.message || 'Failed to import links. Please try again.', { id: 'import' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading your bio dashboard...</p>
        </div>
      </div>
    );
  }

  // Talent login handlers
  const handleCreatorLogin = async () => {
    if (!loginEmail) return;
    setLoginLoading(true);
    try {
      // Check if this is a demo account (password-based login)
      const { data: demoCheck } = await supabase
        .from('demo_accounts')
        .select('id')
        .eq('email', loginEmail.toLowerCase().trim())
        .maybeSingle();
      if (demoCheck) {
        setLoginStep('password');
        setLoginLoading(false);
        return;
      }
      const { data } = await supabase.functions.invoke('send-registration-otp', {
        body: { email: loginEmail.toLowerCase().trim(), checkEmailOnly: true },
      });
      if (data?.sentToExistingPhone) {
        setLoginPhone(data.phone || '');
        setLoginStep('otp');
        toast.success('Code sent!');
      } else {
        setLoginStep('phone');
      }
    } catch { toast.error('Failed to check account'); }
    finally { setLoginLoading(false); }
  };

  const handleDemoLogin = async () => {
    if (!loginPassword) return;
    setLoginLoading(true);
    try {
      const { data, error } = await supabase.rpc('verify_demo_login', {
        p_email: loginEmail.toLowerCase().trim(),
        p_password: loginPassword,
      });
      if (error || !data?.success) { toast.error('Invalid password'); setLoginLoading(false); return; }
      localStorage.setItem('creator_session', JSON.stringify({ userId: data.user_id, email: loginEmail.toLowerCase().trim(), talentId: data.talent_id }));
      localStorage.setItem(`dashboard_welcome_seen_${data.talent_id}`, 'true');
      window.location.href = `/dashboard?token=${data.talent_id}&tab=customize`;
    } catch { toast.error('Login failed'); }
    finally { setLoginLoading(false); }
  };

  const handleCreatorPhone = async () => {
    setLoginLoading(true);
    try {
      await supabase.functions.invoke('send-registration-otp', { body: { email: loginEmail.toLowerCase().trim(), phone: loginPhone } });
      setLoginStep('otp');
      toast.success('Code sent!');
    } catch { toast.error('Failed to send code'); }
    finally { setLoginLoading(false); }
  };

  const handleCreatorOtp = async () => {
    setLoginLoading(true);
    try {
      const verifyPhone = loginPhone && !loginPhone.includes('*') ? loginPhone : undefined;
      const { data, error } = await supabase.functions.invoke('verify-registration-otp', {
        body: { email: loginEmail.toLowerCase().trim(), phone: verifyPhone, code: loginOtp },
      });
      if (error || !data?.success) { toast.error('Invalid code'); setLoginLoading(false); return; }
      // Find talent profile for this user
      const { data: usr } = await supabase.from('users').select('id, email, full_name, user_type').eq('email', loginEmail.toLowerCase().trim()).single();
      if (!usr) { toast.error('No account found'); setLoginLoading(false); return; }
      const { data: tp } = await supabase.from('talent_profiles').select('*').eq('user_id', usr.id).single();
      if (!tp) { toast.error('No creator profile found for this account'); setLoginLoading(false); return; }
      localStorage.setItem('creator_session', JSON.stringify({ userId: usr.id, email: usr.email, talentId: tp.id }));
      window.location.href = `/dashboard?token=${tp.id}`;
    } catch { toast.error('Verification failed'); }
    finally { setLoginLoading(false); }
  };

  if (authError) {
    // Check for saved creator session
    const savedSession = localStorage.getItem('creator_session');
    if (savedSession) {
      try {
        const sess = JSON.parse(savedSession);
        if (sess.talentId) { window.location.href = `/dashboard?token=${sess.talentId}`; return null; }
      } catch { localStorage.removeItem('creator_session'); }
    }

    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center p-4">
        <img src="/shoutoutwhite.png" alt="ShoutOut" className="h-10 mb-6 opacity-60" />
        <h1 className="text-2xl font-bold text-white mb-2">Creator Login</h1>
        <p className="text-gray-400 mb-6 text-center text-sm">Sign in to manage your bio page</p>
        <div className="w-full max-w-sm space-y-4">
          {loginStep === 'email' && (<>
            <input type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreatorLogin()} placeholder="Enter your email" className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40" />
            <button onClick={handleCreatorLogin} disabled={loginLoading} className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {loginLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Continue'}
            </button>
          </>)}
          {loginStep === 'phone' && (<>
            <input type="tel" value={loginPhone} onChange={e => setLoginPhone(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreatorPhone()} placeholder="Phone number" className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40" />
            <button onClick={handleCreatorPhone} disabled={loginLoading} className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {loginLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Send Code'}
            </button>
            <button onClick={() => setLoginStep('email')} className="w-full text-gray-400 text-sm hover:text-white">Back</button>
          </>)}
          {loginStep === 'password' && (<>
            <input type="password" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleDemoLogin()} placeholder="Enter password" className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40" autoFocus />
            <button onClick={handleDemoLogin} disabled={loginLoading || !loginPassword} className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {loginLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Sign In'}
            </button>
            <button onClick={() => { setLoginStep('email'); setLoginPassword(''); }} className="w-full text-gray-400 text-sm hover:text-white">Back</button>
          </>)}
          {loginStep === 'otp' && (<>
            <p className="text-gray-300 text-sm text-center">Code sent to {loginPhone ? (loginPhone.includes('*') ? loginPhone : '***' + loginPhone.slice(-4)) : 'your phone'}</p>
            <input type="text" value={loginOtp} onChange={e => setLoginOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} onKeyDown={e => e.key === 'Enter' && handleCreatorOtp()} placeholder="Enter code" className="w-full bg-white/5 border border-white/20 px-4 py-3 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-white/40 text-center text-2xl tracking-widest" maxLength={6} autoFocus />
            <button onClick={handleCreatorOtp} disabled={loginLoading || loginOtp.length < 4} className="w-full py-3 rounded-xl font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {loginLoading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /> : 'Verify'}
            </button>
            <button onClick={() => { setLoginStep('email'); setLoginOtp(''); }} className="w-full text-gray-400 text-sm hover:text-white">Back</button>
          </>)}
        </div>
      </div>
    );
  }

  const bioUrl = `shoutout.fans/${talentProfile?.username || talentProfile?.id}`;

  return (
    <div className="bg-[#0a0a0a] h-screen flex flex-col overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute inset-0"
          style={{
            background: `linear-gradient(${bioSettings?.gradient_direction === 'to-b' ? '180deg' : '135deg'}, ${bioSettings?.gradient_start || '#0a0a0a'}, ${bioSettings?.gradient_end || '#1a1a2e'})`
          }}
        />
      </div>

      {/* Header - outside scroll flow, pinned to top */}
      <header className="flex-shrink-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10 relative" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-3">
            <img
              src="/whiteicon.png"
              alt="ShoutOut"
              className="h-7 sm:h-8 w-auto flex-shrink-0"
            />
            {/* Mobile Menu button — visible only on mobile */}
            <div className="flex sm:hidden">
              <button
                onClick={() => setShowMobileMenu(prev => !prev)}
                className={`px-3 py-2 rounded-xl text-sm font-medium transition-all border ${showMobileMenu ? 'bg-white/15 border-white/20 text-white' : 'bg-white/5 border-white/10 text-gray-300 hover:text-white'}`}
              >
                {showMobileMenu ? '✕ Close' : '☰ Menu'}
              </button>
            </div>
            {/* Right side: stats + avatar */}
            <div className="flex items-center gap-2 ml-auto">
            {/* Scrollable tab bar — desktop only, replaced by left sidebar */}
            <div className="hidden flex-1 min-w-0 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="flex bg-white/5 rounded-xl p-1 w-max">
                {!isDemoAccount && !(mainView === 'dashboard' || mainView === 'course') && (
                  <button
                    onClick={() => setMainView('dashboard')}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all text-gray-400 hover:text-white whitespace-nowrap"
                  >
                    Dashboard
                  </button>
                )}
                {mainView !== 'customize' && (
                  <button
                    onClick={() => setMainView('customize')}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all text-gray-400 hover:text-white whitespace-nowrap"
                  >
                    Design
                  </button>
                )}
                {mainView !== 'orders' && (
                  <button
                    onClick={() => { setMainView('orders'); if (talentOrders.length === 0) loadTalentOrders(); }}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 text-gray-400 hover:text-white whitespace-nowrap"
                  >
                    Orders
                    {talentOrders.filter(o => (o.status === 'pending' || o.status === 'in_progress') && o.service_type !== 'donation').length > 0 && (
                      <span className="bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {talentOrders.filter(o => (o.status === 'pending' || o.status === 'in_progress') && o.service_type !== 'donation').length}
                      </span>
                    )}
                  </button>
                )}
                {mainView !== 'send_updates' && (
                  <button
                    onClick={() => setMainView('send_updates')}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 text-gray-400 hover:text-white whitespace-nowrap"
                  >
                    Fans
                    {donationMessages.filter(m => !m.is_read).length > 0 && (
                      <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {donationMessages.filter(m => !m.is_read).length}
                      </span>
                    )}
                    {subscriberCount > 0 && donationMessages.filter(m => !m.is_read).length === 0 && (
                      <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">{subscriberCount}</span>
                    )}
                  </button>
                )}
                {partnerProgramEnabled && partnerData && mainView !== 'partner' && (
                  <button
                    onClick={() => { setMainView('partner'); if (partnerReferrals.length === 0) loadPartnerReferrals(); }}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 text-gray-400 hover:text-white whitespace-nowrap"
                  >
                    Partner
                    {partnerReferrals.length > 0 && (
                      <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">{partnerReferrals.length}</span>
                    )}
                  </button>
                )}
                {networkEnabled && mainView !== 'network' && (
                  <button
                    onClick={() => setMainView('network')}
                    className="px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-all text-gray-400 hover:text-white whitespace-nowrap"
                  >
                    Network
                  </button>
                )}
              </div>
            </div>

            {/* Desktop stats */}
            <div className="hidden lg:flex items-center gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded-xl flex-shrink-0" title="Rolling 7-day stats">
              <EyeIcon className="h-4 w-4 text-blue-400" />
              <div className="flex flex-col items-center">
                <span className="text-lg font-bold text-white leading-none">{rolling7dViews.toLocaleString()}</span>
                <span className="text-[10px] text-gray-400 leading-none mt-0.5">7d views</span>
              </div>
              <div className="h-8 w-px bg-white/10" />
              <div className="flex flex-col items-center">
                <span className="text-sm font-semibold text-blue-400 leading-none">{rolling7dCtr}%</span>
                <span className="text-[10px] text-gray-400 leading-none mt-0.5">CTR</span>
              </div>
            </div>
            {/* Profile avatar — always visible */}
            <div className="relative flex-shrink-0">
              <button
                onClick={(e) => { e.stopPropagation(); setShowTalentMenu(!showTalentMenu); }}
                className="w-9 h-9 rounded-full overflow-hidden border-2 border-white/20 hover:border-white/40 transition-colors"
              >
                {talentProfile?.temp_avatar_url || bioSettings?.profile_image_url ? (
                  <img src={bioSettings?.profile_image_url || talentProfile?.temp_avatar_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-white/10 flex items-center justify-center text-white text-sm font-bold">
                    {(talentProfile?.full_name || '?').charAt(0)}
                  </div>
                )}
              </button>
              {showTalentMenu && (
                <>
                  <div className="absolute right-0 top-12 w-56 bg-gray-900 border border-white/10 rounded-xl shadow-2xl py-2 z-50" onMouseDown={(e) => e.stopPropagation()}>
                  <div className="px-4 py-2 border-b border-white/10">
                    <p className="text-white text-sm font-medium truncate">{talentProfile?.full_name || 'Creator'}</p>
                    <p className="text-gray-400 text-xs truncate">@{talentProfile?.username}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setMainView('payouts'); loadTalentPayouts(); setShowTalentMenu(false); }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left"
                  >
                    <CurrencyDollarIcon className="h-4 w-4" /> Payouts
                  </button>
                  <button
                    onClick={() => { copyBioUrl(); setShowTalentMenu(false); }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left"
                  >
                    <ClipboardIcon className="h-4 w-4" /> Copy Bio URL
                  </button>
                  <a
                    href={`https://shoutout.fans/${talentProfile?.username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                    onClick={() => setShowTalentMenu(false)}
                  >
                    <ArrowTopRightOnSquareIcon className="h-4 w-4" /> View Live Page
                  </a>
                  {partnerProgramEnabled && partnerData && (
                    <button
                      onClick={() => { setMainView('partner'); setShowTalentMenu(false); if (partnerReferrals.length === 0) loadPartnerReferrals(); }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left"
                    >
                      <UserGroupIcon className="h-4 w-4" /> Partner Program
                    </button>
                  )}
                  <button
                    onClick={() => { setProfileEditName(talentProfile?.full_name || ''); setProfileEditAvatar(talentProfile?.temp_avatar_url || bioSettings?.profile_image_url || ''); setProfileEditUsername(talentProfile?.username || ''); setProfileUsernameAvailable(null); setShowProfileModal(true); setShowTalentMenu(false); }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left"
                  >
                    <UserCircleIcon className="h-4 w-4" /> Profile
                  </button>
                  <button
                    onClick={() => { setShowSettingsModal(true); setShowTalentMenu(false); }}
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 hover:text-white transition-colors w-full text-left"
                  >
                    <Cog6ToothIcon className="h-4 w-4" /> Settings
                  </button>
                  <div className="border-t border-white/10 mt-1 pt-1">
                    <button
                      onClick={() => { localStorage.removeItem('creator_session'); window.location.href = '/dashboard'; }}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 hover:text-red-300 transition-colors w-full text-left"
                    >
                      <XMarkIcon className="h-4 w-4" /> Sign Out
                    </button>
                  </div>
                </div>
                </>
              )}
            </div>
            </div>{/* end right-side group */}
          </div>
        </div>

        {/* Mobile slide-down nav removed — replaced by left drawer above */}
      </header>

      {/* Mobile left-drawer — slides in from the left, sm:hidden */}
      <div
        className={`sm:hidden fixed inset-0 z-[60] transition-all duration-300 ${showMobileMenu ? 'pointer-events-auto' : 'pointer-events-none'}`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${showMobileMenu ? 'opacity-100' : 'opacity-0'}`}
          onClick={() => setShowMobileMenu(false)}
        />
        {/* Drawer panel */}
        <div
          className={`absolute left-0 top-0 h-full w-72 bg-[#111] border-r border-white/10 flex flex-col py-4 px-3 overflow-y-auto transition-transform duration-300 ease-in-out ${showMobileMenu ? 'translate-x-0' : '-translate-x-full'}`}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between mb-4 px-1">
            <img src="/whiteicon.png" alt="ShoutOut" className="h-7 w-auto" />
            <button
              onClick={() => setShowMobileMenu(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {/* Nav items */}
          <div className="flex flex-col gap-0.5">
            {[
              { label: 'Dashboard', view: 'dashboard' as const, onClick: () => { setMainView('dashboard'); setShowMobileMenu(false); } },
              { label: 'Design', view: 'customize' as const, onClick: () => { setMainView('customize'); setActiveTab('links'); setShowMobileMenu(false); } },
              { label: 'ShoutOuts', view: 'customize' as const, onClick: () => { setMainView('customize'); setActiveTab('shoutouts'); setShowMobileMenu(false); } },
              { label: 'Orders', view: 'orders' as const, onClick: () => { setMainView('orders'); if (talentOrders.length === 0) loadTalentOrders(); setShowMobileMenu(false); } },
              { label: 'Fans', view: 'send_updates' as const, onClick: () => { setMainView('send_updates'); setShowMobileMenu(false); } },
              { label: 'Payouts', view: 'payouts' as const, onClick: () => { setMainView('payouts'); loadTalentPayouts(); setShowMobileMenu(false); } },
              ...(partnerProgramEnabled && partnerData ? [{ label: 'Partner', view: 'partner' as const, onClick: () => { setMainView('partner'); if (partnerReferrals.length === 0) loadPartnerReferrals(); setShowMobileMenu(false); } }] : []),
              ...(networkEnabled ? [{ label: 'Network', view: 'network' as const, onClick: () => { setMainView('network'); setShowMobileMenu(false); } }] : []),
            ].map(({ label, view, onClick }) => (
              <button
                key={label}
                onClick={onClick}
                className={`w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all flex items-center justify-between ${
                  mainView === view && !(label === 'ShoutOuts' && activeTab !== 'shoutouts')
                    ? 'bg-white/15 text-white'
                    : 'text-gray-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                <span>{label}</span>
                {label === 'Orders' && talentOrders.filter(o => (o.status === 'pending' || o.status === 'in_progress') && o.service_type !== 'donation').length > 0 && (
                  <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {talentOrders.filter(o => (o.status === 'pending' || o.status === 'in_progress') && o.service_type !== 'donation').length}
                  </span>
                )}
                {label === 'Fans' && donationMessages.filter(m => !m.is_read).length > 0 && (
                  <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                    {donationMessages.filter(m => !m.is_read).length}
                  </span>
                )}
                {label === 'Partner' && partnerReferrals.length > 0 && (
                  <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">{partnerReferrals.length}</span>
                )}
              </button>
            ))}
            <div className="border-t border-white/10 my-2" />
            <button
              onClick={() => { setProfileEditName(talentProfile?.full_name || ''); setProfileEditAvatar(talentProfile?.temp_avatar_url || bioSettings?.profile_image_url || ''); setProfileEditUsername(talentProfile?.username || ''); setProfileUsernameAvailable(null); setShowProfileModal(true); setShowMobileMenu(false); }}
              className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all text-gray-300 hover:bg-white/10 hover:text-white"
            >
              Profile
            </button>
            <button
              onClick={() => { setShowSettingsModal(true); setShowMobileMenu(false); }}
              className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all text-gray-300 hover:bg-white/10 hover:text-white"
            >
              Settings
            </button>
            <div className="border-t border-white/10 my-2" />
            <button
              onClick={() => { localStorage.removeItem('creator_session'); window.location.href = '/dashboard'; }}
              className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium transition-all text-red-400 hover:bg-white/10 hover:text-red-300"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Below-header layout: left sidebar (desktop) + scrollable content */}
      <div className="flex-1 flex overflow-hidden">

        {/* Desktop left sidebar — hidden on mobile */}
        <aside className="hidden sm:flex flex-col w-52 flex-shrink-0 border-r border-white/10 bg-black/20 backdrop-blur-xl overflow-y-auto py-3 px-2 gap-0.5">
          {[
            { label: 'Dashboard', view: 'dashboard' as const, onClick: () => setMainView('dashboard') },
            { label: 'Design', view: 'customize' as const, onClick: () => { setMainView('customize'); setActiveTab('links'); } },
            { label: 'ShoutOuts', view: 'customize' as const, onClick: () => { setMainView('customize'); setActiveTab('shoutouts'); } },
            { label: 'Orders', view: 'orders' as const, onClick: () => { setMainView('orders'); if (talentOrders.length === 0) loadTalentOrders(); } },
            { label: 'Fans', view: 'send_updates' as const, onClick: () => setMainView('send_updates') },
            { label: 'Payouts', view: 'payouts' as const, onClick: () => { setMainView('payouts'); loadTalentPayouts(); } },
            ...(partnerProgramEnabled && partnerData ? [{ label: 'Partner', view: 'partner' as const, onClick: () => { setMainView('partner'); if (partnerReferrals.length === 0) loadPartnerReferrals(); } }] : []),
            ...(networkEnabled ? [{ label: 'Network', view: 'network' as const, onClick: () => setMainView('network') }] : []),
          ].map(({ label, view, onClick }) => (
            <button
              key={label}
              onClick={onClick}
              className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all flex items-center justify-between ${
                mainView === view && !(label === 'ShoutOuts' && activeTab !== 'shoutouts')
                  ? 'bg-white/15 text-white'
                  : 'text-gray-400 hover:bg-white/8 hover:text-white'
              }`}
            >
              <span>{label}</span>
              {label === 'Orders' && talentOrders.filter(o => (o.status === 'pending' || o.status === 'in_progress') && o.service_type !== 'donation').length > 0 && (
                <span className="bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {talentOrders.filter(o => (o.status === 'pending' || o.status === 'in_progress') && o.service_type !== 'donation').length}
                </span>
              )}
              {label === 'Fans' && donationMessages.filter(m => !m.is_read).length > 0 && (
                <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {donationMessages.filter(m => !m.is_read).length}
                </span>
              )}
              {label === 'Partner' && partnerReferrals.length > 0 && (
                <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">{partnerReferrals.length}</span>
              )}
            </button>
          ))}
          {/* Divider before profile/settings */}
          <div className="border-t border-white/10 my-1" />
          <button
            onClick={() => { setProfileEditName(talentProfile?.full_name || ''); setProfileEditAvatar(talentProfile?.temp_avatar_url || bioSettings?.profile_image_url || ''); setProfileEditUsername(talentProfile?.username || ''); setProfileUsernameAvailable(null); setShowProfileModal(true); }}
            className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-gray-400 hover:bg-white/8 hover:text-white`}
          >
            Profile
          </button>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-gray-400 hover:bg-white/8 hover:text-white"
          >
            Settings
          </button>
          <div className="border-t border-white/10 my-1" />
          <button
            onClick={() => { localStorage.removeItem('creator_session'); window.location.href = '/dashboard'; }}
            className="w-full text-left px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-red-400 hover:bg-white/8 hover:text-red-300"
          >
            Sign Out
          </button>
        </aside>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto relative" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      <div className="max-w-6xl mx-auto px-4 py-6 relative z-10">
        {mainView === 'dashboard' && talentProfile ? (
          <CreatorHubDashboard
            talentProfile={talentProfile}
            onNavigate={(view) => {
              if (view === 'course') {
                setMainView('course');
              } else if (view === 'chat:creators') {
                supabase.from('chat_channels').select('id').eq('channel_type', 'general').ilike('name', 'creators').single()
                  .then(({ data }) => { if (data) setPushDeepLinkChannelId(data.id); });
                setPushOpenChat(true);
              } else if (view === 'chat') {
                setPushOpenChat(true);
              } else if (view === 'customize') {
                setMainView('customize');
              } else {
                setMainView(view as any);
              }
            }}
          />
        ) : mainView === 'course' && talentProfile ? (
          <CreatorCourse
            talentProfile={talentProfile}
            onBack={() => setMainView('dashboard')}
          />
        ) : mainView === 'welcome' ? (
          /* Welcome view — matches shoutout.us talent dashboard welcome tab */
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Welcome Header */}
            <div className="text-center py-4">
              <h1 className="text-lg sm:text-xl font-bold text-white leading-tight max-w-[600px] mx-auto">
                How ShoutOut enables you to transform your audience into a{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">monetizable</span>,{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-400 to-red-400">uncancellable</span>
                {' '}engine that fuels your growth.
              </h1>
            </div>

            {/* Section 1: Kickstart Monetization */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 flex items-center justify-center">
                  <VideoCameraIcon className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-bold text-white">Kickstart monetization through personalized videos</h3>
                </div>
              </div>

              {/* Copy Profile Link */}
              <div
                className="mt-4 mb-4 p-3 rounded-xl bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 border border-emerald-500/20 cursor-pointer hover:border-emerald-400/40 transition-colors"
                onClick={() => copyBioUrl()}
              >
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-white font-medium text-sm">shoutout.fans/{talentProfile?.username || 'yourname'}</span>
                  <ClipboardIcon className="h-4 w-4 text-emerald-400 ml-auto" />
                </div>
              </div>

              {/* Most Effective Way */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <ShareIcon className="h-4 w-4 text-pink-400" />
                  <span className="text-sm font-semibold text-white">Most effective way to start earning</span>
                </div>
                <div className="p-3 rounded-xl bg-gradient-to-r from-pink-500/10 to-orange-500/10 border border-pink-500/20 mb-3">
                  <p className="text-gray-200 text-sm leading-relaxed">
                    After adding any other links to your bio page, use your shoutout.fans link exclusively as your link in bio (Instagram/X suppresses multiple links in bio) and be sure to let your Fans know they can now order a video ShoutOut from you on your link in bio.
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-xs text-gray-300 leading-relaxed">
                    <span className="text-emerald-400 font-semibold">Kaitlin Bennett</span> posted a single story on Instagram with her profile link and got{' '}
                    <span className="text-yellow-400 font-bold bg-yellow-400/10 px-1 rounded">10 orders</span> in the first{' '}
                    <span className="text-emerald-400 font-bold bg-emerald-400/10 px-1 rounded">24 hours</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* Section 2: Replace Linktree */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-4 sm:p-6">
              <div className="mb-6">
                <p className="text-base sm:text-lg font-medium text-white leading-relaxed mb-4">
                  Replace linktree (and other low conversion bio links) on your social media with your{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400 font-bold">ShoutOut Fans</span>
                  {' '}link. Capturing an{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-400 to-red-400 font-semibold">uncancellable audience</span>.
                </p>
                <span className="px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-medium">
                  Free For All ShoutOut Talent
                </span>
              </div>

              {/* Bio Link Carousel */}
              <div className="relative -mx-4 sm:-mx-6 overflow-hidden mb-6">
                <div className="absolute left-0 w-12 sm:w-16 bg-gradient-to-r from-[#0a0a0a] to-transparent z-10 pointer-events-none" style={{ top: 0, bottom: '16px' }} />
                <div className="absolute right-0 w-12 sm:w-16 bg-gradient-to-l from-[#0a0a0a] to-transparent z-10 pointer-events-none" style={{ top: 0, bottom: '16px' }} />
                <div className="overflow-x-clip overflow-y-visible pb-4">
                  <div ref={welcomeCarouselRef} className="flex gap-3" style={{ width: 'max-content' }}>
                    {[...bioCreatorBios, ...bioCreatorBios, ...bioCreatorBios].map((creator, index) => (
                      <div key={index} className="flex-shrink-0 w-[120px]">
                        <div className="relative rounded-xl overflow-hidden border border-white/10 bg-[#1a1a2e]" style={{ boxShadow: '0 15px 30px -8px rgba(0, 0, 0, 0.4)' }}>
                          <div className="relative w-full h-[220px] overflow-hidden">
                            <iframe
                              src={`https://shoutout.fans/${creator.handle}`}
                              title={`${creator.name}'s bio`}
                              className="border-0 rounded-xl origin-top-left pointer-events-none"
                              loading="lazy"
                              scrolling="no"
                              style={{ width: '375px', height: '667px', transform: 'scale(0.32)', transformOrigin: 'top left' }}
                            />
                            <div className="absolute inset-0 cursor-default" />
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                            <p className="font-medium text-[10px] text-white text-center">{creator.name}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Benefits List */}
              <div>
                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10">
                    <StarIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-white"><strong>Sell personalized video shoutouts</strong> directly from your shoutout.fans link</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10">
                    <UserGroupIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-white">Engage followers through fan driven messages and donations</span>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-emerald-500/10">
                    <CurrencyDollarIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-sm text-white">Pre-vetted, pre-paid sponsors are automatically matched to your shoutout fans bio page</span>
                      <span className="inline-block ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-green-500/20 text-green-400 border border-green-500/30">Earn $600–$1,200 per 1,000 ad clicks</span>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                    <ShoppingBagIcon className="h-5 w-5 text-gray-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-gray-400">Coming soon: sell merch like never before</span>
                  </div>
                </div>
                <p className="text-gray-300 text-xs mt-4 text-center">All inside one, powerful link that averages <strong className="text-emerald-400">26% click through rate</strong>.</p>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={() => setMainView('customize')}
              className="w-full py-4 rounded-2xl font-bold text-lg text-white bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2"
            >
              <PaintBrushIcon className="h-5 w-5" />
              Customize Your New Link In Bio
            </button>
          </div>
        ) : mainView === 'customize' ? (
          <>
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {[
                { key: 'links', label: 'Fans Link', icon: LinkIcon },
                { key: 'style', label: 'Style', icon: PaintBrushIcon },
                { key: 'shoutouts', label: 'ShoutOuts', icon: StarIcon },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm whitespace-nowrap transition-all ${
                    activeTab === tab.key
                      ? 'bg-white/20 text-white'
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>

        {/* Mobile Manage/Preview toggle */}
        <div className="lg:hidden flex mb-4 bg-white/5 border border-white/10 rounded-xl p-1">
          <button onClick={() => setMobileView('manage')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${mobileView === 'manage' ? 'bg-white/15 text-white' : 'text-gray-400'}`}>Manage Links</button>
          <button onClick={() => setMobileView('preview')} className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${mobileView === 'preview' ? 'bg-white/15 text-white' : 'text-gray-400'}`}>Preview</button>
            </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Left Column - Editor */}
          <div className={`space-y-6 min-w-0 pb-6 ${mobileView === 'preview' ? 'hidden lg:block' : ''}`}>
            {activeTab === 'links' && (
              <>
                {/* Action Buttons */}
                {/* Admin Suggestions */}
                {adminSuggestions.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {adminSuggestions.map(s => (
                      <div key={s.id} className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-3">
                        <LightBulbIcon className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-amber-200">{s.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{s.body}</p>
                        </div>
                        <button
                          onClick={async () => {
                            await supabase.from('admin_suggestions').update({ dismissed_at: new Date().toISOString() }).eq('id', s.id);
                            setAdminSuggestions(prev => prev.filter(x => x.id !== s.id));
                          }}
                          className="text-gray-500 hover:text-white p-1 flex-shrink-0"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Tagline / One-liner */}
                <div className="mb-4">
                  <label className="block text-xs text-gray-400 mb-1">Tagline</label>
                  <input
                    type="text"
                    value={bioSettings?.one_liner || ''}
                    onChange={e => setBioSettings(prev => prev ? { ...prev, one_liner: e.target.value } : prev)}
                    onBlur={() => saveSettings({ one_liner: bioSettings?.one_liner || '' })}
                    placeholder="Your tagline or one-liner"
                    maxLength={100}
                    className="w-full bg-white/5 border border-white/10 px-3 py-2 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-white/30"
                  />
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-3">
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-blue-500 text-white rounded-xl text-sm font-medium hover:bg-blue-600 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add Link
                  </button>
                  <button
                    onClick={async () => {
                      if (talentProfile?.collabs_enabled) {
                        setServiceModalType('instagram_collab');
                        setShowAddServiceModal(true);
                      } else {
                        setTalentProfile(prev => prev ? { ...prev, collabs_enabled: true } : prev);
                        await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { collabs_enabled: true } });
                        toast.success('Collabs enabled! Add your first collab service.');
                        setServiceModalType('instagram_collab');
                        setShowAddServiceModal(true);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                      talentProfile?.collabs_enabled
                        ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                        : 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600'
                    }`}
                  >
                    {talentProfile?.collabs_enabled ? '✓ Collab' : <><PlusIcon className="h-4 w-4" /> Collab</>}
                  </button>
                  <button
                    onClick={() => { setServiceModalType('sponsorship'); setShowAddServiceModal(true); }}
                    className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl text-sm font-medium hover:from-teal-600 hover:to-cyan-600 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Form
                  </button>
                  <button
                    onClick={() => setShowAddEventModal(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl text-sm font-medium hover:from-orange-600 hover:to-amber-600 transition-colors"
                  >
                    <PlusIcon className="h-4 w-4" />
                    Event
                  </button>
                  <button
                    onClick={() => {
                      if (talentProfile?.support_enabled) {
                        setShowDonateModal(true);
                      } else {
                        setShowDonateModal(true);
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                      talentProfile?.support_enabled
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : 'bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:from-green-600 hover:to-emerald-600'
                    }`}
                  >
                    {talentProfile?.support_enabled ? '✓ Donate On' : <><PlusIcon className="h-4 w-4" /> Donate</>}
                  </button>
                  {sponsorAdsEnabled && (
                    <button
                      onClick={() => { setSponsorBtnBounce(false); setShowSponsorsModal(true); }}
                      className={`relative flex items-center gap-1.5 px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700 transition-colors ${sponsorBtnBounce && !hasActiveSponsors ? 'animate-bounce-subtle' : ''}`}
                    >
                      <CurrencyDollarIcon className="h-4 w-4" />
                      {sponsorEarningsCents !== null && sponsorEarningsCents > 0
                        ? `Sponsorships - $${(sponsorEarningsCents / 100).toFixed(2)}`
                        : 'Pre-paid Sponsors'}
                      {!hasActiveSponsors && (
                        <span className="absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-red-500 text-white leading-none">New</span>
                      )}
                    </button>
                  )}
                </div>

                {/* Links List */}
                <div className="space-y-3">
                  {links.length === 0 ? null : (
                    <>
                      {/* Regular Links (basic, grid, video, sponsor) */}
                      {links.filter(l => !['affiliate', 'donate'].includes(l.link_type)).map((link, index) => (
                        <div
                          key={link.id}
                          className={`bg-white/5 border rounded-xl p-4 transition-all border-white/10 hover:border-white/20 ${!link.is_active ? 'opacity-50' : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Reorder buttons */}
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => moveLink(index, 'up')}
                                disabled={index === 0}
                                className="p-1 text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ArrowsUpDownIcon className="h-4 w-4 rotate-180" />
                              </button>
                              <button
                                onClick={() => moveLink(index, 'down')}
                                disabled={index === links.filter(l => !['affiliate', 'donate'].includes(l.link_type)).length - 1}
                                className="p-1 text-gray-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                              >
                                <ArrowsUpDownIcon className="h-4 w-4" />
                              </button>
                            </div>

                            {/* Thumbnail */}
                            {link.thumbnail_url ? (
                              <img src={link.thumbnail_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                            ) : (
                              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                                link.link_type === 'basic' ? 'bg-blue-500/20' :
                                link.link_type === 'grid' ? 'bg-purple-500/20' :
                                link.link_type === 'video' ? 'bg-red-500/20' :
                                'bg-yellow-500/20'
                              }`}>
                                {link.link_type === 'basic' && <LinkIcon className="h-5 w-5 text-blue-400" />}
                                {link.link_type === 'grid' && <Squares2X2Icon className="h-5 w-5 text-purple-400" />}
                                {link.link_type === 'video' && <VideoCameraIcon className="h-5 w-5 text-red-400" />}
                                {link.link_type === 'sponsor' && <GiftIcon className="h-5 w-5 text-yellow-400" />}
                              </div>
                            )}

                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-white truncate">
                                {link.title || `${link.link_type.charAt(0).toUpperCase() + link.link_type.slice(1)} Link`}
                              </h3>
                              {link.url && (
                                <p className="text-sm text-gray-400 truncate">{link.url}</p>
                              )}
                            </div>

                            {/* CTR Badge */}
                            {link.id && (
                              <span
                                className="text-xs px-2 py-1 rounded-full bg-white/10 text-gray-300 border border-white/10 whitespace-nowrap"
                                title={analyticsStartAt 
                                  ? `CTR since ${new Date(analyticsStartAt).toLocaleDateString()}`
                                  : 'CTR since tracking started'
                                }
                              >
                                {totalViews > 0 
                                  ? `${(((linkClicks[link.id] || 0) / totalViews) * 100).toFixed(1)}%`
                                  : '0.0%'
                                } | {linkClicks[link.id] || 0}
                              </span>
                            )}

                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setEditingLink(link)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => deleteLink(link.id!)}
                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Affiliate Links Section - Smaller, separated */}
                      {links.filter(l => l.link_type === 'affiliate').length > 0 && (
                        <div className="mt-6 pt-6 border-t border-white/10">
                          <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
                            <TagIcon className="h-4 w-4" />
                            Affiliate Links
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {links.filter(l => l.link_type === 'affiliate').map((link) => (
                              <div
                                key={link.id}
                                className={`bg-green-500/10 border border-green-500/30 rounded-lg p-2 transition-all hover:border-green-500/50 relative ${!link.is_active ? 'opacity-50' : ''}`}
                              >
                                {/* Top row: thumbnail + name */}
                                <div className="flex items-center gap-2 mb-1.5">
                                  {link.thumbnail_url ? (
                                    <img src={link.thumbnail_url} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                                  ) : (
                                    <div className="w-8 h-8 rounded bg-green-500/20 flex items-center justify-center flex-shrink-0">
                                      <TagIcon className="h-4 w-4 text-green-400" />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-white truncate">{link.company_name || link.title || 'Affiliate'}</p>
                                    {link.discount_amount && (
                                      <p className="text-[10px] text-green-400">{link.discount_amount}</p>
                                    )}
                                  </div>
                                </div>
                                {/* Bottom row: CTR badge + actions */}
                                <div className="flex items-center justify-between">
                                  {link.id && (
                                    <span
                                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10 whitespace-nowrap"
                                      title={analyticsStartAt 
                                        ? `CTR since ${new Date(analyticsStartAt).toLocaleDateString()}`
                                        : 'CTR'
                                      }
                                    >
                                      {totalViews > 0 
                                        ? `${(((linkClicks[link.id] || 0) / totalViews) * 100).toFixed(1)}%`
                                        : '0%'
                                      } | {linkClicks[link.id] || 0}
                                    </span>
                                  )}
                                  <div className="flex items-center gap-0.5">
                                    <button
                                      onClick={() => setEditingLink(link)}
                                      className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                                    >
                                      <PencilIcon className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      onClick={() => deleteLink(link.id!)}
                                      className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                    >
                                      <TrashIcon className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Donate/Support Links Section - Badge style */}
                      {links.filter(l => l.link_type === 'donate').length > 0 && (
                        <div className="mt-6 pt-6 border-t border-white/10">
                          <h4 className="text-sm font-medium text-yellow-400 mb-3 flex items-center gap-2">
                            <CurrencyDollarIcon className="h-4 w-4" />
                            Support Links
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {links.filter(l => l.link_type === 'donate').map((link) => {
                              // Detect platform from URL
                              const getPlatformName = (url: string) => {
                                const lower = url?.toLowerCase() || '';
                                if (lower.includes('venmo.com')) return 'Venmo';
                                if (lower.includes('paypal')) return 'PayPal';
                                if (lower.includes('cash.app')) return 'Cash App';
                                if (lower.includes('ko-fi.com')) return 'Ko-fi';
                                if (lower.includes('buymeacoffee.com')) return 'Buy Me a Coffee';
                                if (lower.includes('patreon.com')) return 'Patreon';
                                return null;
                              };
                              const platform = getPlatformName(link.url || '');
                              
                              return (
                                <div
                                  key={link.id}
                                  className={`bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2 transition-all hover:border-yellow-500/50 ${!link.is_active ? 'opacity-50' : ''}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <CurrencyDollarIcon className="h-4 w-4 text-yellow-400 flex-shrink-0" />
                                    <span className="text-xs font-medium text-white">{platform || link.title || 'Support'}</span>
                                    {/* CTR Badge */}
                                    {link.id && (
                                      <span
                                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10 whitespace-nowrap"
                                        title={analyticsStartAt 
                                          ? `CTR since ${new Date(analyticsStartAt).toLocaleDateString()}`
                                          : 'CTR'
                                        }
                                      >
                                        {totalViews > 0 
                                          ? `${(((linkClicks[link.id] || 0) / totalViews) * 100).toFixed(1)}%`
                                          : '0%'
                                        } | {linkClicks[link.id] || 0}
                                      </span>
                                    )}
                                    <div className="flex items-center gap-0.5 ml-1">
                                      <button
                                        onClick={() => setEditingLink(link)}
                                        className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                                      >
                                        <PencilIcon className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        onClick={() => deleteLink(link.id!)}
                                        className="p-1 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                      >
                                        <TrashIcon className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Stream Cards - Reorderable */}
                <StreamCardsSection
                  talentProfile={talentProfile}
                  bioSettings={bioSettings}
                  setBioSettings={setBioSettings}
                  setShowStreamChannelModal={setShowStreamChannelModal}
                  setShowPodcastModal={setShowPodcastModal}
                  refreshPreview={refreshPreview}
                  analyticsStartAt={analyticsStartAt}
                  totalViews={totalViews}
                  cardClicks={cardClicks}
                />

                {/* Podcast "Add New" card moved to AddLinkModal */}


                {/* Service Offerings Section */}
                {serviceOfferings.filter(s => s.service_type === 'sponsorship' || talentProfile?.collabs_enabled).length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Services</h3>
                      {talentProfile?.collabs_enabled && (
                        <button
                          onClick={async () => {
                            setTalentProfile(prev => prev ? { ...prev, collabs_enabled: false } : prev);
                            await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { collabs_enabled: false } });
                            toast.success('Collabs disabled');
                            setTimeout(refreshPreview, 500);
                          }}
                          className="text-xs text-gray-500 hover:text-red-400 transition-colors"
                        >
                          Disable Collabs
                        </button>
                      )}
                    </div>
                    {serviceOfferings.filter(s => s.service_type === 'sponsorship' || talentProfile?.collabs_enabled).map((service) => (
                      <div
                        key={service.id}
                        className={`rounded-2xl p-4 ${
                          service.service_type === 'sponsorship'
                            ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30'
                            : 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30'
                        } ${!service.is_active ? 'opacity-50' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              service.service_type === 'sponsorship'
                                ? 'bg-gradient-to-br from-green-500 to-emerald-500'
                                : 'bg-gradient-to-br from-pink-500 to-purple-500'
                            }`}>
                              {service.service_type === 'sponsorship' ? (
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5 text-white">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                              ) : (
                                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-white">
                                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                </svg>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-white">{service.title}</h3>
                              {service.service_type !== 'sponsorship' && service.is_active && (
                                <p className="text-sm text-gray-400">
                                  ${(service.pricing / 100).toFixed(0)}
                                </p>
                              )}
                              {!service.is_active && (
                                <p className="text-[10px] leading-tight mt-1 px-2 py-1 rounded bg-yellow-500/20 text-yellow-200 border border-yellow-500/30">
                                  This card is not delivering results so we've paused it to drive more traffic to your cards that are performing. We will keep optimizing for you!
                                </p>
                              )}
                            </div>
                            {/* CTR Badge */}
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10 whitespace-nowrap"
                              title={analyticsStartAt ? `CTR since ${new Date(analyticsStartAt).toLocaleDateString()}` : 'CTR'}
                            >
                              {totalViews > 0 
                                ? `${(((cardClicks[service.service_type === 'sponsorship' ? 'sponsorship' : 'collab'] || 0) / totalViews) * 100).toFixed(1)}%`
                                : '0%'
                              } | {cardClicks[service.service_type === 'sponsorship' ? 'sponsorship' : 'collab'] || 0}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={service.is_active}
                                onChange={async (e) => {
                                  const updated = serviceOfferings.map(s => 
                                    s.id === service.id ? { ...s, is_active: e.target.checked } : s
                                  );
                                  setServiceOfferings(updated);
                                  await supabase.rpc('set_service_offering_active', {
                                    p_id: service.id,
                                    p_is_active: e.target.checked,
                                  });
                                  toast.success(e.target.checked ? 'Service enabled' : 'Service disabled');
                                  setTimeout(refreshPreview, 500);
                                }}
                                className="sr-only peer"
                              />
                              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
                            </label>
                            <button
                              onClick={() => setEditingService(service)}
                              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                            >
                              <PencilIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to delete this service?')) {
                                  await supabase.rpc('delete_service_offering', { p_id: service.id });
                                  setServiceOfferings(serviceOfferings.filter(s => s.id !== service.id));
                                  toast.success('Service deleted');
                                  setTimeout(refreshPreview, 500);
                                }
                              }}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Events Summary Card */}
                {bioEvents.length > 0 && (
                  <div 
                    onClick={() => setShowAddEventModal(true)}
                    className="bg-gradient-to-r from-orange-500/20 to-amber-500/20 border border-orange-500/30 rounded-xl p-3 cursor-pointer hover:border-orange-500/50 transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-orange-400 flex-shrink-0">
                          <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3A.75.75 0 0118 3v1.5h.75a3 3 0 013 3v11.25a3 3 0 01-3 3H5.25a3 3 0 01-3-3V7.5a3 3 0 013-3H6V3a.75.75 0 01.75-.75zm13.5 9a1.5 1.5 0 00-1.5-1.5H5.25a1.5 1.5 0 00-1.5 1.5v7.5a1.5 1.5 0 001.5 1.5h13.5a1.5 1.5 0 001.5-1.5v-7.5z" clipRule="evenodd" />
                        </svg>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-white text-sm">Events</h3>
                            <span className="text-xs text-orange-400">{bioEvents.length} event{bioEvents.length !== 1 ? 's' : ''}</span>
                          </div>
                          <p className="text-xs text-gray-400">
                            Next: {(() => {
                              const nextEvent = [...bioEvents]
                                .filter(e => e.is_active)
                                .sort((a, b) => {
                                  if (!a.event_date) return 1;
                                  if (!b.event_date) return -1;
                                  return new Date(a.event_date).getTime() - new Date(b.event_date).getTime();
                                })[0];
                              return nextEvent ? `${nextEvent.title}${nextEvent.event_date ? ` • ${new Date(nextEvent.event_date).toLocaleDateString()}` : ''}` : 'No upcoming events';
                            })()}
                          </p>
                        </div>
                      </div>
                      <PencilIcon className="h-4 w-4 text-gray-500" />
                    </div>
                  </div>
                )}

                {/* ShoutOut Card (condensed) */}
                <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-xl p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <GiftIcon className="h-5 w-5 text-blue-400" />
                    <span className="text-white text-sm font-medium">ShoutOut Card</span>
                      </div>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10">
                    {totalViews > 0 ? `${(((cardClicks['shoutout'] || 0) / totalViews) * 100).toFixed(1)}%` : '0%'} CTR
                  </span>
                    </div>

                {/* Sponsor Ad Card - show when there are sponsor_ad clicks */}
                {(cardClicks['sponsor_ad'] || 0) > 0 && (
                  <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-3 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="h-5 w-5 text-green-400">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                      <span className="text-white text-sm font-medium">Sponsor Ad</span>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10">
                      {totalViews > 0 ? `${(((cardClicks['sponsor_ad'] || 0) / totalViews) * 100).toFixed(1)}%` : '0%'} CTR | {cardClicks['sponsor_ad'] || 0} clicks
                    </span>
                  </div>
                )}

                {/* Request Free Design Pill */}
                <div className="pt-2 pb-20">
                  <button
                    onClick={() => setShowDesignModal(true)}
                    disabled={designAlreadyRequested}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all ${
                      designAlreadyRequested
                        ? 'bg-gray-700/60 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:scale-105 hover:shadow-lg'
                    }`}
                    style={designAlreadyRequested ? {} : { boxShadow: '0 4px 16px rgba(139, 92, 246, 0.3)' }}
                  >
                    <SparklesIcon className="h-4 w-4" />
                    {designAlreadyRequested ? 'Design Requested' : 'Request Free Design'}
                  </button>
                </div>
              </>
            )}

            {/* Social tab removed - now accessible via card in Links tab */}
            {false && (
              <div className="space-y-6">
                <button
                  onClick={() => setShowAddSocialModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                >
                  <PlusIcon className="h-5 w-5" />
                  Add Social Link
                </button>

                {/* Social Links List */}
                <div className="space-y-3">
                  {socialLinks.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
                      <SparklesIcon className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-400 mb-2">No social links yet</h3>
                      <p className="text-gray-500 mb-4">Add your social media profiles to display at the bottom of your bio page</p>
                    </div>
                  ) : (
                    socialLinks.map((social) => {
                      const platform = SOCIAL_PLATFORMS.find(p => p.id === social.platform);
                      return (
                        <div
                          key={social.id}
                          className="bg-white/5 border border-white/10 rounded-xl p-4 transition-all hover:border-white/20"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-lg flex items-center justify-center bg-white/10 text-blue-400">
                              {platform?.icon || <LinkIcon className="w-6 h-6" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-white">
                                {platform?.name || social.platform}
                              </h3>
                              <p className="text-sm text-gray-400">@{social.handle}</p>
                              {/* Show follower count if set */}
                              {social.follower_count && social.follower_count > 0 && (
                                <p className="text-xs text-pink-400 mt-1">
                                  {social.follower_count >= 1000000 
                                    ? `${(social.follower_count / 1000000).toFixed(1)}M followers`
                                    : social.follower_count >= 1000 
                                      ? `${(social.follower_count / 1000).toFixed(1)}K followers`
                                      : `${social.follower_count} followers`
                                  }
                                </p>
                              )}
                            </div>
                            <button
                              onClick={async () => {
                                const updated = socialLinks.filter(s => s.id !== social.id);
                                setSocialLinks(updated);
                                
                                // Build the update object - clear the corresponding handle field
                                const updateData: Record<string, unknown> = {};
                                
                                // Map platform to its handle field in talent_profiles
                                switch (social.platform) {
                                  case 'twitter':
                                    updateData.twitter_handle = null;
                                    setTalentProfile(prev => prev ? { ...prev, twitter_handle: undefined } : prev);
                                    break;
                                  case 'instagram':
                                    updateData.instagram_handle = null;
                                    setTalentProfile(prev => prev ? { ...prev, instagram_handle: undefined } : prev);
                                    break;
                                  case 'facebook':
                                    updateData.facebook_handle = null;
                                    setTalentProfile(prev => prev ? { ...prev, facebook_handle: undefined } : prev);
                                    break;
                                  case 'tiktok':
                                    updateData.tiktok_handle = null;
                                    setTalentProfile(prev => prev ? { ...prev, tiktok_handle: undefined } : prev);
                                    break;
                                  case 'youtube':
                                    updateData.youtube_handle = null;
                                    setTalentProfile(prev => prev ? { ...prev, youtube_handle: undefined } : prev);
                                    break;
                                  case 'rumble':
                                    updateData.rumble_handle = null;
                                    updateData.rumble_type = null;
                                    setTalentProfile(prev => prev ? { ...prev, rumble_handle: undefined, rumble_type: undefined } : prev);
                                    break;
                                }
                                
                                // Also delete from social_accounts table if it has a real ID
                                if (!social.id.startsWith('profile-')) {
                                  await supabase
                                    .from('social_accounts')
                                    .delete()
                                    .eq('id', social.id);
                                }
                                
                                // Update talent_profiles to clear the handle
                                await supabase.rpc('update_talent_profile', {
                                  p_id: talentProfile?.id,
                                  p_updates: updateData,
                                });
                                  
                                toast.success('Social link removed');
                                setTimeout(refreshPreview, 500);
                              }}
                              className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            >
                              <TrashIcon className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Info about social icons */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <SparklesIcon className="h-6 w-6 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium text-white mb-1">Social Icons</h3>
                      <p className="text-sm text-gray-300">
                        Social links appear as icons at the bottom of your bio page, just above the footer.
                        They're automatically imported when you use the Import Links feature.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'style' && (
              <div className="space-y-6">
                {/* Background Gradient */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <SwatchIcon className="h-5 w-5 text-purple-400" />
                    Background
                  </h3>
                  <div className="grid grid-cols-4 gap-3">
                    {GRADIENT_PRESETS.map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => saveSettings({
                          gradient_start: preset.start,
                          gradient_end: preset.end,
                          gradient_direction: preset.direction,
                        })}
                        className={`aspect-square rounded-xl border-2 transition-all ${
                          bioSettings?.gradient_start === preset.start && bioSettings?.gradient_end === preset.end
                            ? 'border-white scale-105'
                            : 'border-transparent hover:border-white/50'
                        }`}
                        style={{
                          background: `linear-gradient(${preset.direction === 'to-b' ? '180deg' : '135deg'}, ${preset.start}, ${preset.end})`
                        }}
                        title={preset.name}
                      />
                    ))}
                  </div>
                  {/* Custom Background Color */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-sm text-gray-400 mb-2">Custom color</p>
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <input
                          type="color"
                          value={bioSettings?.gradient_start || '#0a0a0a'}
                          onChange={(e) => {
                            const hex = e.target.value;
                            const r = parseInt(hex.slice(1, 3), 16);
                            const g = parseInt(hex.slice(3, 5), 16);
                            const b = parseInt(hex.slice(5, 7), 16);
                            const darkEnd = `#${Math.max(0, Math.floor(r * 0.2)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(g * 0.2)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(b * 0.2)).toString(16).padStart(2, '0')}`;
                            saveSettings({ gradient_start: hex, gradient_end: darkEnd, gradient_direction: 'to-b' });
                          }}
                          className="w-10 h-10 rounded-lg cursor-pointer border border-white/20"
                        />
                      </div>
                      <input
                        type="text"
                        value={bioSettings?.gradient_start || '#0a0a0a'}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (!val.startsWith('#')) val = '#' + val;
                          if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                            const r = parseInt(val.slice(1, 3), 16);
                            const g = parseInt(val.slice(3, 5), 16);
                            const b = parseInt(val.slice(5, 7), 16);
                            const darkEnd = `#${Math.max(0, Math.floor(r * 0.2)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(g * 0.2)).toString(16).padStart(2, '0')}${Math.max(0, Math.floor(b * 0.2)).toString(16).padStart(2, '0')}`;
                            saveSettings({ gradient_start: val, gradient_end: darkEnd, gradient_direction: 'to-b' });
                          }
                        }}
                        placeholder="#0a0a0a"
                        maxLength={7}
                        className="w-28 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-purple-500"
                      />
                      <div
                        className="flex-1 h-10 rounded-lg border border-white/10"
                        style={{
                          background: `linear-gradient(180deg, ${bioSettings?.gradient_start || '#0a0a0a'}, ${bioSettings?.gradient_end || '#1a1a2e'})`
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Button Color */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <PaintBrushIcon className="h-5 w-5 text-blue-400" />
                    Button Color
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {BUTTON_COLORS.map((color) => (
                      <button
                        key={color.name}
                        onClick={() => saveSettings({ button_color: color.value })}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${
                          bioSettings?.button_color === color.value
                            ? 'border-white scale-110'
                            : 'border-transparent hover:scale-105'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                  {/* Custom Button Color */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-sm text-gray-400 mb-2">Custom color</p>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={bioSettings?.button_color || '#3b82f6'}
                        onChange={(e) => saveSettings({ button_color: e.target.value })}
                        className="w-10 h-10 rounded-lg cursor-pointer border border-white/20"
                      />
                      <input
                        type="text"
                        value={bioSettings?.button_color || '#3b82f6'}
                        onChange={(e) => {
                          let val = e.target.value;
                          if (!val.startsWith('#')) val = '#' + val;
                          if (/^#[0-9A-Fa-f]{6}$/.test(val)) {
                            saveSettings({ button_color: val });
                          }
                        }}
                        placeholder="#3b82f6"
                        maxLength={7}
                        className="w-28 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-blue-500"
                      />
                      <div
                        className="h-10 px-6 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                        style={{ backgroundColor: bioSettings?.button_color || '#3b82f6' }}
                      >
                        Preview
                      </div>
                    </div>
                  </div>
                </div>

                {/* Button Style */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Button Style</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'rounded', label: 'Rounded', className: 'rounded-xl' },
                      { id: 'pill', label: 'Pill', className: 'rounded-full' },
                      { id: 'square', label: 'Square', className: 'rounded-md' },
                    ].map((style) => (
                      <button
                        key={style.id}
                        onClick={() => saveSettings({ button_style: style.id })}
                        className={`p-4 border-2 transition-all ${style.className} ${
                          bioSettings?.button_style === style.id
                            ? 'border-blue-500 bg-blue-500/20'
                            : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        <div 
                          className={`h-8 ${style.className}`}
                          style={{ backgroundColor: bioSettings?.button_color || '#3b82f6' }}
                        />
                        <p className="text-xs text-gray-400 mt-2">{style.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Card Style */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Card Style</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { id: 'glass', label: 'Glass', desc: 'Transparent with blur' },
                      { id: 'solid', label: 'Solid', desc: 'Opaque background' },
                      { id: 'outline', label: 'Outline', desc: 'Border only' },
                    ].map((style) => (
                      <button
                        key={style.id}
                        onClick={() => saveSettings({ card_style: style.id })}
                        className={`p-4 border-2 rounded-xl text-left transition-all ${
                          bioSettings?.card_style === style.id
                            ? 'border-blue-500 bg-blue-500/20'
                            : 'border-white/20 hover:border-white/40'
                        }`}
                      >
                        <p className="font-medium text-white">{style.label}</p>
                        <p className="text-xs text-gray-400">{style.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Social Links Card */}
                <div
                  onClick={() => setShowSocialManageModal(true)}
                  className="bg-white/5 border border-white/10 rounded-2xl p-6 flex items-center justify-between cursor-pointer hover:bg-white/8 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <SparklesIcon className="h-5 w-5 text-purple-400" />
                    <div>
                      <span className="text-white text-sm font-medium">Social Links</span>
                      <p className="text-gray-400 text-xs">{socialLinks.length} links configured</p>
                    </div>
                  </div>
                  <span className="text-gray-400 text-xs font-medium">Edit &rarr;</span>
                </div>
              </div>
            )}

            {/* ShoutOuts Tab */}
            {activeTab === 'shoutouts' && (
              <div className="space-y-6">
                {/* ShoutOut Types */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="text-sm font-semibold text-white">ShoutOut Types</h3>
                    <span className="text-[11px] text-gray-500">
                      {(() => {
                        const selected = talentProfile?.selected_shoutout_types || [];
                        const count = selected.length > 0 ? selected.length : SHOUTOUT_TYPE_OPTIONS.length;
                        return `${count} of ${SHOUTOUT_TYPE_OPTIONS.length} enabled`;
                      })()}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs mb-3">
                    Choose which video occasions appear on your /shoutout page. Unselected types are hidden from fans.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {SHOUTOUT_TYPE_OPTIONS.map(({ key, label, emoji }) => {
                      const selected = talentProfile?.selected_shoutout_types || [];
                      // Null/empty => all enabled (matches /shoutout default behavior)
                      const isActive = selected.length === 0 || selected.includes(key);
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={async () => {
                            if (!talentProfile?.id) return;
                            const current = talentProfile.selected_shoutout_types && talentProfile.selected_shoutout_types.length > 0
                              ? [...talentProfile.selected_shoutout_types]
                              : SHOUTOUT_TYPE_OPTIONS.map(o => o.key);
                            let next: string[];
                            if (current.includes(key)) {
                              next = current.filter(k => k !== key);
                              if (next.length === 0) {
                                toast.error('Keep at least one ShoutOut type enabled');
                                return;
                              }
                            } else {
                              next = [...current, key];
                            }
                            setTalentProfile(prev => prev ? { ...prev, selected_shoutout_types: next } : prev);
                            const { error } = await supabase.rpc('update_talent_profile', {
                              p_id: talentProfile.id,
                              p_updates: { selected_shoutout_types: next },
                            });
                            if (error) {
                              toast.error('Could not save');
                              setTalentProfile(prev => prev ? { ...prev, selected_shoutout_types: current } : prev);
                            } else {
                              setTimeout(refreshPreview, 300);
                            }
                          }}
                          className={`px-3 py-2 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                            isActive
                              ? 'bg-purple-500/20 border-purple-400/60 text-white'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          <span>{emoji}</span>
                          <span>{label}</span>
                          {isActive && <CheckIcon className="h-3.5 w-3.5 text-purple-300" />}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Delivery Time */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-3">Delivery Time</h3>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={talentProfile?.fulfillment_time_hours || 168}
                      onChange={async (e) => {
                        const hrs = parseInt(e.target.value) || 168;
                        setTalentProfile(prev => prev ? { ...prev, fulfillment_time_hours: hrs } : prev);
                        await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { fulfillment_time_hours: hrs } });
                      }}
                      className="w-20 bg-white/5 border border-white/20 px-3 py-2 rounded-lg text-white text-sm focus:outline-none"
                    />
                    <span className="text-gray-400 text-sm">hours (~{Math.ceil((talentProfile?.fulfillment_time_hours || 168) / 24)} days)</span>
                      </div>
                    </div>

                {/* Bio Text */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-3">Bio for ShoutOut Profile</h3>
                  <textarea
                    value={talentProfile?.bio || ''}
                    onChange={(e) => setTalentProfile(prev => prev ? { ...prev, bio: e.target.value } : prev)}
                    onBlur={async () => { if (talentProfile?.id) await supabase.rpc('update_talent_profile', { p_id: talentProfile.id, p_updates: { bio: talentProfile.bio } }); }}
                    placeholder="Tell fans about yourself..."
                    rows={3}
                    className="w-full bg-white/5 border border-white/20 px-3 py-2 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none resize-none"
                  />
                </div>

                {/* ShoutOut Price */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-3">ShoutOut Price</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">$</span>
                    <input
                      type="number"
                      value={(talentProfile as any)?.base_pricing || (talentProfile as any)?.current_pricing || ''}
                      onChange={(e) => {
                        const nextPrice = parseFloat(e.target.value) || 0;
                        setTalentProfile(prev => prev ? {
                          ...prev,
                          base_pricing: nextPrice,
                          current_pricing: nextPrice,
                          pricing: nextPrice,
                        } as any : prev);
                      }}
                      onBlur={async (e) => {
                        const price = parseFloat(e.target.value) || 0;
                        // Keep all pricing fields in sync. Public ShoutOut pages
                        // read `talent_pricing_urgency.current_pricing` (mapped
                        // from `talent_profiles.pricing`), while design/editor
                        // controls are bound to base/current pricing fields.
                        await supabase.rpc('update_talent_profile', {
                          p_id: talentProfile?.id,
                          p_updates: { base_pricing: price, current_pricing: price, pricing: price },
                        });
                      }}
                      placeholder="87"
                      className="w-28 bg-white/5 border border-white/20 px-3 py-2 rounded-lg text-white text-sm focus:outline-none focus:border-white/40"
                    />
                  </div>
                  <p className="text-gray-500 text-xs mt-2">The price fans pay for a personalized video ShoutOut</p>
                </div>

                {/* Audio ShoutOuts */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <MicrophoneIcon className="h-5 w-5 text-purple-400" />
                      <h3 className="text-sm font-semibold text-white">Audio ShoutOuts</h3>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const enabled = !(talentProfile as any)?.audio_shoutout_enabled;
                        setTalentProfile(prev => prev ? { ...prev, audio_shoutout_enabled: enabled } as any : prev);
                        await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { audio_shoutout_enabled: enabled } });
                      }}
                      className={`w-12 h-6 rounded-full transition-colors ${(talentProfile as any)?.audio_shoutout_enabled ? 'bg-purple-500' : 'bg-gray-600'}`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${(talentProfile as any)?.audio_shoutout_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {(talentProfile as any)?.audio_shoutout_enabled && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Audio Price</label>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">$</span>
                      <input
                          type="number"
                          value={(talentProfile as any)?.audio_shoutout_price || ''}
                          onChange={(e) => setTalentProfile(prev => prev ? { ...prev, audio_shoutout_price: parseFloat(e.target.value) || 0 } as any : prev)}
                          onBlur={async () => { await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { audio_shoutout_price: (talentProfile as any)?.audio_shoutout_price } }); }}
                          placeholder={String(Math.round(((talentProfile as any)?.base_pricing || 30) / 3))}
                          className="w-24 bg-white/5 border border-white/20 px-3 py-2 rounded-lg text-white text-sm focus:outline-none"
                        />
                    </div>
                      <p className="text-gray-500 text-xs mt-1">Recommended: ~1/3 of your video price</p>
                  </div>
                  )}
                </div>

                {/* Corporate Pricing */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold text-white">Corporate / Event Pricing</h3>
                    <button
                      type="button"
                      onClick={async () => {
                        const enabled = !(talentProfile as any)?.allow_corporate_pricing;
                        setTalentProfile(prev => prev ? { ...prev, allow_corporate_pricing: enabled } as any : prev);
                        await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { allow_corporate_pricing: enabled } });
                      }}
                      className={`w-12 h-6 rounded-full transition-colors ${(talentProfile as any)?.allow_corporate_pricing ? 'bg-blue-500' : 'bg-gray-600'}`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${(talentProfile as any)?.allow_corporate_pricing ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  {(talentProfile as any)?.allow_corporate_pricing && (
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">$</span>
                      <input
                        type="number"
                        value={(talentProfile as any)?.corporate_pricing || ''}
                        onChange={(e) => setTalentProfile(prev => prev ? { ...prev, corporate_pricing: parseFloat(e.target.value) || 0 } as any : prev)}
                        onBlur={async () => { await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { corporate_pricing: (talentProfile as any)?.corporate_pricing } }); }}
                        placeholder="500"
                        className="w-28 bg-white/5 border border-white/20 px-3 py-2 rounded-lg text-white text-sm focus:outline-none"
                      />
                      <span className="text-gray-400 text-xs">per corporate event</span>
                    </div>
                  )}
                </div>

                {/* 24hr Express Delivery */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-400 text-lg">⚡</span>
                      <div>
                        <h3 className="text-sm font-semibold text-white">24hr Express Delivery</h3>
                        <p className="text-gray-400 text-xs mt-0.5">
                          {talentProfile?.express_delivery_enabled
                            ? `Customers can pay $${Math.round((talentProfile?.base_pricing || talentProfile?.current_pricing || 87) * 1.2)} for 24hr turnaround — you earn 20% more per video`
                            : 'Earn 20% more per video by offering a rush 24hr delivery option'}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        const enabled = !talentProfile?.express_delivery_enabled;
                        const expressPrice = Math.round((talentProfile?.base_pricing || talentProfile?.current_pricing || 87) * 1.2 * 100) / 100;
                        setTalentProfile(prev => prev ? { ...prev, express_delivery_enabled: enabled, express_delivery_price: expressPrice } : prev);
                        await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { express_delivery_enabled: enabled, express_delivery_price: expressPrice } });
                        if (enabled) toast.success(`24hr Express Delivery enabled at $${expressPrice}!`);
                      }}
                      className={`w-12 h-6 rounded-full transition-colors flex-shrink-0 ${talentProfile?.express_delivery_enabled ? 'bg-emerald-500' : 'bg-gray-600'}`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${talentProfile?.express_delivery_enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                        </div>
                  </div>

                {/* Promo Video */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold text-white mb-3">Promo Video</h3>
                  {talentProfile?.promo_video_url ? (
                    <div>
                      <video src={talentProfile.promo_video_url} controls className="w-full rounded-lg max-h-[200px] object-contain bg-black mb-3" />
                      <div className="flex gap-2">
                        <label className="flex-1 py-2 text-center text-sm font-medium text-white bg-blue-600 rounded-lg cursor-pointer hover:bg-blue-700">
                          Replace
                          <input type="file" accept="video/*" className="hidden" onChange={async (e) => {
                            const file = e.target.files?.[0]; if (!file) return;
                            try {
                              const videoUrl = await uploadVideoToWasabi(file, { talentId: talentProfile?.id, uploadType: 'promo' });
                              await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { promo_video_url: videoUrl } });
                              setTalentProfile(prev => prev ? { ...prev, promo_video_url: videoUrl } : prev);
                              toast.success('Promo video updated!');
                              setTimeout(refreshPreview, 500);
                            } catch (err: any) { toast.error(err.message || 'Upload failed'); }
                          }} />
                        </label>
                        <button
                          onClick={async () => {
                            await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { promo_video_url: null } });
                            setTalentProfile(prev => prev ? { ...prev, promo_video_url: undefined } : prev);
                            toast.success('Promo video removed');
                          }}
                          className="flex-1 py-2 text-center text-sm font-medium text-red-400 bg-white/5 rounded-lg hover:bg-white/10"
                        >Remove</button>
                      </div>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center gap-2 py-6 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-white/30 transition-colors">
                      <CloudArrowUpIcon className="h-8 w-8 text-gray-400" />
                      <span className="text-gray-400 text-sm">Upload promo video</span>
                      <input type="file" accept="video/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        try {
                          const videoUrl = await uploadVideoToWasabi(file, { talentId: talentProfile?.id, uploadType: 'promo' });
                          await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { promo_video_url: videoUrl } });
                          setTalentProfile(prev => prev ? { ...prev, promo_video_url: videoUrl } : prev);
                          toast.success('Promo video uploaded!');
                          setTimeout(refreshPreview, 500);
                        } catch (err: any) { toast.error(err.message || 'Upload failed'); }
                      }} />
                    </label>
                  )}
                </div>
              </div>
            )}

          </div>

          {/* Right Column - Live Preview */}
          <div className={`lg:sticky lg:top-24 lg:self-start ${mobileView === 'manage' ? 'hidden lg:block' : ''}`}>
            <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
              {/* Browser Chrome */}
              <div className="bg-gray-900/80 px-3 py-2 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                  </div>
                  <div className="flex-1 flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1.5 ml-2">
                    <button
                      onClick={copyBioUrl}
                      className="text-gray-400 hover:text-white transition-colors text-xs truncate flex-1 text-left cursor-pointer"
                      title="Click to copy URL"
                    >
                      {bioUrl}
                    </button>
                    <button
                      onClick={copyBioUrl}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Copy URL"
                    >
                      <ClipboardIcon className="h-4 w-4" />
                    </button>
                    <a
                      href={`https://${bioUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Open in new tab"
                    >
                      <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Iframe Embed - Always show preview */}
              <div className="relative w-full" style={{ height: '680px' }}>
                {/* Unpublished banner */}
                {!bioSettings?.is_published && (
                  <div className="absolute top-0 left-0 right-0 z-10 bg-yellow-500/90 text-black text-xs font-medium text-center py-1.5 px-2 flex items-center justify-center gap-2">
                    <span>⚠️ Not published yet</span>
                    <button
                      onClick={togglePublish}
                      disabled={saving}
                      className="px-2 py-0.5 bg-black/20 hover:bg-black/30 rounded text-[10px] font-semibold transition-colors"
                    >
                      Publish Now
                    </button>
                  </div>
                )}
                <iframe
                  id="bio-preview-iframe"
                  key={previewKey}
                  src={`https://www.${bioUrl}?preview=true&t=${previewKey}`}
                  className="w-full h-full border-0"
                  title="Bio Preview"
                />
              </div>
            </div>
          </div>
        </div>

          </>
        ) : mainView === 'orders' ? (
          /* Orders View */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Orders</h2>
              <button onClick={loadTalentOrders} className="text-sm text-blue-400 hover:text-blue-300">Refresh</button>
            </div>
            {ordersLoading ? (
              <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
            ) : talentOrders.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No orders yet</div>
            ) : (
              talentOrders.map((order: any) => {
                const isCollab = order.service_type === 'social_collab';
                const isDonation = order.service_type === 'donation';
                const orderAmtCents = order.original_amount && order.original_amount > 0 ? order.original_amount : order.amount;
                const orderAmt = (orderAmtCents || 0) / 100;
                const adminPct = isDonation ? 0 : (isCollab ? 10 : 25);
                const processingFee = orderAmt * 0.029 + 0.30;
                const adminFeeAmt = orderAmt * (adminPct / 100);
                const netPayout = Math.max(0, orderAmt - adminFeeAmt - processingFee);

                return (
                <div key={order.id} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-white text-sm font-medium">{order.customer_name || order.customer_email?.split('@')[0] || 'Customer'}</p>
                      <p className="text-gray-400 text-xs">{order.customer_email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {isCollab && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">Collab</span>}
                        {isDonation && <span className="text-xs px-1.5 py-0.5 rounded bg-green-500/20 text-green-300">Donation</span>}
                        {order.is_audio_shoutout && <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">Audio</span>}
                        {order.shoutout_tier && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300">{order.shoutout_tier}</span>}
                        {order.is_express_delivery && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 font-semibold">⚡ 24hr Delivery</span>}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-green-400 text-sm font-bold">${orderAmt.toFixed(2)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${order.status === 'completed' ? 'text-green-400 bg-green-400/10' : order.status === 'pending' ? 'text-yellow-400 bg-yellow-400/10' : 'text-blue-400 bg-blue-400/10'}`}>
                        {order.status}
                      </span>
                    </div>
                  </div>

                  {/* Request details with who/occasion */}
                  {!order.details_submitted && order.status === 'pending' ? (
                    <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-3 mb-3 text-center">
                      <p className="text-amber-300 font-medium text-sm">⏳ Pending details from customer</p>
                      <p className="text-amber-200/70 text-xs mt-1">The customer will fill in their request details soon</p>
                    </div>
                  ) : (order.recipient_name || order.occasion || order.request_details) ? (
                    <div className="bg-white/5 rounded-lg p-3 mb-3 space-y-1.5 text-xs">
                      {order.recipient_name && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Who it's for</span>
                          <span className="text-white">{order.recipient_name}</span>
                        </div>
                      )}
                      {order.occasion && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Occasion</span>
                          <span className="text-white">{order.occasion}</span>
                        </div>
                      )}
                      {order.request_details && (
                        <div className={order.recipient_name || order.occasion ? 'border-t border-white/10 pt-1.5 mt-1.5' : ''}>
                          <p className="text-gray-400 mb-1 font-medium">Request Details:</p>
                          <p className="text-gray-300 whitespace-pre-wrap">{order.request_details}</p>
                        </div>
                      )}
                    </div>
                  ) : null}

                  {/* Pricing breakdown - collapsible */}
                  <details className="bg-white/5 rounded-lg mb-3 text-xs group">
                    <summary className="flex items-center justify-between p-3 cursor-pointer list-none">
                      <span className="text-gray-400 font-medium">Pricing</span>
                      <div className="flex items-center gap-2">
                        <span className="text-green-400 font-medium">Net: ${netPayout.toFixed(2)}</span>
                        <svg className="h-3.5 w-3.5 text-gray-500 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </summary>
                    <div className="px-3 pb-3 space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Order Price</span>
                        <span className="text-white">${orderAmt.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Platform Fee ({adminPct}%)</span>
                        <span className="text-red-400">-${adminFeeAmt.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Processing (2.9% + $0.30)</span>
                        <span className="text-red-400">-${processingFee.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-medium border-t border-white/10 pt-1.5 mt-1.5">
                        <span className="text-gray-300">You Earn</span>
                        <span className="text-green-400">${netPayout.toFixed(2)}</span>
                      </div>
                    </div>
                  </details>

                  {/* Collab-specific details */}
                  {isCollab && (
                    <div className="bg-white/5 rounded-lg p-3 mb-3 space-y-1.5 text-xs">
                      {order.company_name && <div className="flex justify-between"><span className="text-gray-400">Company</span><span className="text-white">{order.company_name}</span></div>}
                      {order.suggested_script && <div><span className="text-gray-400">Script:</span><p className="text-white mt-0.5">{order.suggested_script}</p></div>}
                      {order.target_audience && <div className="flex justify-between"><span className="text-gray-400">Audience</span><span className="text-white">{order.target_audience}</span></div>}
                      {order.customer_socials && <div><span className="text-gray-400">Socials:</span>{typeof order.customer_socials === 'object' ? (
                        <div className="mt-1 space-y-0.5">{Object.entries(order.customer_socials).filter(([, v]) => v && v !== 'N/A').map(([k, v]) => (
                          <div key={k} className="flex justify-between"><span className="text-gray-500 capitalize">{k}</span><span className="text-white">{String(v)}</span></div>
                        ))}</div>
                      ) : <span className="text-white ml-1">{String(order.customer_socials)}</span>}</div>}
                    </div>
                  )}

                  {order.video_url ? (
                    <div className="flex items-center gap-2">
                      <span className="text-green-400 text-xs font-medium">Delivered</span>
                      <a href={order.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:text-blue-300">View</a>
                    </div>
                  ) : (order.status === 'pending' || order.status === 'in_progress') ? (
                    <div>
                      <label className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-700 transition-colors w-fit">
                        {uploadingOrderId === order.id ? (
                          <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Uploading...</>
                        ) : (
                          <><CloudArrowUpIcon className="h-4 w-4" /> Upload {order.is_audio_shoutout ? 'Audio' : 'Video'}</>
                        )}
                        <input
                          type="file"
                          accept={order.is_audio_shoutout ? 'audio/*' : 'video/*'}
                          className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleOrderVideoUpload(order.id, f); }}
                          disabled={uploadingOrderId === order.id}
                        />
                      </label>
                    </div>
                  ) : null}

                  {/* Creator ↔ customer chat. Hidden on credit_purchase /
                      donation orders (nothing to discuss) and on
                      cancelled orders. Posting a message dispatches an
                      email + SMS to the customer via the
                      notify-order-message edge function so they can
                      reply or fill in missing request details. */}
                  {talentProfile?.user_id &&
                    !NO_CHAT_TYPES.has(order.service_type || '') &&
                    order.status !== 'cancelled' && (
                      <OrderChat
                        orderId={order.id}
                        currentUserId={talentProfile.user_id}
                        customerName={
                          order.customer_name ||
                          order.customer_email?.split('@')[0] ||
                          'Customer'
                        }
                      />
                    )}
                </div>
                );
              })
            )}
          </div>
        ) : mainView === 'payouts' ? (
          /* Payouts View - Full Dashboard */
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Payouts</h2>
              <div className="flex items-center gap-3">
                <button onClick={loadTalentPayouts} className="text-sm text-blue-400 hover:text-blue-300">Refresh</button>
                <button onClick={() => { setMainView('dashboard'); setShowPayoutOnboarding(false); }} className="text-sm text-gray-400 hover:text-white">&larr; Back</button>
              </div>
            </div>

            {(!(talentProfile as any)?.payout_onboarding_completed || !(talentProfile as any)?.moov_account_id || !(talentProfile as any)?.bank_account_linked) && !showPayoutOnboarding ? (
              <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl p-6 text-center">
                <CurrencyDollarIcon className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white text-lg font-bold mb-2">Set Up Your Payouts</h3>
                <p className="text-gray-300 text-sm mb-4">Complete your payout setup to start receiving earnings from ShoutOuts, collabs, and donations.</p>
                <button onClick={() => setShowPayoutOnboarding(true)} className="inline-block px-6 py-3 bg-yellow-500 text-black font-semibold rounded-xl hover:bg-yellow-400 transition-colors">
                  Set Up Payouts
                </button>
              </div>
            ) : (!(talentProfile as any)?.payout_onboarding_completed || !(talentProfile as any)?.moov_account_id || !(talentProfile as any)?.bank_account_linked) && showPayoutOnboarding && talentProfile?.id ? (
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6">
                <PayoutOnboardingInline
                  talentId={talentProfile.id}
                  userId={talentProfile.user_id}
                  onComplete={() => {
                    setShowPayoutOnboarding(false);
                    if (talentProfile) {
                      (talentProfile as any).payout_onboarding_completed = true;
                    }
                    loadTalentPayouts();
                    toast.success('Payout setup complete!');
                  }}
                  onCancel={() => setShowPayoutOnboarding(false)}
                />
              </div>
            ) : payoutsLoading ? (
              <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>
            ) : !payoutsData ? (
              <div className="text-center py-8">
                <button onClick={loadTalentPayouts} className="px-6 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">Load Payout History</button>
              </div>
            ) : (
              <>
                {/* Earnings Summary */}
                <div className="bg-gradient-to-r from-blue-600/30 to-blue-700/30 border border-blue-500/30 rounded-2xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-200 text-sm font-medium">Total Earned</p>
                      <p className="text-3xl font-bold text-white mt-1">${payoutsData.totalEarnings.toFixed(2)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-200 text-sm">{payoutsData.payouts.length} total payout{payoutsData.payouts.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>

                {/* Payouts Active Badge */}
                <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <CurrencyDollarIcon className="h-4 w-4 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-green-400 text-sm font-medium">Payouts Active</p>
                    <p className="text-gray-500 text-xs">Orders paid weekly on Mondays · Sponsor ad revenue paid on the 1st of each month</p>
                  </div>
                </div>

                {/* Accruing Ad Revenue for current month */}
                {payoutsData.accruingAdRevenueCents > 0 && (() => {
                  const now = new Date();
                  const nextPayoutDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                  const nextPayoutLabel = nextPayoutDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                  return (
                    <div className="p-4 bg-indigo-500/10 border border-indigo-500/20 rounded-xl space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center flex-shrink-0">
                            <CurrencyDollarIcon className="h-4 w-4 text-indigo-400" />
                          </div>
                          <div>
                            <p className="text-indigo-300 text-sm font-medium">Ad Revenue Accruing This Month</p>
                            <p className="text-gray-500 text-xs">Pays out <span className="text-indigo-400 font-medium">{nextPayoutLabel}</span></p>
                          </div>
                        </div>
                        <p className="text-indigo-300 text-lg font-bold">${(payoutsData.accruingAdRevenueCents / 100).toFixed(2)}</p>
                      </div>
                      <p className="text-gray-600 text-xs pl-11">Sponsor clicks accumulate all month. On the 1st, your total is paid out automatically.</p>
                    </div>
                  );
                })()}

                {/* Payouts List */}
                <div className="space-y-2">
                  <h3 className="text-white text-sm font-semibold">Payouts</h3>
                  {payoutsData.batches.length === 0 && payoutsData.adRevenueBatches.length === 0 ? (
                    <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-center">
                      <CurrencyDollarIcon className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400 text-sm">No payouts yet</p>
                      <p className="text-gray-500 text-xs mt-1">Complete orders to start earning!</p>
                    </div>
                  ) : [...payoutsData.adRevenueBatches, ...payoutsData.batches]
                      .sort((a, b) => new Date(b.week_start_date).getTime() - new Date(a.week_start_date).getTime())
                      .map((batch: any) => {
                    const isAdRevenue = batch.kind === 'ad_revenue';
                    const weekPayouts = isAdRevenue
                      ? payoutsData.payouts.filter((p: any) => p.payout_type === 'ad_revenue' && p.week_start_date === batch.week_start_date)
                      : payoutsData.payouts.filter((p: any) => p.week_start_date === batch.week_start_date && p.payout_type !== 'ad_revenue');
                    const expansionKey = `${batch.id}`;
                    const isExpanded = expandedWeek === expansionKey;

                    let batchLabel: string;
                    if (isAdRevenue) {
                      const d = new Date(batch.week_start_date + 'T00:00:00');
                      batchLabel = `Sponsor ad revenue · ${d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
                    } else {
                      const ws = new Date(batch.week_start_date + 'T00:00:00');
                      const we = new Date(batch.week_end_date + 'T00:00:00');
                      batchLabel = `${ws.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${we.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
                    }

                    const statusColors: Record<string, string> = { pending: 'text-yellow-400 bg-yellow-400/10', processing: 'text-blue-400 bg-blue-400/10', paid: 'text-green-400 bg-green-400/10', complete: 'text-green-400 bg-green-400/10', pending_review: 'text-orange-400 bg-orange-400/10' };

                    return (
                      <div key={batch.id} className={`border rounded-xl overflow-hidden ${isAdRevenue ? 'bg-indigo-500/5 border-indigo-500/20' : 'bg-white/5 border-white/10'}`}>
                        <button onClick={() => setExpandedWeek(isExpanded ? null : expansionKey)} className="w-full text-left p-4 hover:bg-white/5 transition-colors">
                          <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                              {isAdRevenue
                                ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-indigo-500/20 text-indigo-300 uppercase tracking-wide">Ad</span>
                                : <svg className="w-4 h-4 text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              }
                              <div>
                                <p className="text-white text-sm font-medium">{batchLabel}</p>
                                <p className="text-gray-500 text-xs mt-0.5">
                                  {isAdRevenue
                                    ? `${batch.total_orders} sponsor click${batch.total_orders !== 1 ? 's' : ''}`
                                    : `${batch.total_orders} order${batch.total_orders !== 1 ? 's' : ''}`}
                                  {batch.total_refunded_amount > 0 && <span className="text-red-400 ml-1">({weekPayouts.filter((p: any) => p.is_refunded).length} refunded)</span>}
                                </p>
                                {isAdRevenue && batch.status === 'pending' && (() => {
                                  const batchMonthStart = new Date(batch.week_start_date + 'T00:00:00');
                                  const payoutDate = new Date(batchMonthStart.getFullYear(), batchMonthStart.getMonth() + 1, 1);
                                  return (
                                    <p className="text-indigo-400 text-xs mt-0.5">
                                      Pays out {payoutDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                    </p>
                                  );
                                })()}
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className={`text-sm font-bold ${isAdRevenue ? 'text-indigo-300' : 'text-green-400'}`}>${Number(batch.net_payout_amount).toFixed(2)}</p>
                                {batch.total_refunded_amount > 0 && (
                                  <p className="text-gray-500 text-xs">${Number(batch.total_payout_amount).toFixed(2)} - ${Number(batch.total_refunded_amount).toFixed(2)} refunds</p>
                                )}
                              </div>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[batch.status] || 'text-gray-400 bg-gray-400/10'}`}>
                                {batch.status}
                              </span>
                              <svg className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </div>
                          </div>
                        </button>

                        {isExpanded && weekPayouts.length > 0 && (
                          <div className="border-t border-white/5 px-4 pb-3 space-y-2">
                            {weekPayouts.map((payout: any) => (
                              <div key={payout.id} className={`flex items-center justify-between p-3 rounded-lg ${payout.is_refunded ? 'bg-red-500/5 border border-red-500/10' : 'bg-white/5'}`}>
                                <div>
                                  <p className="text-white text-xs font-medium">
                                    {payout.payout_type === 'ad_revenue'
                                      ? `Sponsor ad revenue · ${batch.total_orders} click${batch.total_orders !== 1 ? 's' : ''}`
                                      : payout.order_id ? `Order #${payout.order_id.slice(0, 8)}` : payout.payout_type || 'Payout'}
                                    {payout.payout_type && payout.payout_type !== 'ad_revenue' && <span className="text-gray-500 ml-1.5">({payout.payout_type})</span>}
                                  </p>
                                  <p className="text-gray-500 text-xs">{new Date(payout.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                                  {payout.is_refunded && <p className="text-red-400 text-xs mt-0.5">Refunded{payout.refund_reason ? `: ${payout.refund_reason}` : ''}</p>}
                                </div>
                                <div className="text-right">
                                  <p className={`text-sm font-bold ${payout.is_refunded ? 'text-red-400 line-through' : isAdRevenue ? 'text-indigo-300' : 'text-green-400'}`}>
                                    ${Number(payout.payout_amount).toFixed(2)}
                                  </p>
                                  {payout.order_id && (
                                    <p className="text-gray-500 text-xs">
                                      ${Number(payout.order_amount).toFixed(2)} - {payout.admin_fee_percentage}% - processing
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        ) : mainView === 'network' && networkEnabled && talentProfile ? (
          <NetworkTab
            talentId={talentProfile.id}
            onOpenChat={(channelId) => {
              // Reset to null first so React sees a change even for the same channel ID
              setPushDeepLinkChannelId(null);
              setTimeout(() => {
                setPushDeepLinkChannelId(channelId);
                setPushOpenChat(true);
              }, 0);
              setMainView('dashboard');
            }}
          />
        ) : mainView === 'partner' && partnerData ? (
          /* Partner Program View */
          (() => {
            const showFollowers = partnerData.registration_source !== 'self_signup';
            return (
              <div className="space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-white">Partner Program</h2>
                  <button onClick={() => setMainView('customize')} className="text-sm text-gray-400 hover:text-white">&larr; Back</button>
                </div>
                <p className="text-sm text-gray-400 -mt-2">
                  Just share your link to creators you think would want to monetize their audience through pre-paid sponsors and replace their linktree.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] gap-5">
                  <div className="space-y-5 min-w-0">
                    {/* Referral Link */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Your Referral Link</p>
                      <button
                        onClick={copyPartnerLink}
                        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-white/10 border border-white/10 text-white rounded-xl text-sm font-medium hover:bg-white/15 active:scale-[0.99] transition-all"
                      >
                        <span className="font-mono text-xs truncate text-gray-300">https://shoutout.us/start?ref={partnerData.referral_token}</span>
                        {partnerCopied ? (
                          <CheckIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                        ) : (
                          <ClipboardIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: 'Total Signups', value: partnerReferrals.length },
                        { label: 'Onboarding Done', value: partnerReferrals.filter(t => t.onboarding_completed).length },
                        { label: 'Payouts Set Up', value: partnerReferrals.filter(t => t.payout_onboarding_completed).length },
                        { label: 'Live on ShoutOut', value: partnerReferrals.filter(t => t.shoutout_fans_status === 'live').length },
                      ].map(stat => (
                        <div key={stat.label} className="bg-white/5 border border-white/10 rounded-xl p-3">
                          <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{stat.label}</p>
                          <p className="text-xl font-bold text-white mt-0.5">{stat.value}</p>
                        </div>
                      ))}
                    </div>

                    {/* Referred Talent Table */}
                    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                      <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
                        <h3 className="text-sm font-semibold text-white">Referred Creators</h3>
                        <button
                          onClick={loadPartnerReferrals}
                          className="text-xs text-gray-400 hover:text-white transition-colors"
                        >
                          Refresh
                        </button>
                      </div>

                      {partnerReferralsLoading && partnerReferrals.length === 0 ? (
                        <div className="flex items-center justify-center py-12">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                        </div>
                      ) : partnerReferrals.length === 0 ? (
                        <div className="text-center py-12 px-4">
                          <UserGroupIcon className="h-10 w-10 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400 text-sm">No creators have signed up through your link yet.</p>
                          <p className="text-gray-500 text-xs mt-1">Share your referral link to get started.</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-white/5">
                          {partnerReferrals.map((t: any) => {
                            const totalFollowers = t.follower_counts ? Object.values(t.follower_counts as Record<string, number>).reduce((s: number, n: number) => s + n, 0) : (t.partner_noted_followers || 0);
                            return (
                              <div key={t.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-white/5 transition-colors">
                                {/* Avatar */}
                                <div className="flex-shrink-0">
                                  {t.temp_avatar_url ? (
                                    <img src={t.temp_avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                                  ) : (
                                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white text-sm font-bold">
                                      {(t.temp_full_name || '?').charAt(0)}
                                    </div>
                                  )}
                                </div>
                                {/* Info */}
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-white truncate">{t.temp_full_name || 'Unnamed'}</p>
                                  <p className="text-xs text-gray-500 truncate">{t.user_email || (t.username ? `@${t.username}` : '—')}</p>
                                </div>
                                {/* Onboarding */}
                                <div className="hidden sm:block flex-shrink-0">
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    t.onboarding_completed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-yellow-500/20 text-yellow-400'
                                  }`}>
                                    {t.onboarding_completed ? 'Onboarded' : `Step ${t.current_onboarding_step || 1}`}
                                  </span>
                                </div>
                                {/* Status */}
                                <div className="hidden sm:block flex-shrink-0">
                                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                    t.shoutout_fans_status === 'live' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-500/20 text-gray-400'
                                  }`}>
                                    {t.shoutout_fans_status === 'live' ? 'Live' : 'Pending'}
                                  </span>
                                </div>
                                {/* Followers button */}
                                {showFollowers && (
                                  <button
                                    onClick={() => { setFollowerModalTalent(t); setFollowerInputs(t.follower_counts || {}); }}
                                    className="flex-shrink-0 px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-gray-300 hover:bg-white/10 hover:text-white transition-colors"
                                  >
                                    {totalFollowers > 0 ? `${totalFollowers >= 1000 ? (totalFollowers / 1000).toFixed(0) + 'K' : totalFollowers} followers` : 'Add Followers'}
                                  </button>
                                )}
                                {/* Date */}
                                <span className="hidden lg:block flex-shrink-0 text-xs text-gray-500">
                                  {new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right rail: suggested outreach script */}
                  <aside className="lg:sticky lg:top-20 lg:self-start space-y-3">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Suggested Script</p>
                      <p className="text-xs text-gray-500 mb-3">Click to copy and send to a creator.</p>
                      <button
                        onClick={copySuggestedScript}
                        className="w-full text-left bg-black/30 border border-white/10 rounded-xl p-3 hover:bg-black/40 transition-colors mb-3"
                      >
                        <pre className="whitespace-pre-wrap text-xs text-gray-200 font-sans leading-relaxed">{SUGGESTED_SCRIPT}</pre>
                      </button>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={copySuggestedScript}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white text-gray-900 text-xs font-semibold hover:bg-gray-100 transition-colors"
                        >
                          {scriptCopied ? <CheckIcon className="h-4 w-4" /> : <ClipboardIcon className="h-4 w-4" />}
                          {scriptCopied ? 'Copied!' : 'Copy script'}
                        </button>
                        <button
                          onClick={copyPartnerLinkSidebar}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white/10 border border-white/10 text-white text-xs font-medium hover:bg-white/15 transition-colors"
                        >
                          {partnerLinkCopied ? <CheckIcon className="h-4 w-4 text-emerald-400" /> : <ClipboardIcon className="h-4 w-4" />}
                          {partnerLinkCopied ? 'Copied!' : 'Copy link'}
                        </button>
                      </div>
                    </div>
                  </aside>
                </div>
              </div>
            );
          })()
        ) : (
          /* Fans View */
          <div>
            {/* Sub-tab navigation */}
            <div className="flex bg-white/5 rounded-xl p-1 mb-6">
              <button
                onClick={() => { setFansSubTab('messages'); if (donationMessages.length === 0) loadDonationMessages(); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${fansSubTab === 'messages' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                <span className="hidden sm:inline">Messages &amp; Donations</span>
                <span className="sm:hidden">Messages</span>
                {donationMessages.filter(m => !m.is_read).length > 0 && (
                  <span className="bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                    {donationMessages.filter(m => !m.is_read).length} new
                  </span>
                )}
              </button>
              <button
                onClick={() => setFansSubTab('update')}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${fansSubTab === 'update' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                Update Fans
              </button>
              <button
                onClick={() => { setFansSubTab('fans'); if (fansData.length === 0) loadFansData(); }}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${fansSubTab === 'fans' ? 'bg-white/20 text-white' : 'text-gray-400 hover:text-white'}`}
              >
                Fans
                {subscriberCount > 0 && (
                  <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">{subscriberCount}</span>
                )}
              </button>
            </div>

            {fansSubTab === 'messages' ? (
              /* Donation Messages Sub-tab */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Donation Messages</h3>
                  {donationMessages.filter(m => !m.is_read).length > 0 && (
                    <button onClick={markMessagesRead} className="text-sm text-blue-400 hover:text-blue-300">Mark all read</button>
                  )}
                </div>
                {donationMessagesLoading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" /></div>
                ) : donationMessages.length === 0 ? (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
                    <p className="text-gray-400">No donation messages yet</p>
                    <p className="text-gray-500 text-sm mt-1">When fans leave a message with their donation, it will appear here.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {donationMessages.map(msg => (
                      <div key={msg.id} className={`bg-white/5 border rounded-2xl p-4 transition-colors ${msg.is_read ? 'border-white/10' : 'border-green-500/30 bg-green-500/5'}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white font-medium text-sm">{msg.donor_name || 'Anonymous'}</span>
                              <span className="text-gray-500 text-xs">{msg.donor_email}</span>
                              {!msg.is_read && <span className="bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">NEW</span>}
                            </div>
                            <p className="text-gray-300 text-sm">{msg.message}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-green-400 font-bold text-sm">${(msg.amount || 0).toFixed(2)}</p>
                            <p className="text-gray-500 text-xs">{msg.is_monthly ? 'Monthly' : 'One-time'}</p>
                            <p className="text-gray-600 text-xs mt-1">{new Date(msg.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        {msg.talent_reply ? (
                          <div className="mt-3 pl-3 border-l-2 border-blue-500/30">
                            <p className="text-blue-400 text-xs font-medium mb-1">Your reply</p>
                            <p className="text-gray-300 text-sm">{msg.talent_reply}</p>
                            <p className="text-gray-600 text-xs mt-1">{msg.talent_reply_at ? new Date(msg.talent_reply_at).toLocaleDateString() : ''}</p>
                          </div>
                        ) : replyingTo === msg.id ? (
                          <div className="mt-3 flex items-center gap-2">
                            <input
                              type="text"
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && handleReply(msg.id)}
                              placeholder="Write a reply..."
                              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500/50"
                              autoFocus
                            />
                            <button
                              onClick={() => handleReply(msg.id)}
                              disabled={sendingReply || !replyText.trim()}
                              className="px-3 py-2 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                            >
                              {sendingReply ? '...' : 'Send'}
                            </button>
                            <button
                              onClick={() => { setReplyingTo(null); setReplyText(''); }}
                              className="px-2 py-2 text-gray-400 hover:text-white text-sm transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setReplyingTo(msg.id)}
                            className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            Reply
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : fansSubTab === 'fans' ? (
              /* Fans List Sub-tab */
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">All Fans ({fansData.length || subscriberCount})</h3>
                  <button
                    onClick={() => setShowImportFansModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-gray-300 hover:bg-white/10 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Import
                  </button>
                </div>
                {fansLoading ? (
                  <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" /></div>
                ) : fansData.length === 0 ? (
                  <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
                    <p className="text-gray-400">No fans connected yet</p>
                    <p className="text-gray-500 text-sm mt-1">Fans who subscribe through your bio page will appear here.</p>
                  </div>
                ) : (
                  <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left text-gray-400 text-xs font-medium px-4 py-3">Fan</th>
                          <th className="text-left text-gray-400 text-xs font-medium px-4 py-3">Badges</th>
                          <th className="text-right text-gray-400 text-xs font-medium px-4 py-3">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fansData.map(fan => (
                          <tr key={fan.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                            <td className="px-4 py-3">
                              <p className="text-white text-sm font-medium">{fan.name || fan.email}</p>
                              {fan.name && <p className="text-gray-500 text-xs">{fan.email}</p>}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {fan.is_monthly_supporter && (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">💛 Monthly Supporter</span>
                                )}
                                {fan.is_supporter && !fan.is_monthly_supporter && (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 border border-green-500/30">💚 Supporter</span>
                                )}
                                {fan.has_ordered_shoutout && (
                                  <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">🎬 Ordered ShoutOut</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <p className="text-gray-400 text-xs">{new Date(fan.created_at).toLocaleDateString()}</p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : (
              /* Update Fans Sub-tab (original Send Updates content) */
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Left Column - Email Composer */}
            <div className="space-y-6">
              {/* Stats */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                      <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">{subscriberCount}</p>
                      <p className="text-gray-400 text-sm">Fans</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowImportFansModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-xl text-gray-300 hover:bg-white/10 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    Import
                  </button>
                </div>
              </div>

              {/* Email Composer */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-white">Compose Update</h3>
                  {draftSaveStatus === 'saving' && (
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </span>
                  )}
                  {draftSaveStatus === 'saved' && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      <CheckIcon className="w-3 h-3" />
                      Saved
                    </span>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Subject</label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="What's new this week..."
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Content</label>
                  <textarea
                    value={emailContent}
                    onChange={(e) => setEmailContent(e.target.value)}
                    placeholder="Share your latest news, upcoming events, or anything you want your subscribers to know..."
                    rows={6}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 resize-none"
                  />
                </div>

                {/* Add Button/Image Buttons */}
                <div className="flex gap-2">
                  <div className="relative">
                    <button
                      onClick={() => setShowButtonFields(!showButtonFields)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        showButtonFields || (emailButtonText && emailButtonUrl)
                          ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                          : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      {emailButtonText && emailButtonUrl ? 'Edit Button' : 'Add Button'}
                    </button>
                    {(showButtonFields || (emailButtonText && emailButtonUrl)) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEmailButtonText('');
                          setEmailButtonUrl('');
                          setShowButtonFields(false);
                        }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-700 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                        title="Remove button"
                      >
                        <XMarkIcon className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setShowImageUpload(!showImageUpload)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        showImageUpload || emailImageUrl
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                          : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {emailImageUrl ? 'Edit Image' : 'Add Image'}
                    </button>
                    {(showImageUpload || emailImageUrl) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEmailImageUrl('');
                          setEmailImageLinkUrl('');
                          setShowImageUpload(false);
                        }}
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gray-700 hover:bg-red-500 rounded-full flex items-center justify-center transition-colors"
                        title="Remove image"
                      >
                        <XMarkIcon className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Button Fields (collapsible) */}
                {showButtonFields && (
                  <div className="bg-white/5 rounded-xl p-4 space-y-3 border border-white/10">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-300">Button Settings</span>
                      {emailButtonText && emailButtonUrl && (
                        <button
                          onClick={() => {
                            setEmailButtonText('');
                            setEmailButtonUrl('');
                            setShowButtonFields(false);
                          }}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={emailButtonText}
                        onChange={(e) => setEmailButtonText(e.target.value)}
                        placeholder="Button text"
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                      />
                      <input
                        type="url"
                        value={emailButtonUrl}
                        onChange={(e) => setEmailButtonUrl(e.target.value)}
                        placeholder="https://..."
                        className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                      />
                    </div>
                  </div>
                )}

                {/* Image Upload (collapsible) */}
                {showImageUpload && (
                  <div className="bg-white/5 rounded-xl p-4 space-y-3 border border-white/10">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-300">Image</span>
                      {emailImageUrl && (
                        <button
                          onClick={() => {
                            setEmailImageUrl('');
                            setEmailImageLinkUrl('');
                            setShowImageUpload(false);
                          }}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    
                    {emailImageUrl ? (
                      <div className="space-y-3">
                        <div className="relative">
                          <img src={emailImageUrl} alt="Preview" className="w-full h-32 object-cover rounded-lg" />
                          <button
                            onClick={() => setEmailImageUrl('')}
                            className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Link URL for the image */}
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">Link URL (optional)</label>
                          <input
                            type="url"
                            value={emailImageLinkUrl}
                            onChange={(e) => setEmailImageLinkUrl(e.target.value)}
                            placeholder="https://... (where image links to when clicked)"
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500/50"
                          />
                        </div>
                      </div>
                    ) : (
                      <label className="block">
                        <div className="flex items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-white/40 transition-colors">
                          {uploadingEmailImage ? (
                            <div className="flex items-center gap-2 text-gray-400">
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Uploading...
                            </div>
                          ) : (
                            <div className="text-center">
                              <CloudArrowUpIcon className="w-8 h-8 text-gray-500 mx-auto mb-1" />
                              <span className="text-sm text-gray-400">Click to upload image</span>
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setUploadingEmailImage(true);
                              try {
                                const result = await uploadImageToWasabi(file, `email-images/${talentProfile?.id}`);
                                if (result.success && result.imageUrl) {
                                  setEmailImageUrl(result.imageUrl);
                                  toast.success('Image uploaded!');
                                } else {
                                  toast.error(result.error || 'Failed to upload image');
                                }
                              } catch (error) {
                                toast.error('Failed to upload image');
                              }
                              setUploadingEmailImage(false);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowScheduleModal(true)}
                    className="flex-1 py-3 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {emailScheduledDate && emailScheduledTime ? (
                      <span className="text-blue-400">
                        {new Date(`${emailScheduledDate}T${emailScheduledTime}`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                      </span>
                    ) : (
                      'Schedule'
                    )}
                  </button>
                  <button
                    onClick={async () => {
                      if (!emailSubject || !emailContent) {
                        toast.error('Please add a subject and content');
                        return;
                      }
                      if (!emailDraftId) {
                        toast.error('Please wait for draft to save');
                        return;
                      }
                      if (emailScheduledDate && emailScheduledTime) {
                        // TODO: Implement scheduled sends
                        toast.success(`Email scheduled for ${emailScheduledDate} at ${emailScheduledTime}`);
                        return;
                      }
                      
                      setSendingEmail(true);
                      try {
                        const { data, error } = await supabase.functions.invoke('send-talent-update', {
                          body: {
                            draft_id: emailDraftId,
                            talent_id: talentProfile?.id,
                          },
                        });
                        
                        if (error) throw error;
                        
                        if (data?.success) {
                          toast.success(`Email sent to ${data.sent} fans!`);
                          // Clear the form after successful send
                          setEmailSubject('');
                          setEmailContent('');
                          setEmailButtonText('');
                          setEmailButtonUrl('');
                          setEmailImageUrl('');
                          setEmailImageLinkUrl('');
                          setEmailDraftId(null);
                        } else {
                          toast.error(data?.error || 'Failed to send email');
                        }
                      } catch (error: any) {
                        console.error('Send error:', error);
                        toast.error(error.message || 'Failed to send email');
                      } finally {
                        setSendingEmail(false);
                      }
                    }}
                    disabled={subscriberCount === 0 || !emailSubject || !emailContent || sendingEmail}
                    className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingEmail ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : subscriberCount === 0 
                      ? 'No Fans Yet' 
                      : `Send Now (${subscriberCount})`}
                  </button>
                </div>
              </div>
            </div>

            {/* Schedule Modal */}
            {showScheduleModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-md">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Schedule Send</h3>
                    <button onClick={() => setShowScheduleModal(false)} className="text-gray-400 hover:text-white">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Date</label>
                      <input
                        type="date"
                        value={emailScheduledDate}
                        onChange={(e) => setEmailScheduledDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">Time</label>
                      <input
                        type="time"
                        value={emailScheduledTime}
                        onChange={(e) => setEmailScheduledTime(e.target.value)}
                        className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-blue-500/50"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3 mt-6">
                    {emailScheduledDate && emailScheduledTime && (
                      <button
                        onClick={() => {
                          setEmailScheduledDate('');
                          setEmailScheduledTime('');
                        }}
                        className="px-4 py-2 text-red-400 hover:text-red-300 text-sm"
                      >
                        Clear
                      </button>
                    )}
                    <div className="flex-1" />
                    <button
                      onClick={() => setShowScheduleModal(false)}
                      className="px-6 py-2.5 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (emailScheduledDate && emailScheduledTime) {
                          toast.success('Schedule set!');
                        }
                        setShowScheduleModal(false);
                      }}
                      disabled={!emailScheduledDate || !emailScheduledTime}
                      className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Set Schedule
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Import Fans Modal */}
            {showImportFansModal && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-lg">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-white">Import Fans</h3>
                    <button onClick={() => setShowImportFansModal(false)} className="text-gray-400 hover:text-white">
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    {/* CSV Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-3">Upload CSV</label>
                      <label className="block">
                        <div className="flex items-center justify-center w-full h-28 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-white/40 transition-colors bg-white/5">
                          {importingFans ? (
                            <div className="flex items-center gap-2 text-gray-400">
                              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Importing...
                            </div>
                          ) : (
                            <div className="text-center">
                              <svg className="w-8 h-8 text-gray-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm text-gray-400">Drop CSV file or click to browse</span>
                              <p className="text-xs text-gray-500 mt-1">Format: email, name (optional)</p>
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          accept=".csv"
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setImportingFans(true);
                              // TODO: Parse CSV and import fans
                              setTimeout(() => {
                                toast.success('CSV import coming soon!');
                                setImportingFans(false);
                              }, 1000);
                            }
                          }}
                        />
                      </label>
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-white/10"></div>
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="px-2 bg-gray-900 text-gray-500">or sync from email provider</span>
                      </div>
                    </div>

                    {/* Email Integrations - Use existing newsletter providers */}
                    <div>
                      {/* Show connected integrations first */}
                      {newsletterConfigs.some(c => c.is_active) ? (
                        <>
                          <label className="block text-sm font-medium text-gray-300 mb-3">Connected Integrations</label>
                          <div className="space-y-2 mb-4">
                            {newsletterConfigs.filter(c => c.is_active).map(config => {
                              const provider = NEWSLETTER_PROVIDERS.find(p => p.id === config.provider);
                              return (
                                <div
                                  key={config.id}
                                  className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-xl"
                                >
                                  <div className="flex items-center gap-3">
                                    <span className="text-lg">{provider?.icon}</span>
                                    <div>
                                      <span className="text-green-400 font-medium text-sm">{provider?.name}</span>
                                      <p className="text-green-400/60 text-xs">Connected</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => {
                                        setShowImportFansModal(false);
                                        setShowNewsletterModal(true);
                                      }}
                                      className="text-xs text-green-400 hover:text-green-300"
                                    >
                                      Edit
                                    </button>
                                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <p className="text-gray-500 text-xs mb-3">
                            New fans will automatically sync to your connected email provider.
                          </p>
                        </>
                      ) : (
                        <>
                          <label className="block text-sm font-medium text-gray-300 mb-3">Connect Email Provider</label>
                          <p className="text-gray-500 text-xs mb-3">
                            Connect your email provider to automatically sync new fans.
                          </p>
                        </>
                      )}

                      {/* Show available providers to connect */}
                      <div className="grid grid-cols-2 gap-2">
                        {NEWSLETTER_PROVIDERS.map((provider) => {
                          const config = newsletterConfigs.find(c => c.provider === provider.id);
                          const isConnected = config?.is_active;
                          
                          if (isConnected) return null; // Don't show already connected ones in grid
                          
                          return (
                            <button
                              key={provider.id}
                              onClick={() => {
                                setShowImportFansModal(false);
                                setShowNewsletterModal(true);
                              }}
                              className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors"
                            >
                              <span className="text-base">{provider.icon}</span>
                              <span className="text-gray-300 text-sm">{provider.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end mt-6">
                    <button
                      onClick={() => setShowImportFansModal(false)}
                      className="px-6 py-2.5 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Right Column - Email Preview */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                {/* Browser Chrome */}
                <div className="bg-gray-900/80 px-3 py-2 border-b border-white/10">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
                    </div>
                    <div className="flex-1 flex items-center gap-2 bg-white/5 rounded-lg px-3 py-1.5 ml-2">
                      <span className="text-gray-400 text-xs">Email Preview</span>
                    </div>
                  </div>
                </div>
                
                {/* Email Preview */}
                <div 
                  className="p-6 overflow-y-auto"
                  style={{ 
                    height: '680px',
                    background: `linear-gradient(${bioSettings?.gradient_direction === 'to-b' ? '180deg' : '135deg'}, ${bioSettings?.gradient_start || '#0a0a0a'}, ${bioSettings?.gradient_end || '#1a1a2e'})`
                  }}
                >
                  {/* Email Content */}
                  <div className="max-w-md mx-auto space-y-6">
                    {/* Header with profile */}
                    <div className="text-center">
                      <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden border-2 border-white/20 shadow-xl">
                        {talentProfile?.temp_avatar_url || bioSettings?.profile_image_url ? (
                          <img 
                            src={talentProfile?.temp_avatar_url || bioSettings?.profile_image_url} 
                            alt={talentProfile?.full_name || ''} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-2xl text-white font-bold">
                            {(talentProfile?.full_name || 'C')[0]}
                          </div>
                        )}
                      </div>
                      <h2 className="text-xl font-bold text-white">{talentProfile?.full_name || 'Creator'}</h2>
                    </div>

                    {/* Subject */}
                    {emailSubject && (
                      <h1 className="text-2xl font-bold text-white text-center">{emailSubject}</h1>
                    )}

                    {/* Image */}
                    {emailImageUrl && (
                      emailImageLinkUrl ? (
                        <a href={emailImageLinkUrl} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden hover:opacity-90 transition-opacity">
                          <img src={emailImageUrl} alt="" className="w-full h-auto" />
                        </a>
                      ) : (
                        <div className="rounded-xl overflow-hidden">
                          <img src={emailImageUrl} alt="" className="w-full h-auto" />
                        </div>
                      )
                    )}

                    {/* Content */}
                    {emailContent ? (
                      <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                        {emailContent}
                      </div>
                    ) : (
                      <div className="text-gray-500 italic text-center py-8">
                        Your message will appear here...
                      </div>
                    )}

                    {/* Button */}
                    {emailButtonText && emailButtonUrl && (
                      <div className="text-center">
                        <a
                          href={emailButtonUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block px-8 py-3 rounded-full font-medium transition-colors"
                          style={{
                            backgroundColor: bioSettings?.button_color || '#3b82f6',
                            color: '#ffffff'
                          }}
                        >
                          {emailButtonText}
                        </a>
                      </div>
                    )}

                    {/* Divider */}
                    <div className="border-t border-white/10 pt-6 mt-6 space-y-4">
                      {/* Connect Card - Grid of mini cards */}
                      {(() => {
                        // Collect all items for the grid
                        const gridItems: Array<{
                          type: 'event' | 'collab' | 'link';
                          title: string;
                          subtitle?: string;
                          image?: string;
                          icon?: React.ReactNode;
                          platformIcons?: Array<{ id: string; icon: React.ReactNode } | null>;
                        }> = [];

                        // Add event if exists - show details, not image
                        const activeEvent = bioEvents.find(e => e.is_active);
                        if (activeEvent) {
                          const eventDate = activeEvent.event_date 
                            ? new Date(activeEvent.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : '';
                          gridItems.push({
                            type: 'event',
                            title: activeEvent.title || 'Upcoming Event',
                            subtitle: eventDate || activeEvent.location || undefined,
                            icon: (
                              <svg className="w-4 h-4 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            )
                          });
                        }

                        // Add collab if exists and enabled - show social icons and total followers
                        if (talentProfile?.collabs_enabled && serviceOfferings.filter(s => s.service_type !== 'sponsorship').length > 0) {
                          const collab = serviceOfferings.find(s => s.service_type !== 'sponsorship') || serviceOfferings[0];
                          // Calculate total followers from selected platforms
                          const totalFollowers = collab.platforms?.reduce((total, platformId) => {
                            const social = socialLinks.find(s => s.platform === platformId);
                            return total + (social?.follower_count || 0);
                          }, 0) || 0;
                          
                          // Get social icons for the platforms
                          const platformIcons = collab.platforms?.slice(0, 3).map(platformId => {
                            const platform = COLLAB_PLATFORMS.find(p => p.id === platformId);
                            return platform ? { id: platformId, icon: platform.icon } : null;
                          }).filter(Boolean) || [];
                          
                          gridItems.push({
                            type: 'collab',
                            title: 'Collaborate with me',
                            subtitle: totalFollowers > 0 ? `${totalFollowers >= 1000000 ? (totalFollowers / 1000000).toFixed(1) + 'M' : totalFollowers >= 1000 ? (totalFollowers / 1000).toFixed(0) + 'K' : totalFollowers} followers` : undefined,
                            platformIcons: platformIcons,
                            icon: (
                              <svg className="w-4 h-4 text-purple-400" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073z"/>
                              </svg>
                            )
                          });
                        }

                        // Add links - show images if they have them
                        links.filter(l => l.is_active && (l.link_type === 'basic' || l.link_type === 'grid')).slice(0, 4 - gridItems.length).forEach(link => {
                          // Check all possible image fields
                          const linkImage = link.image_url || link.thumbnail_url || link.icon_url || link.background_image_url;
                          gridItems.push({
                            type: 'link',
                            title: link.title || 'Link',
                            image: linkImage || undefined,
                            icon: <LinkIcon className="w-4 h-4 text-blue-400" />
                          });
                        });

                        const itemCount = gridItems.length;

                        if (itemCount === 0) {
                          return (
                            <a 
                              href={`https://${bioUrl}`}
                              className="block p-4 rounded-2xl overflow-hidden hover:scale-[1.02] transition-transform"
                              style={{
                                background: 'rgba(255,255,255,0.05)',
                                border: '1px solid rgba(255,255,255,0.15)'
                              }}
                            >
                              <p className="text-white font-semibold text-sm mb-2">✨ More ways to connect</p>
                              <p className="text-gray-500 text-xs italic">Add content to your bio to show here</p>
                            </a>
                          );
                        }

                        return (
                          <a 
                            href={`https://${bioUrl}`}
                            className="block p-4 rounded-2xl overflow-hidden hover:scale-[1.02] transition-transform"
                            style={{
                              background: 'rgba(255,255,255,0.05)',
                              border: '1px solid rgba(255,255,255,0.15)'
                            }}
                          >
                            <p className="text-white font-semibold text-sm mb-3">✨ More ways to connect</p>
                            
                            {/* Dynamic grid based on item count */}
                            <div className={`grid gap-2 ${
                              itemCount === 1 ? 'grid-cols-1' :
                              itemCount === 2 ? 'grid-cols-2' :
                              itemCount === 3 ? 'grid-cols-2' :
                              'grid-cols-2'
                            }`}>
                              {gridItems.map((item, i) => (
                                <div 
                                  key={i}
                                  className={`rounded-lg overflow-hidden bg-black/30 ${
                                    itemCount === 3 && i === 2 ? 'col-span-2' : ''
                                  }`}
                                >
                                  {/* Links with images show the image */}
                                  {item.type === 'link' && item.image ? (
                                    <div className="relative">
                                      <img 
                                        src={item.image} 
                                        alt="" 
                                        className={`w-full object-cover ${
                                          itemCount === 3 && i === 2 ? 'h-16' : 'h-14'
                                        }`}
                                      />
                                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                      <div className="absolute bottom-1 left-2 right-2">
                                        <p className="text-white text-[10px] font-medium truncate">{item.title}</p>
                                      </div>
                                    </div>
                                  ) : item.type === 'collab' && item.platformIcons && item.platformIcons.length > 0 ? (
                                    /* Collab shows social platform icons and follower count */
                                    <div className={`flex items-center gap-2 p-2.5 ${
                                      itemCount === 3 && i === 2 ? 'justify-center' : ''
                                    }`}>
                                      <div className="flex -space-x-1">
                                        {item.platformIcons.map((p, idx) => p && (
                                          <div key={p.id} className="w-4 h-4 text-purple-400" style={{ zIndex: 10 - idx }}>
                                            {p.icon}
                                          </div>
                                        ))}
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <p className="text-gray-200 text-[10px] font-medium truncate">{item.title}</p>
                                        {item.subtitle && (
                                          <p className="text-purple-400 text-[9px] truncate">{item.subtitle}</p>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    /* Events show details, links without images show icon + text */
                                    <div className={`flex items-center gap-2 p-2.5 ${
                                      itemCount === 3 && i === 2 ? 'justify-center' : ''
                                    }`}>
                                      {item.icon}
                                      <div className="min-w-0 flex-1">
                                        <p className="text-gray-200 text-[10px] font-medium truncate">{item.title}</p>
                                        {item.subtitle && (
                                          <p className="text-gray-500 text-[9px] truncate">{item.subtitle}</p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </a>
                        );
                      })()}

                      {/* ShoutOut Card - Redesigned with flush image */}
                      <a
                        href={`https://shoutout.us/${talentProfile?.username || talentProfile?.id}`}
                        className="block rounded-2xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 hover:border-blue-500/50 transition-colors overflow-hidden"
                      >
                        <div className="flex items-stretch">
                          {/* Image flush to edges with play button */}
                          <div className="w-24 flex-shrink-0 relative overflow-hidden bg-black/30">
                            {talentProfile?.temp_avatar_url || bioSettings?.profile_image_url ? (
                              <img 
                                src={talentProfile?.temp_avatar_url || bioSettings?.profile_image_url} 
                                alt="" 
                                className="absolute inset-0 w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                                <GiftIcon className="w-8 h-8 text-white" />
                              </div>
                            )}
                            {/* Play button overlay */}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                                <svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 p-4 min-w-0">
                            <p className="text-white text-sm font-semibold mb-1">Get a Personalized Video Shoutout</p>
                            
                            {/* Show review */}
                            {randomReview ? (
                              <div>
                                <div className="flex items-center gap-0.5 mb-1">
                                  {[...Array(5)].map((_, i) => (
                                    <svg 
                                      key={i} 
                                      className={`w-3 h-3 ${i < randomReview.rating ? 'text-yellow-400' : 'text-gray-600'}`} 
                                      fill="currentColor" 
                                      viewBox="0 0 20 20"
                                    >
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  ))}
                                </div>
                                {randomReview.comment && (
                                  <p className="text-gray-300 text-xs line-clamp-1">
                                    "{randomReview.comment}"
                                  </p>
                                )}
                                {randomReview.users?.full_name && (
                                  <p className="text-gray-500 text-[10px] mt-1">
                                    — {randomReview.users.full_name}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-gray-400 text-xs">From {talentProfile?.full_name || 'your favorite creator'}</p>
                            )}
                          </div>
                        </div>
                      </a>
                    </div>

                    {/* Footer */}
                    <div className="text-center pt-6 space-y-3 border-t border-white/5 mt-4">
                      <p className="text-gray-500 text-[10px] leading-relaxed">
                        You subscribed to {talentProfile?.full_name?.split(' ')[0] || 'this creator'} through their link in bio on their social platforms.
                      </p>
                      <a href="https://shoutout.us/creators" className="inline-flex items-center gap-2 opacity-50 hover:opacity-70 transition-opacity">
                        <span className="text-gray-400 text-[10px]">Powered by</span>
                        <img 
                          src="/whiteicon.png" 
                          alt="ShoutOut" 
                          className="h-6 w-auto brightness-0 invert"
                        />
                      </a>
                      <div className="text-gray-600 text-[9px] leading-relaxed space-y-1">
                        <p>ShoutOut, LLC</p>
                        <p>1201 N Riverfront Blvd Ste 100, Dallas, TX 75207</p>
                        <p className="pt-1">
                          <span className="text-gray-500 hover:text-gray-400 underline cursor-pointer">Unsubscribe</span>
                          {' · '}
                          <a href="https://shoutout.us/privacy" className="text-gray-500 hover:text-gray-400 underline">Privacy Policy</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
            )}
          </div>
        )}
      </div>

      {/* Pre-paid Sponsors Modal */}
      {talentProfile && (
        <PrePaidSponsorsModal
          isOpen={showSponsorsModal}
          onClose={() => setShowSponsorsModal(false)}
          talentId={talentProfile.id}
        />
      )}

      {/* Instagram Disclaimer Modal */}
      {showIgDisclaimer && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center z-[60] p-4" onClick={() => {}}>
          <div className="bg-[#111] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/10" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-orange-400 mb-4">
                  <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                </div>
                <h2 className="text-xl font-bold text-white">Important Note About Instagram Links</h2>
              </div>

              {/* Content */}
              <div className="space-y-5 text-sm leading-relaxed">
                <p className="text-gray-300">
                  Instagram does not want you sending traffic outside Instagram. So they let you add as many links as you want, significantly dropping traffic to <strong className="text-white">all</strong> of them. Here's how to earn the most from your link:
                </p>

                {/* Tip 1 - Highlighted */}
                <div className="bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/40 rounded-xl p-4">
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-500 text-white text-sm font-bold flex items-center justify-center">1</span>
                    <div>
                      <p className="text-white font-semibold mb-1">Use your ShoutOut.fans link as the ONLY link on your social profiles</p>
                      <p className="text-gray-300 text-xs">Multiple links in your bio immediately kills traffic to all links added.</p>
                      <p className="text-purple-300 text-xs mt-1.5 font-medium">💡 Hint: You can add all your extra links to your ShoutOut.fans link!</p>
                    </div>
                  </div>
                </div>

                {/* Tip 2 */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-500 text-white text-sm font-bold flex items-center justify-center">2</span>
                    <div>
                      <p className="text-white font-semibold mb-1">Post a story at least once a week with your link</p>
                      <p className="text-gray-300 text-xs">Give people a reason to visit — mention donations, messaging you, ordering a personalized ShoutOut video, etc. This drives more traffic and increases your CTR, earning you more sponsorship revenue.</p>
                    </div>
                  </div>
                </div>

                {/* Case Study */}
                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-4">
                  <p className="text-emerald-400 font-semibold text-xs uppercase tracking-wider mb-1.5">📊 Case Study</p>
                  <p className="text-gray-200 text-xs leading-relaxed">
                    <strong className="text-white">Isabella Riley</strong> posts every other day and has driven over <strong className="text-white">20,000 views/month</strong> and earned <strong className="text-emerald-400">$4,978 in her first 30 days</strong> through pre-paid sponsor cards and ShoutOut videos.
                  </p>
                </div>

              </div>

              {/* Checkbox + Continue */}
              <div className="mt-6 pt-5 border-t border-white/10">
                <label className="flex items-start gap-3 cursor-pointer group mb-4">
                  <input
                    type="checkbox"
                    checked={igDisclaimerChecked}
                    onChange={e => setIgDisclaimerChecked(e.target.checked)}
                    className="mt-0.5 w-5 h-5 rounded border-white/30 bg-white/5 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
                  />
                  <span className="text-sm text-gray-300 group-hover:text-white transition-colors">I understand and have read the Instagram disclaimer.</span>
                </label>
                <button
                  onClick={() => {
                    if (igDisclaimerChecked && talentProfile?.id) {
                      localStorage.setItem(`ig_disclaimer_${talentProfile.id}`, 'true');
                      setShowIgDisclaimer(false);
                    }
                  }}
                  disabled={!igDisclaimerChecked}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                    igDisclaimerChecked
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
                      : 'bg-white/5 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Request Free Design Modal */}
      {showDesignModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowDesignModal(false)}>
          <div className="bg-[#111] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-white/10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <h3 className="text-lg font-bold text-white">Request our team to design your ShoutOut Fans link for free!</h3>
                <p className="text-sm text-gray-400 mt-1">We'll design your link and optimize it for the best CTR.</p>
              </div>
              <button onClick={() => setShowDesignModal(false)} className="text-gray-500 hover:text-white flex-shrink-0 ml-3">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Auto-scrolling carousel of designed links */}
            <div className="pt-5 overflow-x-clip overflow-y-visible pb-4">
              <div
                ref={designCarouselRef}
                className="flex gap-4"
                style={{ width: 'max-content' }}
              >
                {[...bioCreatorBios, ...bioCreatorBios, ...bioCreatorBios, ...bioCreatorBios].map((creator, index) => (
                  <div key={index} className="flex-shrink-0 group" style={{ width: '140px' }}>
                    <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-[#1a1a2e] transition-all duration-500 group-hover:scale-[1.02] group-hover:-translate-y-1" style={{ boxShadow: '0 10px 25px -6px rgba(0,0,0,0.5)' }}>
                      <div className="relative overflow-hidden" style={{ width: '140px', height: '270px' }}>
                        <iframe
                          src={`https://shoutout.fans/${creator.handle}`}
                          title={`${creator.name}'s bio`}
                          className="border-0 pointer-events-none absolute top-0 left-0"
                          loading="lazy"
                          scrolling="no"
                          style={{ width: '375px', height: '750px', transform: 'scale(0.373)', transformOrigin: 'top left' }}
                        />
                        <div className="absolute inset-0 cursor-default" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent">
                        <p className="font-semibold text-[10px] text-white text-center">{creator.name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form */}
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Your LinkTree URL (optional)</label>
                <input
                  type="url"
                  value={designLinktree}
                  onChange={e => setDesignLinktree(e.target.value)}
                  placeholder="https://linktr.ee/yourname"
                  className="w-full bg-white/5 border border-white/10 px-3 py-2.5 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500/50"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Other links you'd like added (optional)</label>
                <textarea
                  value={designOtherLinks}
                  onChange={e => setDesignOtherLinks(e.target.value)}
                  placeholder="Paste any other links you want on your page..."
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 px-3 py-2.5 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500/50 resize-none"
                />
              </div>
              <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                <div className="flex-1 mr-3">
                  <p className="text-sm font-medium text-white">Get paid with pre-paid sponsor cards</p>
                  <p className="text-xs text-gray-400 mt-0.5">Earn $600–$1,200 per 1,000 clicks on your link</p>
                </div>
                <button
                  onClick={() => setDesignSponsorCards(!designSponsorCards)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${designSponsorCards ? 'bg-purple-600' : 'bg-white/20'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${designSponsorCards ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <button
                onClick={async () => {
                  if (!talentProfile?.id) return;
                  setDesignSubmitting(true);
                  try {
                    const { error } = await supabase.from('design_requests').insert({
                      talent_id: talentProfile.id,
                      linktree_url: designLinktree || null,
                      other_links: designOtherLinks || null,
                      sponsor_cards_interested: designSponsorCards,
                    });
                    if (error) throw error;
                    toast.success('Design request submitted! Our team will get to work.');
                    setDesignAlreadyRequested(true);
                    setShowDesignModal(false);
                    setDesignLinktree('');
                    setDesignOtherLinks('');
                    setDesignSponsorCards(false);
                  } catch (err: any) {
                    toast.error(err.message || 'Failed to submit request');
                  } finally {
                    setDesignSubmitting(false);
                  }
                }}
                disabled={designSubmitting}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold rounded-xl hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50"
              >
                {designSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partner Followers Modal */}
      {followerModalTalent && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setFollowerModalTalent(null)}>
          <div className="bg-[#111] rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto border border-white/10" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                {followerModalTalent.temp_avatar_url ? (
                  <img src={followerModalTalent.temp_avatar_url} alt="" className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-white font-bold">
                    {(followerModalTalent.temp_full_name || '?').charAt(0)}
                  </div>
                )}
                <div>
                  <h3 className="text-white font-semibold">{followerModalTalent.temp_full_name || 'Creator'}</h3>
                  <p className="text-xs text-gray-500">Social Media Followers</p>
                </div>
              </div>
              <button onClick={() => setFollowerModalTalent(null)} className="text-gray-500 hover:text-white">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {[
                { id: 'youtube', label: 'YouTube', emoji: '📺' },
                { id: 'rumble', label: 'Rumble', emoji: '🟩' },
                { id: 'instagram', label: 'Instagram', emoji: '📸' },
                { id: 'twitter', label: 'Twitter / X', emoji: '𝕏' },
                { id: 'tiktok', label: 'TikTok', emoji: '🎵' },
                { id: 'facebook', label: 'Facebook', emoji: '📘' },
                { id: 'truth_social', label: 'Truth Social', emoji: '🇺🇸' },
                { id: 'telegram', label: 'Telegram', emoji: '✈️' },
                { id: 'gettr', label: 'GETTR', emoji: '🔴' },
                { id: 'linkedin', label: 'LinkedIn', emoji: '💼' },
                { id: 'podcast', label: 'Podcast', emoji: '🎙️' },
                { id: 'snapchat', label: 'Snapchat', emoji: '👻' },
                { id: 'other', label: 'Other', emoji: '🌐' },
              ].map(network => {
                const hasValue = followerInputs[network.id] !== undefined;
                return (
                  <div key={network.id} className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        const updated = { ...followerInputs };
                        if (hasValue) { delete updated[network.id]; } else { updated[network.id] = 0; }
                        setFollowerInputs(updated);
                      }}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center text-lg flex-shrink-0 border-2 transition-colors ${
                        hasValue ? 'border-orange-500 bg-orange-500/10' : 'border-white/10 bg-white/5'
                      }`}
                    >
                      {network.emoji}
                    </button>
                    <span className={`text-sm font-medium w-28 ${hasValue ? 'text-white' : 'text-gray-500'}`}>
                      {network.label}
                    </span>
                    {hasValue ? (
                      <input
                        type="number"
                        min={0}
                        value={followerInputs[network.id] || ''}
                        onChange={e => setFollowerInputs({ ...followerInputs, [network.id]: parseInt(e.target.value) || 0 })}
                        placeholder="0"
                        className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    ) : (
                      <button
                        onClick={() => setFollowerInputs({ ...followerInputs, [network.id]: 0 })}
                        className="flex-1 px-3 py-2 border border-dashed border-white/10 rounded-lg text-sm text-gray-500 hover:border-white/20 hover:text-gray-400 transition-colors text-left"
                      >
                        Click to add...
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="p-4 mx-5 mb-3 bg-orange-500/10 border border-orange-500/20 rounded-xl">
              <p className="text-orange-400 text-xs">You'll get paid for the platforms they use their shoutout.fans link exclusively on.</p>
            </div>
            <div className="p-5 flex items-center justify-between border-t border-white/10">
              <div className="text-sm text-gray-400">
                Total: <span className="font-bold text-white">
                  {Object.values(followerInputs).reduce((s: number, n) => s + ((n as number) || 0), 0).toLocaleString()}
                </span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setFollowerModalTalent(null)} className="px-4 py-2 text-sm font-medium text-gray-400">
                  Cancel
                </button>
                <button
                  disabled={followerSaving}
                  onClick={saveFollowerCounts}
                  className="px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 disabled:opacity-50 transition-colors"
                >
                  {followerSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddLinkModal
          onClose={() => setShowAddModal(false)}
          onAdd={addLink}
          onAddMultiple={addMultipleLinks}
          talentId={talentProfile?.id || ''}
        />
      )}

      {editingLink && (
        <EditLinkModal
          link={editingLink}
          onClose={() => setEditingLink(null)}
          onSave={updateLink}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowSettingsModal(false)}>
          <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-md border border-white/10 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Settings</h2>
              <button onClick={() => setShowSettingsModal(false)} className="text-gray-400 hover:text-white">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <button
                onClick={() => { setShowSettingsModal(false); setShowNewsletterModal(true); }}
                className="w-full bg-white/5 border border-white/10 rounded-xl p-4 flex items-center justify-between hover:bg-white/8 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <EnvelopeIcon className="h-5 w-5 text-green-400" />
                  <div>
                    <span className="text-white text-sm font-medium">Email Integrations</span>
                    <p className="text-gray-400 text-xs">Mailchimp, ConvertKit, etc.</p>
                  </div>
                </div>
                <span className="text-gray-400 text-xs">Configure &rarr;</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Edit Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setShowProfileModal(false)}>
          <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-md border border-white/10 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Edit Profile</h2>
              <button onClick={() => setShowProfileModal(false)} className="text-gray-400 hover:text-white">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Avatar */}
              <div className="flex flex-col items-center gap-3">
                <div className="relative group">
                  <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-white/20 shadow-xl">
                    {profileEditAvatar ? (
                      <img src={profileEditAvatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-white/10 flex items-center justify-center">
                        <UserCircleIcon className="h-12 w-12 text-gray-500" />
                      </div>
                    )}
                  </div>
                  <label className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                    <CameraIcon className="h-6 w-6 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setProfileUploading(true);
                        try {
                          const result = await uploadImageToWasabi(file, `avatars/${talentProfile?.id}`);
                          if (result.success && result.imageUrl) {
                            setProfileEditAvatar(result.imageUrl);
                          } else {
                            toast.error(result.error || 'Upload failed');
                          }
                        } catch {
                          toast.error('Failed to upload image');
                        }
                        setProfileUploading(false);
                      }}
                    />
                  </label>
                  {profileUploading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-400">Tap to change photo</p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Display Name</label>
                <input
                  type="text"
                  value={profileEditName}
                  onChange={(e) => setProfileEditName(e.target.value)}
                  placeholder="Your name"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
                />
              </div>

              {/* ShoutOut URL */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">ShoutOut URL</label>
                <div className="flex items-center gap-0">
                  <span className="bg-white/5 border border-r-0 border-white/10 rounded-l-xl px-3 py-3 text-gray-400 text-sm whitespace-nowrap">shoutout.fans/</span>
                  <input
                    type="text"
                    value={profileEditUsername}
                    onChange={(e) => {
                      const val = e.target.value.replace(/[^a-zA-Z0-9_.-]/g, '');
                      setProfileEditUsername(val);
                      setProfileUsernameAvailable(null);
                    }}
                    onBlur={async () => {
                      if (!profileEditUsername || profileEditUsername === talentProfile?.username) {
                        setProfileUsernameAvailable(null);
                        return;
                      }
                      setProfileCheckingUsername(true);
                      const { data } = await supabase
                        .from('talent_profiles')
                        .select('id')
                        .ilike('username', profileEditUsername)
                        .neq('id', talentProfile?.id || '')
                        .maybeSingle();
                      setProfileUsernameAvailable(!data);
                      setProfileCheckingUsername(false);
                    }}
                    placeholder="your-url"
                    className={`flex-1 bg-white/5 border rounded-r-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none transition-colors ${
                      profileUsernameAvailable === false ? 'border-red-500/50' : profileUsernameAvailable === true ? 'border-green-500/50' : 'border-white/10 focus:border-purple-500/50'
                    }`}
                  />
                </div>
                {profileCheckingUsername && <p className="text-xs text-gray-400 mt-1">Checking availability...</p>}
                {profileUsernameAvailable === false && <p className="text-xs text-red-400 mt-1">That URL is already taken</p>}
                {profileUsernameAvailable === true && <p className="text-xs text-green-400 mt-1">URL is available!</p>}
              </div>
            </div>

            <div className="p-5 border-t border-white/10">
              <button
                disabled={profileSaving || profileUsernameAvailable === false || profileCheckingUsername}
                onClick={async () => {
                  if (!talentProfile?.id) return;
                  const usernameChanged = profileEditUsername && profileEditUsername !== talentProfile.username;
                  if (usernameChanged && profileUsernameAvailable === null) {
                    toast.error('Please wait for URL availability check');
                    return;
                  }
                  setProfileSaving(true);
                  try {
                    const profileUpdates: Record<string, unknown> = {
                      full_name: profileEditName,
                      temp_avatar_url: profileEditAvatar,
                    };
                    if (usernameChanged && profileUsernameAvailable) {
                      profileUpdates.username = profileEditUsername;
                    }
                    await supabase.rpc('update_talent_profile', {
                      p_id: talentProfile.id,
                      p_updates: profileUpdates,
                    });
                    if (bioSettings?.id) {
                      await supabase.rpc('update_bio_settings', {
                        p_id: bioSettings.id,
                        p_updates: {
                          display_name: profileEditName,
                          profile_image_url: profileEditAvatar,
                        },
                      });
                      setBioSettings(prev => prev ? { ...prev, display_name: profileEditName, profile_image_url: profileEditAvatar } : prev);
                    }
                    setTalentProfile(prev => prev ? {
                      ...prev,
                      full_name: profileEditName,
                      temp_avatar_url: profileEditAvatar,
                      ...(usernameChanged && profileUsernameAvailable ? { username: profileEditUsername } : {}),
                    } : prev);
                    toast.success('Profile updated!');
                    setShowProfileModal(false);
                    setTimeout(refreshPreview, 500);
                  } catch (err) {
                    console.error('Failed to save profile:', err);
                    toast.error('Failed to save profile');
                  }
                  setProfileSaving(false);
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium py-3 rounded-xl hover:from-purple-500 hover:to-blue-500 transition-all disabled:opacity-50"
              >
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewsletterModal && (
        <NewsletterModal
          configs={newsletterConfigs}
          talentId={talentProfile?.id || ''}
          onClose={() => setShowNewsletterModal(false)}
          onSave={setNewsletterConfigs}
        />
      )}

      {/* Stream Channel Selection Modal */}
      {showStreamChannelModal && (
        <StreamChannelModal
          platform={showStreamChannelModal}
          socialLinks={socialLinks}
          currentHandle={showStreamChannelModal === 'rumble' ? talentProfile?.rumble_handle : talentProfile?.youtube_handle}
          currentRumbleType={talentProfile?.rumble_type}
          onClose={() => setShowStreamChannelModal(null)}
          onSelect={async (handle, rumbleType) => {
            const platform = showStreamChannelModal;
            console.log('StreamChannelModal onSelect called:', { platform, handle, rumbleType, talentId: talentProfile?.id });
            
            const updateData: Record<string, unknown> = {};
            
            if (platform === 'rumble') {
              updateData.rumble_handle = handle;
              if (rumbleType) updateData.rumble_type = rumbleType;
              // Clear rumble cache when handle changes
              await supabase
                .from('rumble_cache')
                .delete()
                .eq('talent_id', talentProfile?.id);
            } else {
              updateData.youtube_handle = handle;
              // Clear YouTube cache when handle changes so fresh data is fetched
              await supabase
                .from('youtube_cache')
                .delete()
                .eq('talent_id', talentProfile?.id);
            }
            
            console.log('Updating talent_profiles with:', updateData);
            
            const { error } = await supabase.rpc('update_talent_profile', {
              p_id: talentProfile?.id,
              p_updates: updateData,
            });
            
            if (error) {
              console.error('Error updating talent profile:', error);
              toast.error('Failed to update channel');
              return;
            }
            
            console.log('Successfully updated talent_profiles');
            
            setTalentProfile(prev => prev ? { 
              ...prev, 
              ...(platform === 'rumble' 
                ? { rumble_handle: handle, rumble_type: rumbleType } 
                : { youtube_handle: handle }
              )
            } : prev);
            toast.success(`${platform === 'rumble' ? 'Rumble' : 'YouTube'} channel updated`);
            setTimeout(refreshPreview, 500);
            setShowStreamChannelModal(null);
          }}
          onAddNew={async (handle, rumbleType) => {
            // Add to social links
            const platform = showStreamChannelModal as 'rumble' | 'youtube';
            const newSocial: SocialAccount = {
              id: `social-${Date.now()}`,
              platform: platform,
              handle: handle,
            };
            const updated = [...socialLinks, newSocial];
            setSocialLinks(updated);
            
            // Update talent profile with social accounts and the handle
            const updateData: Record<string, unknown> = { social_accounts: updated };
            if (platform === 'rumble') {
              updateData.rumble_handle = handle;
              if (rumbleType) updateData.rumble_type = rumbleType;
              // Clear rumble cache when handle changes
              await supabase
                .from('rumble_cache')
                .delete()
                .eq('talent_id', talentProfile?.id);
            } else {
              updateData.youtube_handle = handle;
              // Clear YouTube cache when handle changes so fresh data is fetched
              await supabase
                .from('youtube_cache')
                .delete()
                .eq('talent_id', talentProfile?.id);
            }
            
            await supabase.rpc('update_talent_profile', {
              p_id: talentProfile?.id,
              p_updates: updateData,
            });
            
            setTalentProfile(prev => prev ? { 
              ...prev, 
              ...(platform === 'rumble' 
                ? { rumble_handle: handle, rumble_type: rumbleType } 
                : { youtube_handle: handle }
              )
            } : prev);
            toast.success(`${platform === 'rumble' ? 'Rumble' : 'YouTube'} channel added`);
            setTimeout(refreshPreview, 500);
            setShowStreamChannelModal(null);
          }}
        />
      )}

      {/* Podcast Modal */}
      {showPodcastModal && (
        <PodcastModal
          currentRssUrl={talentProfile?.podcast_rss_url}
          currentPodcastName={talentProfile?.podcast_name}
          currentPlatformLinks={talentProfile?.podcast_platform_links}
          onClose={() => setShowPodcastModal(false)}
          onSave={async (rssUrl, podcastName, platformLinks) => {
            await supabase.rpc('update_talent_profile', {
              p_id: talentProfile?.id,
              p_updates: { 
                podcast_rss_url: rssUrl, 
                podcast_name: podcastName,
                podcast_platform_links: platformLinks 
              },
            });
            
            // Also enable the podcast card in bio_settings
            if (bioSettings?.id) {
              await supabase.rpc('update_bio_settings', {
                p_id: bioSettings.id,
                p_updates: { show_podcast_card: true },
              });
              setBioSettings({ ...bioSettings, show_podcast_card: true });
            }
            
            setTalentProfile(prev => prev ? { 
              ...prev, 
              podcast_rss_url: rssUrl,
              podcast_name: podcastName,
              podcast_platform_links: platformLinks
            } : prev);
            toast.success('Podcast settings saved');
            setTimeout(refreshPreview, 500);
            setShowPodcastModal(false);
          }}
          onRemove={async () => {
            await supabase.rpc('update_talent_profile', {
              p_id: talentProfile?.id,
              p_updates: { podcast_rss_url: null, podcast_name: null, podcast_platform_links: null },
            });
            
            // Also disable the podcast card in bio_settings
            if (bioSettings?.id) {
              await supabase.rpc('update_bio_settings', {
                p_id: bioSettings.id,
                p_updates: { show_podcast_card: false },
              });
              setBioSettings({ ...bioSettings, show_podcast_card: false });
            }
            
            setTalentProfile(prev => prev ? { 
              ...prev, 
              podcast_rss_url: undefined,
              podcast_name: undefined,
              podcast_platform_links: undefined
            } : prev);
            toast.success('Podcast removed');
            setTimeout(refreshPreview, 500);
            setShowPodcastModal(false);
          }}
        />
      )}

      {showImportModal && (
        <ImportModal
          onClose={() => setShowImportModal(false)}
          onImport={importFromUrl}
        />
      )}

      {showAddSocialModal && (
        <AddSocialModal
          onClose={() => setShowAddSocialModal(false)}
          onAdd={async (social) => {
            const cleanHandle = social.handle.replace(/^@/, '');
            const handleWithAt = cleanHandle.startsWith('@') ? cleanHandle : `@${cleanHandle}`;
            
            // Check if this platform already exists in social_accounts (from admin)
            const { data: existingSocial } = await supabase
              .from('social_accounts')
              .select('id')
              .eq('talent_id', talentProfile?.id)
              .eq('platform', social.platform)
              .maybeSingle();
            
            let socialId: string;
            
            if (existingSocial) {
              // Platform already exists - update it instead of inserting
              console.log('Platform already exists in social_accounts, updating:', existingSocial.id);
              const { error: updateError } = await supabase
                .from('social_accounts')
                .update({ handle: handleWithAt })
                .eq('id', existingSocial.id);
              
              if (updateError) {
                console.error('Failed to update existing social_accounts:', updateError);
                socialId = `profile-${social.platform}`;
              } else {
                socialId = existingSocial.id;
                console.log('Updated existing social_accounts entry');
              }
            } else {
              // Try to insert into social_accounts table
              const { data: insertedSocial, error: insertError } = await supabase
                .from('social_accounts')
                .insert({
                  talent_id: talentProfile?.id,
                  platform: social.platform,
                  handle: handleWithAt,
                })
                .select()
                .single();
              
              if (insertError) {
                console.warn('Could not insert into social_accounts (likely RLS), will use profile fields:', insertError);
                // Use a temporary ID if insert fails (due to RLS)
                socialId = `profile-${social.platform}`;
              } else {
                // Use the real DB ID if insert succeeded
                socialId = insertedSocial.id;
                console.log('Inserted new social_accounts entry:', socialId);
              }
            }
            
            // Add to local state
            const newSocial: SocialAccount = {
              id: socialId,
              platform: social.platform as SocialAccount['platform'],
              handle: social.handle,
            };
            const updated = [...socialLinks, newSocial];
            setSocialLinks(updated);
            
            // Build the update object for talent_profiles (this is the important part)
            const updateData: Record<string, unknown> = {};
            // Note: cleanHandle and handleWithAt are already declared above
            
            // Map ALL platforms to their corresponding talent_profiles fields
            switch (social.platform) {
              case 'twitter':
                updateData.twitter_handle = cleanHandle;
                setTalentProfile(prev => prev ? { ...prev, twitter_handle: cleanHandle } : prev);
                break;
              case 'instagram':
                updateData.instagram_handle = cleanHandle;
                setTalentProfile(prev => prev ? { ...prev, instagram_handle: cleanHandle } : prev);
                break;
              case 'facebook':
                updateData.facebook_handle = cleanHandle;
                setTalentProfile(prev => prev ? { ...prev, facebook_handle: cleanHandle } : prev);
                break;
              case 'tiktok':
                updateData.tiktok_handle = cleanHandle;
                setTalentProfile(prev => prev ? { ...prev, tiktok_handle: cleanHandle } : prev);
                break;
              case 'youtube':
                updateData.youtube_handle = cleanHandle;
                setTalentProfile(prev => prev ? { ...prev, youtube_handle: cleanHandle } : prev);
                // Clear YouTube cache
                if (talentProfile?.id) {
                  await supabase
                    .from('youtube_cache')
                    .delete()
                    .eq('talent_id', talentProfile.id);
                  console.log('Cleared youtube cache for talent:', talentProfile.id);
                }
                break;
              case 'rumble':
                updateData.rumble_handle = cleanHandle;
                updateData.rumble_type = social.rumble_type || 'c';
                setTalentProfile(prev => prev ? { 
                  ...prev, 
                  rumble_handle: cleanHandle,
                  rumble_type: social.rumble_type || 'c'
                } : prev);
                // Clear rumble cache
                if (talentProfile?.id) {
                  await supabase
                    .from('rumble_cache')
                    .delete()
                    .eq('talent_id', talentProfile.id);
                  console.log('Cleared rumble cache for talent:', talentProfile.id);
                }
                // Auto-enable the Rumble card
                if (bioSettings?.id) {
                  await supabase.rpc('update_bio_settings', {
                    p_id: bioSettings.id,
                    p_updates: { show_rumble_card: true },
                  });
                  setBioSettings({ ...bioSettings, show_rumble_card: true });
                }
                break;
              case 'snapchat':
                updateData.snapchat_handle = cleanHandle;
                setTalentProfile(prev => prev ? { ...prev, snapchat_handle: cleanHandle } : prev);
                break;
            }
            
            // CRITICAL: Check if this platform already exists in social_accounts table
            // If it does, we need to update that row, otherwise the old handle will show on refresh
            const { data: existingSocialInDb } = await supabase
              .from('social_accounts')
              .select('id')
              .eq('talent_id', talentProfile?.id)
              .eq('platform', social.platform)
              .maybeSingle();
            
            if (existingSocialInDb) {
              // Update the existing social_accounts entry
              console.log('Updating existing social_accounts entry:', existingSocialInDb.id, 'with handle:', handleWithAt);
              const { error: updateSocialError } = await supabase
                .from('social_accounts')
                .update({ handle: handleWithAt })
                .eq('id', existingSocialInDb.id);
              
              if (updateSocialError) {
                console.error('Failed to update social_accounts:', updateSocialError);
              } else {
                console.log('Successfully updated social_accounts entry');
              }
            } else {
              console.log('No existing social_accounts entry found for platform:', social.platform);
            }
            
            // Always update talent_profiles with the handle fields (this is critical for persistence)
            console.log('Updating talent_profiles with:', updateData, 'for talent ID:', talentProfile?.id);
            const { error } = await supabase.rpc('update_talent_profile', {
              p_id: talentProfile?.id,
              p_updates: updateData,
            });
            
            console.log('Update via RPC, error:', error);
            
            if (error) {
              console.error('Failed to update talent profile:', error);
              toast.error('Failed to save social link');
            } else {
              console.log('Talent profile updated successfully');
              toast.success('Social link added!');
            }
            
            setTimeout(refreshPreview, 500);
            setShowAddSocialModal(false);
          }}
          existingPlatforms={socialLinks.map(s => s.platform)}
        />
      )}

      {/* Donate Enable Modal */}
      {showDonateModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowDonateModal(false)}>
          <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-2">Fan Donations</h3>
            <p className="text-gray-400 text-sm mb-4">
              Let fans support you with one-time or monthly donations, and optionally send you a public message along with their donation. Monthly supporters get <span className="text-green-400 font-medium">10% off</span> your shoutouts, which incentivizes recurring support.
            </p>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-4 space-y-2 text-xs text-gray-300">
              <div className="flex items-center gap-2"><span className="text-green-400">✓</span> Fans can donate any amount</div>
              <div className="flex items-center gap-2"><span className="text-green-400">✓</span> Fans can send a public message with their donation</div>
              <div className="flex items-center gap-2"><span className="text-green-400">✓</span> Monthly donations encouraged</div>
              <div className="flex items-center gap-2"><span className="text-green-400">✓</span> Monthly supporters get 10% off shoutouts</div>
              <div className="flex items-center gap-2"><span className="text-green-400">✓</span> 5% processing fee charged to donor</div>
            </div>
            <div className="flex gap-3">
              {talentProfile?.support_enabled ? (
                <button
                  onClick={async () => {
                    setTalentProfile(prev => prev ? { ...prev, support_enabled: false, fan_shoutouts_enabled: false } : prev);
                    await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { support_enabled: false, fan_shoutouts_enabled: false } });
                    toast.success('Donations disabled');
                    setShowDonateModal(false);
                    setTimeout(refreshPreview, 500);
                  }}
                  className="flex-1 py-2.5 rounded-xl font-medium text-red-400 bg-red-500/10 border border-red-500/20 hover:bg-red-500/20"
                >Disable</button>
              ) : null}
              <button
                onClick={async () => {
                  if (!talentProfile?.support_enabled) {
                    setTalentProfile(prev => prev ? { ...prev, support_enabled: true } : prev);
                    await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { support_enabled: true } });
                    toast.success('Donations enabled!');
                    setTimeout(refreshPreview, 500);
                  }
                  setShowDonateModal(false);
                }}
                className="flex-1 py-2.5 rounded-xl font-medium text-white bg-green-600 hover:bg-green-700"
              >{talentProfile?.support_enabled ? 'Done' : 'Enable Donations'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Social Management Modal */}
      {showSocialManageModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowSocialManageModal(false)}>
          <div className="bg-[#1a1a2e] rounded-2xl w-full max-w-md p-6 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Social Links</h3>
              <button onClick={() => setShowSocialManageModal(false)} className="text-gray-400 hover:text-white"><XMarkIcon className="h-5 w-5" /></button>
            </div>
            <div className="space-y-2 mb-4">
              {socialLinks.map((social, i) => (
                <div key={social.id || i} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm capitalize text-white">{social.platform}</span>
                    <span className="text-gray-400 text-xs">{social.handle}</span>
                  </div>
                  <button
                    onClick={async () => {
                      const updated = socialLinks.filter((_, idx) => idx !== i);
                      setSocialLinks(updated);
                      await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { social_accounts: updated } });
                      // Delete from social_accounts table by talent+platform
                      try { await supabase.from('social_accounts').delete().eq('talent_id', talentProfile?.id).eq('platform', social.platform); } catch {}
                      // Clear the corresponding handle column in talent_profiles so it doesn't get re-synced
                      const handleFieldMap: Record<string, string> = {
                        twitter: 'twitter_handle', instagram: 'instagram_handle', facebook: 'facebook_handle',
                        tiktok: 'tiktok_handle', youtube: 'youtube_handle', rumble: 'rumble_handle', snapchat: 'snapchat_handle',
                      };
                      const handleField = handleFieldMap[social.platform];
                      if (handleField) {
                        await supabase.rpc('update_talent_profile', { p_id: talentProfile?.id, p_updates: { [handleField]: '' } });
                      }
                      setPreviewKey(prev => prev + 1);
                    }}
                    className="text-red-400 text-xs hover:text-red-300"
                  >Remove</button>
                </div>
              ))}
              {socialLinks.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No social links added</p>}
            </div>
            <button
              onClick={() => { setShowSocialManageModal(false); setShowAddSocialModal(true); }}
              className="w-full py-2.5 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >+ Add Social Link</button>
          </div>
        </div>
      )}

      {(showAddServiceModal || editingService) && (
        <AddServiceModal
          service={editingService || undefined}
          socialLinks={socialLinks}
          forcedType={serviceModalType || undefined}
          onUpdateFollowerCount={async (socialId: string, count: number) => {
            console.log('onUpdateFollowerCount called:', { socialId, count });
            
            // Update local state
            setSocialLinks(prev => prev.map(s => 
              s.id === socialId ? { ...s, follower_count: count } : s
            ));
            
            // Check if this is a temporary ID (from talent_profiles) or a real database ID
            if (socialId.startsWith('profile-')) {
              // This is a temporary ID - need to find or create by platform and talent_id
              const platform = socialId.replace('profile-', '');
              console.log('Profile-based social, platform:', platform);
              const social = socialLinks.find(s => s.id === socialId);
              if (social && talentProfile?.id) {
                // First check if a record exists in social_accounts
                const { data: existing, error: lookupError } = await supabase
                  .from('social_accounts')
                  .select('id')
                  .eq('talent_id', talentProfile.id)
                  .eq('platform', platform)
                  .single();
                
                console.log('Lookup result:', { existing, lookupError });
                
                if (existing) {
                  // Update existing record in social_accounts
                  const { error } = await supabase
                    .from('social_accounts')
                    .update({ follower_count: count })
                    .eq('id', existing.id);
                  
                  if (error) {
                    console.error('Failed to save follower count:', error);
                    toast.error('Failed to save follower count');
                  } else {
                    toast.success('Follower count saved');
                    // Update the socialId in local state to use the real ID
                    setSocialLinks(prev => prev.map(s => 
                      s.id === socialId ? { ...s, id: existing.id, follower_count: count } : s
                    ));
                  }
                } else {
                  // No record in social_accounts - save to talent_profiles.follower_counts instead
                  console.log('No social_accounts record found, saving to talent_profiles.follower_counts for platform:', platform);
                  const currentCounts = (talentProfile as any).follower_counts || {};
                  console.log('Current follower_counts:', currentCounts);
                  const updatedCounts = { ...currentCounts, [platform]: count };
                  console.log('Updated follower_counts to save:', updatedCounts);
                  
                  const { error } = await supabase.rpc('update_talent_profile', {
                    p_id: talentProfile.id,
                    p_updates: { follower_counts: updatedCounts },
                  });
                  
                  if (error) {
                    console.error('Failed to save follower count to profile:', error);
                    toast.error('Failed to save follower count');
                  } else {
                    toast.success('Follower count saved');
                    // Update local talentProfile state
                    setTalentProfile(prev => prev ? { ...prev, follower_counts: updatedCounts } as any : null);
                  }
                }
              }
            } else {
              // This is a real database ID - update directly
              console.log('Updating social_accounts directly with ID:', socialId, 'count:', count);
              const { data, error } = await supabase
                .from('social_accounts')
                .update({ follower_count: count })
                .eq('id', socialId)
                .select();
              
              console.log('Update result:', { data, error });
              
              if (error) {
                console.error('Failed to save follower count:', error);
                toast.error('Failed to save follower count');
              } else if (!data || data.length === 0) {
                // ID doesn't exist in social_accounts - find the platform and save to talent_profiles
                console.log('ID not in social_accounts, finding platform from local state...');
                const social = socialLinks.find(s => s.id === socialId);
                if (social && talentProfile?.id) {
                  console.log('Found platform:', social.platform, '- saving to talent_profiles.follower_counts');
                  const currentCounts = (talentProfile as any).follower_counts || {};
                  const updatedCounts = { ...currentCounts, [social.platform]: count };
                  
                  const { error: profileError } = await supabase.rpc('update_talent_profile', { p_id: talentProfile.id, p_updates: { follower_counts: updatedCounts } });
                  
                  if (profileError) {
                    console.error('Failed to save to talent_profiles:', profileError);
                    toast.error('Failed to save follower count');
                  } else {
                    console.log('Saved to talent_profiles.follower_counts:', updatedCounts);
                    toast.success('Follower count saved');
                    setTalentProfile(prev => prev ? { ...prev, follower_counts: updatedCounts } as any : null);
                  }
                } else {
                  toast.error('Failed to save - account not found');
                }
              } else {
                console.log('Successfully updated follower count:', data);
                toast.success('Follower count saved');
              }
            }
          }}
          onClose={() => {
            setShowAddServiceModal(false);
            setEditingService(null);
          }}
          onSave={async (service) => {
            if (editingService) {
              // Update existing service
              const { error } = await supabase.rpc('update_service_offering', {
                p_id: editingService.id,
                p_data: {
                  title: service.title,
                  pricing: service.pricing,
                  video_length_seconds: service.video_length_seconds,
                  benefits: service.benefits,
                  platforms: service.platforms,
                  description: service.description,
                  is_active: service.is_active,
                  total_followers: service.total_followers || 0,
                  coupon_code: service.coupon_code,
                  coupon_discount: service.coupon_discount_amount,
                  coupon_discount_type: service.coupon_discount_type,
                  is_recurring: service.is_recurring,
                  contact_email: service.contact_email,
                },
              });
              
              if (error) {
                toast.error('Failed to update service');
                return;
              }
              
              setServiceOfferings(serviceOfferings.map(s => 
                s.id === editingService.id ? { ...s, ...service } : s
              ));
              toast.success('Service updated!');
            } else {
              // Create new service
              const { data, error } = await supabase.rpc('create_service_offering', {
                p_data: {
                  talent_id: talentProfile?.id,
                  service_type: service.service_type,
                  title: service.title,
                  pricing: service.pricing,
                  video_length_seconds: service.video_length_seconds,
                  benefits: service.benefits,
                  platforms: service.platforms,
                  description: service.description,
                  is_active: service.is_active,
                  display_order: serviceOfferings.length,
                  total_followers: service.total_followers || 0,
                  coupon_code: service.coupon_code,
                  coupon_discount: service.coupon_discount_amount,
                  coupon_discount_type: service.coupon_discount_type,
                  is_recurring: service.is_recurring,
                  contact_email: service.contact_email,
                },
              });
              
              if (error) {
                toast.error('Failed to create service');
                return;
              }
              
              setServiceOfferings([...serviceOfferings, { ...(data as any), benefits: service.benefits, platforms: service.platforms }]);
              toast.success('Service created!');
            }
            
            setShowAddServiceModal(false);
            setEditingService(null);
            setTimeout(refreshPreview, 500);
          }}
        />
      )}

      {showAddEventModal && talentProfile && (
        <AddEventModal
          events={bioEvents}
          talentId={talentProfile.id}
          onClose={() => {
            setShowAddEventModal(false);
            setTimeout(refreshPreview, 500);
          }}
          onEventsChange={(newEvents) => {
            setBioEvents(newEvents);
          }}
        />
      )}

      {/* Creator Chat - hidden for demo account */}
      {talentProfile && (() => {
        const sess = localStorage.getItem('creator_session');
        const isDemoAccount = sess && JSON.parse(sess).email === 'applereview@shoutout.us';
        if (isDemoAccount) return null;
        return (
          <CreatorChat
            talentId={talentProfile.id}
            talentName={talentProfile.full_name || 'Creator'}
            talentAvatar={talentProfile.temp_avatar_url}
            isAdmin={chatIsAdmin}
            adminUserId={chatAdminUserId}
            initialChannelId={pushDeepLinkChannelId}
            initialOpen={pushOpenChat}
          />
        );
      })()}

      {/* Download App Button — web only */}
      {!Capacitor.isNativePlatform() && (
        <a
          href="https://apps.apple.com/us/app/shoutout-creators/id6762010230"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 px-4 py-2.5 bg-white text-black rounded-full shadow-lg shadow-black/40 hover:bg-gray-100 transition-colors group"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 21.99 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 21.99C7.79 22.03 6.8 20.68 5.96 19.47C4.25 16.99 2.97 12.5 4.7 9.56C5.55 8.1 7.13 7.17 8.82 7.15C10.09 7.13 11.29 8.01 12.05 8.01C12.81 8.01 14.25 6.95 15.76 7.11C16.39 7.14 18.22 7.37 19.4 9.08C19.3 9.14 17.08 10.42 17.1 13.12C17.13 16.34 19.92 17.42 19.95 17.43C19.93 17.49 19.47 19.03 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
          </svg>
          <span className="text-sm font-semibold">Download The Creator App!</span>
          <span className="absolute -top-2 -right-1 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full animate-pulse">
            NEW
          </span>
        </a>
      )}
      </div>{/* end scroll content */}
      </div>{/* end below-header flex row */}
    </div>
  );
};

// Grid link data for carousel
interface GridLinkData {
  title: string;
  url: string;
  thumbnailUrl: string;
}

// Add Link Modal
const AddLinkModal: React.FC<{
  onClose: () => void;
  onAdd: (link: Omit<BioLink, 'id' | 'display_order'>) => void;
  onAddMultiple?: (links: Omit<BioLink, 'id' | 'display_order'>[]) => void;
  talentId: string;
}> = ({ onClose, onAdd, onAddMultiple, talentId }) => {
  const [linkType, setLinkType] = useState<'basic' | 'grid' | 'sponsor' | 'video' | 'affiliate' | 'donate' | 'podcast'>('basic');
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [gridColumns, setGridColumns] = useState(2);
  const [isFeatured, setIsFeatured] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [linkFormat, setLinkFormat] = useState<'thin' | 'tall' | 'square'>('thin');
  const [gridFormat, setGridFormat] = useState<'thin' | 'tall' | 'square'>('square'); // Default to square for grids
  const [expires, setExpires] = useState(false);
  const [expiresDate, setExpiresDate] = useState(''); // yyyy-mm-dd

  // Grid carousel state
  const [currentGridSlot, setCurrentGridSlot] = useState(0);
  const [gridLinks, setGridLinks] = useState<GridLinkData[]>([
    { title: '', url: '', thumbnailUrl: '' },
    { title: '', url: '', thumbnailUrl: '' },
  ]);

  // Update grid links array when columns change
  useEffect(() => {
    if (linkType === 'grid') {
      setGridLinks(prevLinks => {
        const newGridLinks = Array(gridColumns).fill(null).map((_, i) => 
          prevLinks[i] || { title: '', url: '', thumbnailUrl: '' }
        );
        return newGridLinks;
      });
      setCurrentGridSlot(prev => prev >= gridColumns ? gridColumns - 1 : prev);
    }
  }, [gridColumns, linkType]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, slotIndex?: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadImageToWasabi(file, `bio-images/${talentId}`);
      if (result.success && result.imageUrl) {
        if (linkType === 'grid' && slotIndex !== undefined) {
          const newGridLinks = [...gridLinks];
          newGridLinks[slotIndex] = { ...newGridLinks[slotIndex], thumbnailUrl: result.imageUrl };
          setGridLinks(newGridLinks);
        } else {
          setThumbnailUrl(result.imageUrl);
        }
        toast.success('Image uploaded!');
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Helper to auto-add https://
  const ensureHttps = (inputUrl: string) => {
    if (!inputUrl) return inputUrl;
    if (inputUrl.startsWith('http://') || inputUrl.startsWith('https://')) return inputUrl;
    return 'https://' + inputUrl;
  };

  // Auto-add https:// when URL loses focus
  const handleUrlBlur = () => {
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      setUrl('https://' + url);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (linkType === 'podcast') {
      if (!podcastRssUrl) { toast.error('Please enter a podcast RSS URL'); return; }
      try {
        await supabase.rpc('update_talent_profile', {
          p_id: talentId,
          p_updates: { podcast_rss_url: podcastRssUrl },
        });
        toast.success('Podcast added!');
        onClose();
      } catch { toast.error('Failed to add podcast'); }
      return;
    }

    // Compute expiration as end-of-day (UTC) of the chosen date
    const expiresIso = expires && expiresDate
      ? new Date(expiresDate + 'T23:59:59').toISOString()
      : null;
    if (expires && !expiresDate) {
      toast.error('Please pick an expiration date');
      return;
    }

    if (linkType === 'grid' && onAddMultiple) {
      // Add all grid links at once
      const validLinks = gridLinks
        .filter(gl => gl.title || gl.url || gl.thumbnailUrl)
        .map(gl => ({
          talent_id: talentId,
          link_type: 'grid' as const,
          title: gl.title,
          url: ensureHttps(gl.url),
          thumbnail_url: gl.thumbnailUrl || undefined,
          grid_columns: gridColumns,
          is_active: true,
          is_featured: false,
          link_format: gridFormat, // Apply the chosen format to grid cards
          expires_at: expiresIso,
        }));
      
      if (validLinks.length > 0) {
        onAddMultiple(validLinks);
      } else {
        toast.error('Please fill in at least one grid card');
      }
    } else {
      onAdd({
        talent_id: talentId,
        link_type: linkType,
        title: linkType === 'video' ? 'Featured Video' : (linkType === 'affiliate' ? companyName : title),
        url: linkType === 'video' ? undefined : ensureHttps(url),
        video_url: linkType === 'video' ? videoUrl : undefined,
        company_name: linkType === 'affiliate' ? companyName : undefined,
        discount_amount: linkType === 'affiliate' ? discountAmount : undefined,
        discount_code: linkType === 'affiliate' ? discountCode : undefined,
        thumbnail_url: thumbnailUrl || undefined,
        grid_columns: gridColumns,
        is_active: true,
        is_featured: linkType === 'basic' ? isFeatured : false,
        link_format: linkType === 'basic' ? linkFormat : undefined,
        expires_at: expiresIso,
      });
    }
  };

  const updateGridLink = (index: number, field: keyof GridLinkData, value: string) => {
    const newGridLinks = [...gridLinks];
    newGridLinks[index] = { ...newGridLinks[index], [field]: value };
    setGridLinks(newGridLinks);
  };

  const linkTypes = [
    { type: 'basic' as const, label: 'Basic Link', icon: LinkIcon, color: 'blue', desc: 'Simple link with title' },
    { type: 'grid' as const, label: 'Grid Card', icon: Squares2X2Icon, color: 'purple', desc: 'Image card with link' },
    { type: 'video' as const, label: 'Featured Video', icon: VideoCameraIcon, color: 'red', desc: 'Autoplay video card' },
    { type: 'affiliate' as const, label: 'Affiliate Link', icon: TagIcon, color: 'green', desc: 'Brand partnership with discount' },
    { type: 'donate' as const, label: 'Donate/Support', icon: CurrencyDollarIcon, color: 'yellow', desc: 'Payment link badge' },
    { type: 'podcast' as const, label: 'Podcast', icon: MicrophoneIcon, color: 'pink', desc: 'Add your podcast RSS feed' },
  ];
  const [podcastRssUrl, setPodcastRssUrl] = useState('');

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Add New Link</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Link Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-3">Link Type</label>
            <div className="grid grid-cols-2 gap-2">
              {linkTypes.map((lt) => (
                <button
                  key={lt.type}
                  type="button"
                  onClick={() => setLinkType(lt.type)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    linkType === lt.type
                      ? 'border-blue-500 bg-blue-500/20'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <lt.icon className="h-5 w-5 text-blue-400" />
                    <span className="text-sm font-medium text-white">{lt.label}</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{lt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Podcast Fields */}
          {linkType === 'podcast' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Podcast RSS Feed URL *</label>
              <input
                type="url"
                value={podcastRssUrl}
                onChange={(e) => setPodcastRssUrl(e.target.value)}
                placeholder="https://feeds.example.com/your-podcast"
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Your podcast RSS feed URL (from Apple Podcasts, Spotify, etc.)</p>
            </div>
          )}

          {/* Basic Link Fields */}
          {linkType !== 'grid' && linkType !== 'podcast' && (
            <>
              {linkType === 'video' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Video URL *</label>
                  <input
                    type="url"
                    value={videoUrl}
                    onChange={(e) => setVideoUrl(e.target.value)}
                    placeholder="https://example.com/video.mp4"
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Direct link to .mp4, .webm, or .mov file</p>
                </div>
              ) : linkType === 'affiliate' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Company Name *</label>
                    <input
                      type="text"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="Brand Name"
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Affiliate URL *</label>
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      onBlur={handleUrlBlur}
                      placeholder="youraffiliatelink.com"
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Your affiliate/referral link</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Brand Logo/Image *</label>
                    {thumbnailUrl ? (
                      <div className="flex items-center gap-4">
                        <img src={thumbnailUrl} alt="Preview" className="w-20 h-20 rounded-lg object-cover" />
                        <button type="button" onClick={() => setThumbnailUrl('')} className="text-sm text-red-400 hover:text-red-300">
                          Remove
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-white/40 transition-colors">
                        <div className="flex flex-col items-center justify-center">
                          {uploading ? (
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                          ) : (
                            <>
                              <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-400">Upload brand logo</p>
                              <p className="text-xs text-gray-500 mt-1">Square image recommended</p>
                            </>
                          )}
                        </div>
                        <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e)} disabled={uploading} />
                      </label>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Discount <span className="text-gray-500 text-xs">(optional)</span></label>
                      <input
                        type="text"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value)}
                        placeholder="20% OFF"
                        className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Code <span className="text-gray-500 text-xs">(optional)</span></label>
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        placeholder="SAVE20"
                        className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 -mt-2">Badge appears on brand logo (top right)</p>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="My Website"
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      onBlur={handleUrlBlur}
                      placeholder="example.com"
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">https:// will be added automatically</p>
                  </div>
                </>
              )}

              {/* Image Upload for basic links */}
              {linkType === 'basic' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Icon/Image (optional)</label>
                  
                  {thumbnailUrl ? (
                    <div className="flex items-center gap-4">
                      <img src={thumbnailUrl} alt="Preview" className="w-16 h-16 rounded-lg object-cover" />
                      <button type="button" onClick={() => setThumbnailUrl('')} className="text-sm text-red-400 hover:text-red-300">
                        Remove
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-white/40 transition-colors">
                      <div className="flex flex-col items-center justify-center">
                        {uploading ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        ) : (
                          <>
                            <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mb-1" />
                            <p className="text-xs text-gray-400">Upload image</p>
                          </>
                        )}
                      </div>
                      <input type="file" className="hidden" accept="image/*" onChange={(e) => handleImageUpload(e)} disabled={uploading} />
                    </label>
                  )}
                </div>
              )}

              {/* Link Format for basic links */}
              {linkType === 'basic' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Link Format</label>
                  <div className="grid grid-cols-3 gap-3">
                    {/* Thin */}
                    <button
                      type="button"
                      onClick={() => setLinkFormat('thin')}
                      className={`p-3 rounded-xl transition-all ${
                        linkFormat === 'thin'
                          ? 'bg-blue-500/20 border-2 border-blue-500'
                          : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-full h-6 bg-current opacity-40 rounded flex items-center px-2">
                          <div className="w-3 h-3 bg-white/60 rounded mr-2" />
                          <div className="flex-1 h-2 bg-white/40 rounded" />
                        </div>
                        <span className="text-xs text-gray-300">Thin</span>
                      </div>
                    </button>
                    
                    {/* Tall */}
                    <button
                      type="button"
                      onClick={() => setLinkFormat('tall')}
                      className={`p-3 rounded-xl transition-all ${
                        linkFormat === 'tall'
                          ? 'bg-blue-500/20 border-2 border-blue-500'
                          : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-full h-12 bg-current opacity-40 rounded flex items-center px-2">
                          <div className="w-8 h-8 bg-white/60 rounded mr-2" />
                          <div className="flex-1">
                            <div className="h-2 bg-white/40 rounded mb-1" />
                            <div className="h-1.5 bg-white/20 rounded w-2/3" />
                          </div>
                        </div>
                        <span className="text-xs text-gray-300">Tall</span>
                      </div>
                    </button>
                    
                    {/* Square (no title) */}
                    <button
                      type="button"
                      onClick={() => setLinkFormat('square')}
                      className={`p-3 rounded-xl transition-all ${
                        linkFormat === 'square'
                          ? 'bg-blue-500/20 border-2 border-blue-500'
                          : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-10 h-10 bg-current opacity-40 rounded flex items-center justify-center">
                          <div className="w-6 h-6 bg-white/60 rounded" />
                        </div>
                        <span className="text-xs text-gray-300">Square</span>
                      </div>
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    {linkFormat === 'square' ? 'Image only, no title displayed' : 'Choose how your link appears'}
                  </p>
                </div>
              )}

              {/* Featured option for basic links */}
              {linkType === 'basic' && (
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                    className="w-5 h-5 rounded bg-white/10 border-white/20 text-yellow-500 focus:ring-yellow-500"
                  />
                  <div className="flex items-center gap-2">
                    <span className="text-white">Featured link</span>
                    <span className="text-yellow-400">⭐</span>
                  </div>
                  <span className="text-xs text-gray-400">(gradient highlight)</span>
                </label>
              )}
            </>
          )}

          {/* Grid Link Fields with Carousel */}
          {linkType === 'grid' && (
            <>
              {/* Grid Layout Selection - Only 2 or 3 columns */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Grid Layout</label>
                <div className="grid grid-cols-2 gap-3">
                  {/* 2 Columns (1x2) */}
                  <button
                    type="button"
                    onClick={() => setGridColumns(2)}
                    className={`p-3 rounded-xl transition-all ${
                      gridColumns === 2 ? 'bg-blue-500/20 border-2 border-blue-500' : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-1 mb-2">
                      <div className="aspect-square bg-current opacity-40 rounded" />
                      <div className="aspect-square bg-current opacity-40 rounded" />
                    </div>
                    <div className="text-xs text-center text-gray-300">1×2</div>
                  </button>
                  
                  {/* 4 Columns (2x2) */}
                  <button
                    type="button"
                    onClick={() => setGridColumns(4)}
                    className={`p-3 rounded-xl transition-all ${
                      gridColumns === 4 ? 'bg-blue-500/20 border-2 border-blue-500' : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="grid grid-cols-2 gap-1 mb-2">
                      <div className="aspect-square bg-current opacity-40 rounded" />
                      <div className="aspect-square bg-current opacity-40 rounded" />
                      <div className="aspect-square bg-current opacity-40 rounded" />
                      <div className="aspect-square bg-current opacity-40 rounded" />
                    </div>
                    <div className="text-xs text-center text-gray-300">2×2</div>
                  </button>
                </div>
              </div>

              {/* Card Format Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Card Format</label>
                <div className="grid grid-cols-3 gap-3">
                  {/* Thin */}
                  <button
                    type="button"
                    onClick={() => setGridFormat('thin')}
                    className={`p-3 rounded-xl transition-all ${
                      gridFormat === 'thin'
                        ? 'bg-purple-500/20 border-2 border-purple-500'
                        : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-full h-6 bg-current opacity-40 rounded flex items-center px-2">
                        <div className="w-3 h-3 bg-white/60 rounded mr-2" />
                        <div className="flex-1 h-2 bg-white/40 rounded" />
                      </div>
                      <span className="text-xs text-gray-300">Thin</span>
                    </div>
                  </button>
                  
                  {/* Tall */}
                  <button
                    type="button"
                    onClick={() => setGridFormat('tall')}
                    className={`p-3 rounded-xl transition-all ${
                      gridFormat === 'tall'
                        ? 'bg-purple-500/20 border-2 border-purple-500'
                        : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-full h-12 bg-current opacity-40 rounded overflow-hidden">
                        <div className="w-full h-6 bg-white/60" />
                        <div className="px-2 py-1">
                          <div className="h-1.5 bg-white/40 rounded w-2/3" />
                        </div>
                      </div>
                      <span className="text-xs text-gray-300">Tall</span>
                    </div>
                  </button>
                  
                  {/* Square (no title) */}
                  <button
                    type="button"
                    onClick={() => setGridFormat('square')}
                    className={`p-3 rounded-xl transition-all ${
                      gridFormat === 'square'
                        ? 'bg-purple-500/20 border-2 border-purple-500'
                        : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 bg-current opacity-40 rounded flex items-center justify-center">
                        <div className="w-6 h-6 bg-white/60 rounded" />
                      </div>
                      <span className="text-xs text-gray-300">Square</span>
                    </div>
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {gridFormat === 'square' ? 'Image only, no title displayed' : 
                   gridFormat === 'tall' ? 'Image with title below' : 'Compact card with title'}
                </p>
              </div>

              {/* Carousel Navigation */}
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-300">
                  Card {currentGridSlot + 1} of {gridColumns}
                </span>
                <div className="flex items-center gap-2">
                  {Array(gridColumns).fill(null).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCurrentGridSlot(i)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        currentGridSlot === i ? 'bg-blue-500 w-6' : 'bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Carousel Card Editor */}
              <div className="relative">
                <div className="flex items-center">
                  {/* Left Arrow */}
                  <button
                    type="button"
                    onClick={() => setCurrentGridSlot(Math.max(0, currentGridSlot - 1))}
                    disabled={currentGridSlot === 0}
                    className={`p-2 rounded-lg mr-2 transition-colors ${
                      currentGridSlot === 0 ? 'text-gray-600 cursor-not-allowed' : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <ChevronLeftIcon className="h-6 w-6" />
                  </button>

                  {/* Card Content */}
                  <div className="flex-1 bg-white/5 border border-white/20 rounded-xl p-4 space-y-4">
                    <div className="text-center text-sm font-medium text-blue-400 mb-3">
                      Grid Card {currentGridSlot + 1}
                    </div>

                    {/* Image Upload */}
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-2">Image</label>
                      {gridLinks[currentGridSlot]?.thumbnailUrl ? (
                        <div className="flex items-center gap-3">
                          <img 
                            src={gridLinks[currentGridSlot].thumbnailUrl} 
                            alt="Preview" 
                            className="w-20 h-20 rounded-lg object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => updateGridLink(currentGridSlot, 'thumbnailUrl', '')}
                            className="text-xs text-red-400 hover:text-red-300"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-white/40 transition-colors">
                          {uploading ? (
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                          ) : (
                            <>
                              <CloudArrowUpIcon className="h-8 w-8 text-gray-400 mb-1" />
                              <p className="text-xs text-gray-400">Upload image</p>
                            </>
                          )}
                          <input 
                            type="file" 
                            className="hidden" 
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, currentGridSlot)}
                            disabled={uploading}
                          />
                        </label>
                      )}
                    </div>

                    {/* Title */}
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">Title</label>
                      <input
                        type="text"
                        value={gridLinks[currentGridSlot]?.title || ''}
                        onChange={(e) => updateGridLink(currentGridSlot, 'title', e.target.value)}
                        placeholder="Card title"
                        className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      />
                    </div>

                    {/* URL */}
                    <div>
                      <label className="block text-xs font-medium text-gray-400 mb-1">URL</label>
                      <input
                        type="url"
                        value={gridLinks[currentGridSlot]?.url || ''}
                        onChange={(e) => updateGridLink(currentGridSlot, 'url', e.target.value)}
                        placeholder="https://example.com"
                        className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  {/* Right Arrow */}
                  <button
                    type="button"
                    onClick={() => setCurrentGridSlot(Math.min(gridColumns - 1, currentGridSlot + 1))}
                    disabled={currentGridSlot === gridColumns - 1}
                    className={`p-2 rounded-lg ml-2 transition-colors ${
                      currentGridSlot === gridColumns - 1 ? 'text-gray-600 cursor-not-allowed' : 'text-white hover:bg-white/10'
                    }`}
                  >
                    <ChevronRightIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Preview of all cards */}
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-2">Preview</label>
                <div className="grid grid-cols-2 gap-2">
                  {gridLinks.slice(0, gridColumns).map((gl, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCurrentGridSlot(i)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        currentGridSlot === i ? 'border-blue-500' : 'border-white/10'
                      }`}
                    >
                      {gl.thumbnailUrl ? (
                        <div className="relative w-full h-full">
                          <img src={gl.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                          {gl.title && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5">
                              <p className="text-[8px] text-white truncate">{gl.title}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="w-full h-full bg-white/5 flex items-center justify-center">
                          <span className="text-xs text-gray-500">{i + 1}</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {linkType !== 'podcast' && (
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">Expiring link</p>
                  <p className="text-gray-400 text-xs mt-0.5">Auto-remove the link on a specific date.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const next = !expires;
                    setExpires(next);
                    if (next && !expiresDate) {
                      const d = new Date();
                      d.setDate(d.getDate() + 7);
                      setExpiresDate(d.toISOString().slice(0, 10));
                    }
                  }}
                  className={`w-12 h-6 rounded-full transition-colors ${expires ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${expires ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {expires && (
                <div className="mt-3">
                  <label className="block text-xs text-gray-400 mb-1">Removes on</label>
                  <input
                    type="date"
                    value={expiresDate}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setExpiresDate(e.target.value)}
                    className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors"
            >
              {linkType === 'grid' ? `Add ${gridColumns} Card${gridColumns > 1 ? 's' : ''}` : 'Add Link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Link Modal
const EditLinkModal: React.FC<{
  link: BioLink;
  onClose: () => void;
  onSave: (link: BioLink) => void;
}> = ({ link, onClose, onSave }) => {
  const [title, setTitle] = useState(link.title || '');
  const [url, setUrl] = useState(link.url || '');
  const [thumbnailUrl, setThumbnailUrl] = useState(link.thumbnail_url || link.image_url || '');
  const [gridColumns, setGridColumns] = useState(link.grid_columns || 2);
  const [isActive, setIsActive] = useState(link.is_active);
  const [isFeatured, setIsFeatured] = useState(link.is_featured || false);
  const [linkFormat, setLinkFormat] = useState<'thin' | 'tall' | 'square'>(link.link_format as 'thin' | 'tall' | 'square' || 'thin');
  const [uploading, setUploading] = useState(false);
  // Affiliate-specific fields
  const [companyName, setCompanyName] = useState(link.company_name || '');
  const [discountAmount, setDiscountAmount] = useState(link.discount_amount || '');
  const [discountCode, setDiscountCode] = useState(link.discount_code || '');
  // Expiration
  const [expires, setExpires] = useState(!!link.expires_at);
  const [expiresDate, setExpiresDate] = useState(link.expires_at ? new Date(link.expires_at).toISOString().slice(0, 10) : '');

  // Auto-add https:// when URL loses focus
  const handleUrlBlur = () => {
    if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
      setUrl('https://' + url);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadImageToWasabi(file, `bio-images/${link.talent_id}`);
      if (result.success && result.imageUrl) {
        setThumbnailUrl(result.imageUrl);
        toast.success('Image uploaded!');
      } else {
        toast.error(result.error || 'Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (expires && !expiresDate) {
      toast.error('Please pick an expiration date');
      return;
    }
    const expiresIso = expires && expiresDate
      ? new Date(expiresDate + 'T23:59:59').toISOString()
      : null;
    // Auto-add https:// before saving
    let finalUrl = url;
    if (finalUrl && !finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
      finalUrl = 'https://' + finalUrl;
    }
    onSave({
      ...link,
      title,
      url: finalUrl,
      thumbnail_url: thumbnailUrl || undefined,
      image_url: link.link_type === 'affiliate' ? thumbnailUrl || undefined : undefined,
      grid_columns: gridColumns,
      is_active: isActive,
      is_featured: isFeatured,
      link_format: linkFormat,
      expires_at: expiresIso,
      // Affiliate fields
      company_name: link.link_type === 'affiliate' ? companyName : undefined,
      discount_amount: link.link_type === 'affiliate' ? discountAmount : undefined,
      discount_code: link.link_type === 'affiliate' ? discountCode : undefined,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Edit Link</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Affiliate Link Fields */}
          {link.link_type === 'affiliate' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Brand Name"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Affiliate URL *</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onBlur={handleUrlBlur}
                  placeholder="youraffiliatelink.com"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Your affiliate/referral link</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Brand Logo/Image *</label>
                {thumbnailUrl ? (
                  <div className="flex items-center gap-4">
                    <img src={thumbnailUrl} alt="Preview" className="w-20 h-20 rounded-lg object-cover" />
                    <button type="button" onClick={() => setThumbnailUrl('')} className="text-sm text-red-400 hover:text-red-300">
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-white/40 transition-colors">
                    <div className="flex flex-col items-center justify-center">
                      {uploading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-400">Upload brand logo</p>
                          <p className="text-xs text-gray-500 mt-1">Square image recommended</p>
                        </>
                      )}
                    </div>
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                  </label>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Discount <span className="text-gray-500 text-xs">(optional)</span></label>
                  <input
                    type="text"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(e.target.value)}
                    placeholder="20% OFF"
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Code <span className="text-gray-500 text-xs">(optional)</span></label>
                  <input
                    type="text"
                    value={discountCode}
                    onChange={(e) => setDiscountCode(e.target.value)}
                    placeholder="SAVE20"
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">Badge appears on brand logo (top right)</p>
            </>
          ) : link.link_type === 'donate' ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Support Link URL *</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onBlur={handleUrlBlur}
                  placeholder="venmo.com/your-handle"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Venmo, PayPal, Cash App, etc.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Custom Title <span className="text-gray-500 text-xs">(optional)</span></label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Support Me"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-yellow-500"
                />
                <p className="text-xs text-gray-500 mt-1">Platform name used if left blank</p>
              </div>
            </>
          ) : (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="My Website"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">URL</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onBlur={handleUrlBlur}
                  placeholder="example.com"
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">https:// will be added automatically</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Image (optional)</label>
                
                {thumbnailUrl ? (
                  <div className="flex items-center gap-4">
                    <img 
                      src={thumbnailUrl} 
                      alt="Preview" 
                      className="w-20 h-20 rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setThumbnailUrl('')}
                      className="text-sm text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-white/20 rounded-xl cursor-pointer hover:border-white/40 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                      ) : (
                        <>
                          <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-400">Click to upload image</p>
                          <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                        </>
                      )}
                    </div>
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/*"
                      onChange={handleImageUpload}
                      disabled={uploading}
                    />
                  </label>
                )}
              </div>
            </>
          )}

          {link.link_type === 'grid' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Grid Layout</label>
              <div className="grid grid-cols-2 gap-3">
                {/* 2 Columns (1x2) */}
                <button
                  type="button"
                  onClick={() => setGridColumns(2)}
                  className={`p-3 rounded-xl transition-all ${
                    gridColumns === 2
                      ? 'bg-blue-500/20 border-2 border-blue-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="grid grid-cols-2 gap-1 mb-2">
                    <div className="aspect-square bg-current opacity-40 rounded" />
                    <div className="aspect-square bg-current opacity-40 rounded" />
                  </div>
                  <div className="text-xs text-center text-gray-300">1×2</div>
                </button>
                
                {/* 4 Columns (2x2) */}
                <button
                  type="button"
                  onClick={() => setGridColumns(4)}
                  className={`p-3 rounded-xl transition-all ${
                    gridColumns === 4
                      ? 'bg-blue-500/20 border-2 border-blue-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="grid grid-cols-2 gap-1 mb-2">
                    <div className="aspect-square bg-current opacity-40 rounded" />
                    <div className="aspect-square bg-current opacity-40 rounded" />
                    <div className="aspect-square bg-current opacity-40 rounded" />
                    <div className="aspect-square bg-current opacity-40 rounded" />
                  </div>
                  <div className="text-xs text-center text-gray-300">2×2</div>
                </button>
              </div>
            </div>
          )}

          {/* Link Format for basic links */}
          {link.link_type === 'basic' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Link Format</label>
              <div className="grid grid-cols-3 gap-3">
                {/* Thin */}
                <button
                  type="button"
                  onClick={() => setLinkFormat('thin')}
                  className={`p-3 rounded-xl transition-all ${
                    linkFormat === 'thin'
                      ? 'bg-blue-500/20 border-2 border-blue-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-full h-6 bg-current opacity-40 rounded flex items-center px-2">
                      <div className="w-3 h-3 bg-white/60 rounded mr-2" />
                      <div className="flex-1 h-2 bg-white/40 rounded" />
                    </div>
                    <span className="text-xs text-gray-300">Thin</span>
                  </div>
                </button>
                
                {/* Tall */}
                <button
                  type="button"
                  onClick={() => setLinkFormat('tall')}
                  className={`p-3 rounded-xl transition-all ${
                    linkFormat === 'tall'
                      ? 'bg-blue-500/20 border-2 border-blue-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-full h-12 bg-current opacity-40 rounded flex items-center px-2">
                      <div className="w-8 h-8 bg-white/60 rounded mr-2" />
                      <div className="flex-1">
                        <div className="h-2 bg-white/40 rounded mb-1" />
                        <div className="h-1.5 bg-white/20 rounded w-2/3" />
                      </div>
                    </div>
                    <span className="text-xs text-gray-300">Tall</span>
                  </div>
                </button>
                
                {/* Square (no title) */}
                <button
                  type="button"
                  onClick={() => setLinkFormat('square')}
                  className={`p-3 rounded-xl transition-all ${
                    linkFormat === 'square'
                      ? 'bg-blue-500/20 border-2 border-blue-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 bg-current opacity-40 rounded flex items-center justify-center">
                      <div className="w-6 h-6 bg-white/60 rounded" />
                    </div>
                    <span className="text-xs text-gray-300">Square</span>
                  </div>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {linkFormat === 'square' ? 'Image only, no title displayed' : 'Choose how your link appears'}
              </p>
            </div>
          )}

          {/* Card Format for grid links */}
          {link.link_type === 'grid' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Card Format</label>
              <div className="grid grid-cols-3 gap-3">
                {/* Thin */}
                <button
                  type="button"
                  onClick={() => setLinkFormat('thin')}
                  className={`p-3 rounded-xl transition-all ${
                    linkFormat === 'thin'
                      ? 'bg-purple-500/20 border-2 border-purple-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-full h-6 bg-current opacity-40 rounded flex items-center px-2">
                      <div className="w-3 h-3 bg-white/60 rounded mr-2" />
                      <div className="flex-1 h-2 bg-white/40 rounded" />
                    </div>
                    <span className="text-xs text-gray-300">Thin</span>
                  </div>
                </button>
                
                {/* Tall */}
                <button
                  type="button"
                  onClick={() => setLinkFormat('tall')}
                  className={`p-3 rounded-xl transition-all ${
                    linkFormat === 'tall'
                      ? 'bg-purple-500/20 border-2 border-purple-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-full h-12 bg-current opacity-40 rounded overflow-hidden">
                      <div className="w-full h-6 bg-white/60" />
                      <div className="px-2 py-1">
                        <div className="h-1.5 bg-white/40 rounded w-2/3" />
                      </div>
                    </div>
                    <span className="text-xs text-gray-300">Tall</span>
                  </div>
                </button>
                
                {/* Square (no title) */}
                <button
                  type="button"
                  onClick={() => setLinkFormat('square')}
                  className={`p-3 rounded-xl transition-all ${
                    linkFormat === 'square'
                      ? 'bg-purple-500/20 border-2 border-purple-500'
                      : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 bg-current opacity-40 rounded flex items-center justify-center">
                      <div className="w-6 h-6 bg-white/60 rounded" />
                    </div>
                    <span className="text-xs text-gray-300">Square</span>
                  </div>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {linkFormat === 'square' ? 'Image only, flush edges' : 
                 linkFormat === 'tall' ? 'Image with title below' : 'Compact card with title'}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-5 h-5 rounded bg-white/10 border-white/20 text-blue-500 focus:ring-blue-500"
              />
              <span className="text-white">Link is active</span>
            </label>

            {link.link_type === 'basic' && (
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isFeatured}
                  onChange={(e) => setIsFeatured(e.target.checked)}
                  className="w-5 h-5 rounded bg-white/10 border-white/20 text-yellow-500 focus:ring-yellow-500"
                />
                <div className="flex items-center gap-2">
                  <span className="text-white">Featured link</span>
                  <span className="text-yellow-400">⭐</span>
                </div>
                <span className="text-xs text-gray-400">(gradient highlight)</span>
              </label>
            )}
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-medium">Expiring link</p>
                <p className="text-gray-400 text-xs mt-0.5">Auto-remove the link on a specific date.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const next = !expires;
                  setExpires(next);
                  if (next && !expiresDate) {
                    const d = new Date();
                    d.setDate(d.getDate() + 7);
                    setExpiresDate(d.toISOString().slice(0, 10));
                  }
                }}
                className={`w-12 h-6 rounded-full transition-colors ${expires ? 'bg-green-500' : 'bg-gray-600'}`}
              >
                <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${expires ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {expires && (
              <div className="mt-3">
                <label className="block text-xs text-gray-400 mb-1">Removes on</label>
                <input
                  type="date"
                  value={expiresDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setExpiresDate(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-medium transition-colors"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Stream Cards Section with up/down reordering
const StreamCardsSection: React.FC<{
  talentProfile: TalentProfile | null;
  bioSettings: BioSettings | null;
  setBioSettings: React.Dispatch<React.SetStateAction<BioSettings | null>>;
  setShowStreamChannelModal: React.Dispatch<React.SetStateAction<'rumble' | 'youtube' | null>>;
  setShowPodcastModal: React.Dispatch<React.SetStateAction<boolean>>;
  refreshPreview: () => void;
  analyticsStartAt: string | null;
  totalViews: number;
  cardClicks: Record<string, number>;
}> = ({ talentProfile, bioSettings, setBioSettings, setShowStreamChannelModal, setShowPodcastModal, refreshPreview, analyticsStartAt, totalViews, cardClicks }) => {
  // Get available stream cards
  const availableCards: { id: string; hasContent: boolean }[] = [
    { id: 'rumble', hasContent: !!talentProfile?.rumble_handle },
    { id: 'youtube', hasContent: !!talentProfile?.youtube_handle },
    { id: 'podcast', hasContent: !!talentProfile?.podcast_rss_url },
  ];
  
  // Get order from settings or use default
  const defaultOrder = ['rumble', 'youtube', 'podcast'];
  const cardOrder = bioSettings?.stream_card_order || defaultOrder;
  
  // Sort cards by order, only show ones with content
  const sortedCards = [...cardOrder]
    .filter(id => availableCards.find(c => c.id === id)?.hasContent)
    .concat(availableCards.filter(c => c.hasContent && !cardOrder.includes(c.id)).map(c => c.id));

  const moveCard = async (cardId: string, direction: 'up' | 'down') => {
    if (!bioSettings) return;
    const idx = sortedCards.indexOf(cardId);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sortedCards.length) return;

    const newOrder = [...sortedCards];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];

    const updated = { ...bioSettings, stream_card_order: newOrder };
    setBioSettings(updated);

    await supabase.rpc('update_bio_settings', {
      p_id: bioSettings.id,
      p_updates: { stream_card_order: newOrder },
    });

    toast.success('Card order updated');
    setTimeout(refreshPreview, 500);
  };

  const toggleCard = async (cardType: 'rumble' | 'youtube' | 'podcast', enabled: boolean) => {
    if (!bioSettings) return;
    
    const fieldName = cardType === 'rumble' ? 'show_rumble_card' 
      : cardType === 'youtube' ? 'show_youtube_card' 
      : 'show_podcast_card';
    
    const updated = { ...bioSettings, [fieldName]: enabled };
    setBioSettings(updated);
    
    await supabase.rpc('update_bio_settings', {
      p_id: bioSettings.id,
      p_updates: { [fieldName]: enabled },
    });
    
    const cardName = cardType.charAt(0).toUpperCase() + cardType.slice(1);
    toast.success(`${cardName} card ${enabled ? 'enabled' : 'disabled'}`);
    setTimeout(refreshPreview, 500);
  };

  if (sortedCards.length === 0) return null;

  return (
    <div className="space-y-2">
      {sortedCards.map((cardId, cardIdx) => {
        if (cardId === 'rumble' && talentProfile?.rumble_handle) {
          return (
            <div
              key="rumble"
              className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-3 transition-all"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveCard('rumble', 'up')} disabled={cardIdx === 0} className="p-0.5 text-gray-500 hover:text-white disabled:opacity-30"><ArrowsUpDownIcon className="h-3.5 w-3.5 rotate-180" /></button>
                    <button onClick={() => moveCard('rumble', 'down')} disabled={cardIdx === sortedCards.length - 1} className="p-0.5 text-gray-500 hover:text-white disabled:opacity-30"><ArrowsUpDownIcon className="h-3.5 w-3.5" /></button>
                  </div>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-green-400 flex-shrink-0">
                    <path d="M14.4528 13.5458c0.8064 -0.6542 0.9297 -1.8381 0.2756 -2.6445a1.8802 1.8802 0 0 0 -0.2756 -0.2756 21.2127 21.2127 0 0 0 -4.3121 -2.776c-1.066 -0.51 -2.256 0.2 -2.4261 1.414a23.5226 23.5226 0 0 0 -0.14 5.5021c0.116 1.23 1.292 1.964 2.372 1.492a19.6285 19.6285 0 0 0 4.5062 -2.704v-0.008zm6.9322 -5.4002c2.0335 2.228 2.0396 5.637 0.014 7.8723A26.1487 26.1487 0 0 1 8.2946 23.846c-2.6848 0.6713 -5.4168 -0.914 -6.1662 -3.5781 -1.524 -5.2002 -1.3 -11.0803 0.17 -16.3045 0.772 -2.744 3.3521 -4.4661 6.0102 -3.832 4.9242 1.174 9.5443 4.196 13.0764 8.0121v0.002z"/>
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-white text-sm">Rumble</h3>
                      <span className="text-xs text-gray-500">@{talentProfile.rumble_handle}</span>
                      <button
                        onClick={() => setShowStreamChannelModal('rumble')}
                        className="p-1 text-gray-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                      >
                        <PencilIcon className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">Shows your latest video or live stream</p>
                  </div>
                  {/* CTR Badge */}
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10 whitespace-nowrap"
                    title={analyticsStartAt ? `CTR since ${new Date(analyticsStartAt).toLocaleDateString()}` : 'CTR'}
                  >
                    {totalViews > 0 ? `${(((cardClicks['rumble'] || 0) / totalViews) * 100).toFixed(1)}%` : '0%'} | {cardClicks['rumble'] || 0}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={bioSettings?.show_rumble_card !== false}
                    onChange={(e) => toggleCard('rumble', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                </label>
              </div>
            </div>
          );
        }
        
        if (cardId === 'youtube' && talentProfile?.youtube_handle) {
          return (
            <div
              key="youtube"
              className="bg-gradient-to-r from-red-500/20 to-rose-500/20 border border-red-500/30 rounded-xl p-3 transition-all"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveCard('youtube', 'up')} disabled={cardIdx === 0} className="p-0.5 text-gray-500 hover:text-white disabled:opacity-30"><ArrowsUpDownIcon className="h-3.5 w-3.5 rotate-180" /></button>
                    <button onClick={() => moveCard('youtube', 'down')} disabled={cardIdx === sortedCards.length - 1} className="p-0.5 text-gray-500 hover:text-white disabled:opacity-30"><ArrowsUpDownIcon className="h-3.5 w-3.5" /></button>
                  </div>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-red-400 flex-shrink-0">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-white text-sm">YouTube</h3>
                      <span className="text-xs text-gray-500">@{talentProfile.youtube_handle}</span>
                      <button
                        onClick={() => setShowStreamChannelModal('youtube')}
                        className="p-1 text-gray-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                      >
                        <PencilIcon className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">Shows your latest video or live stream</p>
                  </div>
                  {/* CTR Badge */}
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10 whitespace-nowrap"
                    title={analyticsStartAt ? `CTR since ${new Date(analyticsStartAt).toLocaleDateString()}` : 'CTR'}
                  >
                    {totalViews > 0 ? `${(((cardClicks['youtube'] || 0) / totalViews) * 100).toFixed(1)}%` : '0%'} | {cardClicks['youtube'] || 0}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={bioSettings?.show_youtube_card !== false}
                    onChange={(e) => toggleCard('youtube', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-red-500"></div>
                </label>
              </div>
            </div>
          );
        }
        
        if (cardId === 'podcast' && talentProfile?.podcast_rss_url) {
          return (
            <div
              key="podcast"
              className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-3 transition-all"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveCard('podcast', 'up')} disabled={cardIdx === 0} className="p-0.5 text-gray-500 hover:text-white disabled:opacity-30"><ArrowsUpDownIcon className="h-3.5 w-3.5 rotate-180" /></button>
                    <button onClick={() => moveCard('podcast', 'down')} disabled={cardIdx === sortedCards.length - 1} className="p-0.5 text-gray-500 hover:text-white disabled:opacity-30"><ArrowsUpDownIcon className="h-3.5 w-3.5" /></button>
                  </div>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-purple-400 flex-shrink-0">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-white text-sm">Podcast</h3>
                      <span className="text-xs text-gray-500 truncate max-w-[120px]">{talentProfile.podcast_name || 'RSS Feed'}</span>
                      <button
                        onClick={() => setShowPodcastModal(true)}
                        className="p-1 text-gray-500 hover:text-white hover:bg-white/10 rounded transition-colors"
                      >
                        <PencilIcon className="h-3 w-3" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-400">Shows your latest podcast episode</p>
                  </div>
                  {/* CTR Badge */}
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 border border-white/10 whitespace-nowrap"
                    title={analyticsStartAt ? `CTR since ${new Date(analyticsStartAt).toLocaleDateString()}` : 'CTR'}
                  >
                    {totalViews > 0 ? `${(((cardClicks['podcast'] || 0) / totalViews) * 100).toFixed(1)}%` : '0%'} | {cardClicks['podcast'] || 0}
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={bioSettings?.show_podcast_card !== false}
                    onChange={(e) => toggleCard('podcast', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-9 h-5 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-500"></div>
                </label>
              </div>
            </div>
          );
        }
        
        return null;
      })}
    </div>
  );
};

// Stream Channel Selection Modal
const StreamChannelModal: React.FC<{
  platform: 'rumble' | 'youtube';
  socialLinks: SocialAccount[];
  currentHandle?: string;
  currentRumbleType?: 'c' | 'user';
  onClose: () => void;
  onSelect: (handle: string, rumbleType?: 'c' | 'user') => void;
  onAddNew: (handle: string, rumbleType?: 'c' | 'user') => void;
}> = ({ platform, socialLinks, currentHandle, currentRumbleType, onClose, onSelect, onAddNew }) => {
  const [newHandle, setNewHandle] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [rumbleType, setRumbleType] = useState<'c' | 'user'>(currentRumbleType || 'c');
  
  // Filter social links for this platform
  const platformAccounts = socialLinks.filter(s => s.platform === platform);
  
  const platformInfo = {
    rumble: {
      name: 'Rumble',
      color: 'green',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-green-400">
          <path d="M14.4528 13.5458c0.8064 -0.6542 0.9297 -1.8381 0.2756 -2.6445a1.8802 1.8802 0 0 0 -0.2756 -0.2756 21.2127 21.2127 0 0 0 -4.3121 -2.776c-1.066 -0.51 -2.256 0.2 -2.4261 1.414a23.5226 23.5226 0 0 0 -0.14 5.5021c0.116 1.23 1.292 1.964 2.372 1.492a19.6285 19.6285 0 0 0 4.5062 -2.704v-0.008zm6.9322 -5.4002c2.0335 2.228 2.0396 5.637 0.014 7.8723A26.1487 26.1487 0 0 1 8.2946 23.846c-2.6848 0.6713 -5.4168 -0.914 -6.1662 -3.5781 -1.524 -5.2002 -1.3 -11.0803 0.17 -16.3045 0.772 -2.744 3.3521 -4.4661 6.0102 -3.832 4.9242 1.174 9.5443 4.196 13.0764 8.0121v0.002z"/>
        </svg>
      ),
    },
    youtube: {
      name: 'YouTube',
      color: 'red',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6 text-red-400">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
        </svg>
      ),
    },
  };
  
  const info = platformInfo[platform];
  
  const handleAddNew = () => {
    if (newHandle.trim()) {
      const cleanHandle = newHandle.trim().replace(/^@/, '');
      onAddNew(cleanHandle, platform === 'rumble' ? rumbleType : undefined);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-md">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            {info.icon}
            <h2 className="text-xl font-bold text-white">Select {info.name} Channel</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        
        <p className="text-gray-400 text-sm mb-4">
          Choose which {info.name} {platform === 'rumble' ? 'user or channel' : 'channel'} to display on your bio page.
        </p>
        
        {/* Rumble Type Selector */}
        {platform === 'rumble' && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">Account Type</label>
            <div className="flex gap-2">
              <button
                onClick={() => setRumbleType('user')}
                className={`flex-1 p-3 rounded-xl border text-sm font-medium transition-colors ${
                  rumbleType === 'user'
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  User
                </div>
                <p className="text-xs text-gray-500 mt-1">rumble.com/user/...</p>
              </button>
              <button
                onClick={() => setRumbleType('c')}
                className={`flex-1 p-3 rounded-xl border text-sm font-medium transition-colors ${
                  rumbleType === 'c'
                    ? 'bg-green-500/20 border-green-500/50 text-green-400'
                    : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Channel
                </div>
                <p className="text-xs text-gray-500 mt-1">rumble.com/c/...</p>
              </button>
            </div>
          </div>
        )}
        
        {/* Existing accounts */}
        <div className="space-y-2 mb-4">
          {platformAccounts.length > 0 ? (
            platformAccounts.map((account) => {
              const isSelected = currentHandle === account.handle.replace(/^@/, '');
              const selectedBg = platform === 'rumble' ? 'bg-green-500/20 border-green-500/50' : 'bg-red-500/20 border-red-500/50';
              const checkColor = platform === 'rumble' ? 'text-green-400' : 'text-red-400';
              
              return (
                <button
                  key={account.id}
                  onClick={() => {
                    console.log('StreamChannelModal: Selecting account', account.handle, 'for platform', platform);
                    onSelect(account.handle.replace(/^@/, ''), platform === 'rumble' ? rumbleType : undefined);
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border transition-colors ${
                    isSelected ? selectedBg : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {info.icon}
                    <span className="text-white">@{account.handle.replace(/^@/, '')}</span>
                  </div>
                  {isSelected && (
                    <svg className={`w-5 h-5 ${checkColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              );
            })
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">
              No {info.name} {platform === 'rumble' ? 'accounts' : 'channels'} added yet
            </div>
          )}
        </div>
        
        {/* Add new section */}
        {showAddNew ? (
          <div className="border-t border-white/10 pt-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Add New {info.name} {platform === 'rumble' ? (rumbleType === 'user' ? 'User' : 'Channel') : 'Channel'}
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">@</span>
                <input
                  type="text"
                  value={newHandle}
                  onChange={(e) => setNewHandle(e.target.value)}
                  placeholder={`${info.name} handle`}
                  className="w-full bg-white/5 border border-white/20 rounded-xl pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddNew()}
                />
              </div>
              <button
                onClick={handleAddNew}
                disabled={!newHandle.trim()}
                className={`px-4 py-3 ${platform === 'rumble' ? 'bg-green-500 hover:bg-green-600' : 'bg-red-500 hover:bg-red-600'} text-white rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                Add
              </button>
            </div>
            <button
              onClick={() => setShowAddNew(false)}
              className="mt-2 text-sm text-gray-500 hover:text-gray-400"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddNew(true)}
            className="w-full flex items-center justify-center gap-2 p-3 border border-dashed border-white/20 rounded-xl text-gray-400 hover:text-white hover:border-white/40 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            Add New {info.name} {platform === 'rumble' ? (rumbleType === 'user' ? 'User' : 'Channel') : 'Channel'}
          </button>
        )}
        
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Available podcast platforms for manual link entry
const PODCAST_PLATFORMS = [
  { id: 'spotify', name: 'Spotify', icon: '🎵', color: 'from-green-500 to-green-600' },
  { id: 'apple', name: 'Apple Podcasts', icon: '🎧', color: 'from-purple-500 to-pink-500' },
  { id: 'youtube', name: 'YouTube', icon: '📺', color: 'from-red-500 to-red-600' },
  { id: 'amazon', name: 'Amazon Music', icon: '🎶', color: 'from-blue-400 to-cyan-500' },
  { id: 'iheart', name: 'iHeartRadio', icon: '❤️', color: 'from-red-400 to-pink-500' },
  { id: 'google', name: 'Google Podcasts', icon: '🎙️', color: 'from-blue-500 to-green-500' },
  { id: 'overcast', name: 'Overcast', icon: '☁️', color: 'from-orange-500 to-orange-600' },
  { id: 'pocketcasts', name: 'Pocket Casts', icon: '📱', color: 'from-red-500 to-orange-500' },
  { id: 'stitcher', name: 'Stitcher', icon: '🎛️', color: 'from-yellow-500 to-orange-500' },
  { id: 'tunein', name: 'TuneIn', icon: '📻', color: 'from-cyan-500 to-blue-500' },
];

// Podcast Modal - Configure podcast RSS feed and manual platform links
const PodcastModal: React.FC<{
  currentRssUrl?: string;
  currentPodcastName?: string;
  currentPlatformLinks?: Record<string, string>;
  onClose: () => void;
  onSave: (rssUrl: string, podcastName: string, platformLinks: Record<string, string>) => void;
  onRemove: () => void;
}> = ({ currentRssUrl, currentPodcastName, currentPlatformLinks, onClose, onSave, onRemove }) => {
  const [rssUrl, setRssUrl] = useState(currentRssUrl || '');
  const [podcastName, setPodcastName] = useState(currentPodcastName || '');
  const [platformLinks, setPlatformLinks] = useState<Record<string, string>>(currentPlatformLinks || {});
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [showManualLinks, setShowManualLinks] = useState(Object.keys(currentPlatformLinks || {}).length > 0);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [newLinkUrl, setNewLinkUrl] = useState('');

  const addPlatformLink = () => {
    if (selectedPlatform && newLinkUrl.trim()) {
      setPlatformLinks(prev => ({
        ...prev,
        [selectedPlatform]: newLinkUrl.trim()
      }));
      setSelectedPlatform('');
      setNewLinkUrl('');
    }
  };

  const removePlatformLink = (platformId: string) => {
    setPlatformLinks(prev => {
      const updated = { ...prev };
      delete updated[platformId];
      return updated;
    });
  };

  const validateAndSave = async () => {
    if (!rssUrl.trim()) {
      setError('Please enter an RSS feed URL');
      return;
    }

    setValidating(true);
    setError('');

    try {
      // Try to fetch and parse the RSS feed to validate it
      const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(rssUrl)}`;
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        // If proxy fails, try direct fetch (might work for some feeds)
        console.warn('Proxy failed, trying direct fetch');
        const directResponse = await fetch(rssUrl);
        if (!directResponse.ok) {
          throw new Error('Could not fetch RSS feed');
        }
      }

      // Try to parse if we got it, but don't fail if parsing doesn't work
      let finalName = podcastName.trim() || 'My Podcast';
      try {
        const text = await response.text();
        const parser = new DOMParser();
        const xml = parser.parseFromString(text, 'text/xml');
        
        const channel = xml.querySelector('channel');
        if (channel) {
          const feedTitle = channel.querySelector('title')?.textContent || '';
          if (feedTitle && !podcastName.trim()) {
            finalName = feedTitle;
          }
        }
      } catch (parseErr) {
        console.warn('Could not parse feed for title, using provided name:', parseErr);
      }

      // Save even if validation/parsing had issues - the bio page will validate on display
      onSave(rssUrl.trim(), finalName, platformLinks);
    } catch (err: any) {
      console.error('RSS validation error:', err);
      // Be more lenient - allow saving even if validation fails
      const finalName = podcastName.trim() || 'My Podcast';
      onSave(rssUrl.trim(), finalName, platformLinks);
    } finally {
      setValidating(false);
    }
  };

  // Get platforms that haven't been added yet
  const availablePlatforms = PODCAST_PLATFORMS.filter(p => !platformLinks[p.id]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 text-white">
                <path d="M12 1c-6.1 0-11 4.9-11 11s4.9 11 11 11 11-4.9 11-11S18.1 1 12 1zm0 20c-5 0-9-4-9-9s4-9 9-9 9 4 9 9-4 9-9 9z"/>
                <path d="M12 6c-3.3 0-6 2.7-6 6 0 2.5 1.5 4.6 3.7 5.5l.3-1.9c-1.4-.7-2.4-2.1-2.4-3.6 0-2.2 1.8-4 4-4s4 1.8 4 4c0 1.5-1 2.9-2.4 3.6l.3 1.9c2.2-.9 3.7-3 3.7-5.5.2-3.3-2.5-6-5.2-6z"/>
                <circle cx="12" cy="12" r="2"/>
                <path d="M12 16l-1 6h2l-1-6z"/>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-white">Podcast Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <p className="text-gray-400 text-sm mb-4">
          Add your podcast RSS feed URL to display your latest episode. Platform links are auto-detected, but you can add them manually below.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">RSS Feed URL *</label>
            <input
              type="url"
              value={rssUrl}
              onChange={(e) => setRssUrl(e.target.value)}
              placeholder="https://feed.podbean.com/yourshow/feed.xml"
              className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Supports .xml and .rss formats from Podbean, Spotify, Apple Podcasts, Libsyn, etc.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Podcast Name (optional)</label>
            <input
              type="text"
              value={podcastName}
              onChange={(e) => setPodcastName(e.target.value)}
              placeholder="Auto-detected from feed"
              className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            />
          </div>

          {/* Manual Platform Links Section */}
          <div className="border-t border-white/10 pt-4 mt-4">
            <button
              type="button"
              onClick={() => setShowManualLinks(!showManualLinks)}
              className="flex items-center gap-2 text-sm font-medium text-purple-400 hover:text-purple-300 transition-colors"
            >
              <svg className={`w-4 h-4 transition-transform ${showManualLinks ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              Add Platform Links Manually
            </button>
            <p className="text-xs text-gray-500 mt-1">
              Links are auto-detected, but you can override or add missing platforms here.
            </p>

            {showManualLinks && (
              <div className="mt-4 space-y-3">
                {/* Current platform links */}
                {Object.entries(platformLinks).length > 0 && (
                  <div className="space-y-2">
                    {Object.entries(platformLinks).map(([platformId, url]) => {
                      const platform = PODCAST_PLATFORMS.find(p => p.id === platformId);
                      return (
                        <div key={platformId} className="flex items-center gap-2 bg-white/5 rounded-lg p-2">
                          <span className="text-lg">{platform?.icon || '🎙️'}</span>
                          <span className="text-sm text-white flex-1 truncate">{platform?.name || platformId}</span>
                          <span className="text-xs text-gray-500 truncate max-w-[150px]">{url}</span>
                          <button
                            onClick={() => removePlatformLink(platformId)}
                            className="p-1 text-red-400 hover:text-red-300 hover:bg-red-500/20 rounded transition-colors"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add new platform link */}
                {availablePlatforms.length > 0 && (
                  <div className="flex gap-2">
                    <select
                      value={selectedPlatform}
                      onChange={(e) => setSelectedPlatform(e.target.value)}
                      className="bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                    >
                      <option value="">Select platform...</option>
                      {availablePlatforms.map(platform => (
                        <option key={platform.id} value={platform.id}>
                          {platform.icon} {platform.name}
                        </option>
                      ))}
                    </select>
                    <input
                      type="url"
                      value={newLinkUrl}
                      onChange={(e) => setNewLinkUrl(e.target.value)}
                      placeholder="https://..."
                      className="flex-1 bg-white/5 border border-white/20 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                    />
                    <button
                      onClick={addPlatformLink}
                      disabled={!selectedPlatform || !newLinkUrl.trim()}
                      className="px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <PlusIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <p className="text-red-400 text-sm">{error}</p>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          {currentRssUrl && (
            <button
              onClick={onRemove}
              className="px-4 py-2.5 bg-red-500/20 border border-red-500/30 text-red-400 rounded-xl font-medium hover:bg-red-500/30 transition-colors"
            >
              Remove
            </button>
          )}
          <button
            onClick={onClose}
            className="flex-1 px-6 py-2.5 bg-white/5 border border-white/10 text-gray-300 rounded-xl font-medium hover:bg-white/10 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={validateAndSave}
            disabled={validating || !rssUrl.trim()}
            className="flex-1 px-6 py-2.5 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {validating ? 'Validating...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Newsletter Modal
const NewsletterModal: React.FC<{
  configs: NewsletterConfig[];
  talentId: string;
  onClose: () => void;
  onSave: (configs: NewsletterConfig[]) => void;
}> = ({ configs, talentId, onClose, onSave }) => {
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [listId, setListId] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!selectedProvider) return;
    
    setSaving(true);
    try {
      const existingConfig = configs.find(c => c.provider === selectedProvider);
      
      const configData = {
        talent_id: talentId,
        provider: selectedProvider,
        api_key: apiKey || null,
        list_id: listId || null,
        webhook_url: webhookUrl || null,
        is_active: true,
      };

      if (existingConfig?.id) {
        const { error } = await supabase
          .from('bio_newsletter_configs')
          .update(configData)
          .eq('id', existingConfig.id);
        
        if (error) throw error;
        onSave(configs.map(c => c.id === existingConfig.id ? { ...c, ...configData } : c));
      } else {
        const { data, error } = await supabase
          .from('bio_newsletter_configs')
          .insert([configData])
          .select()
          .single();
        
        if (error) throw error;
        onSave([...configs, data]);
      }

      toast.success('Newsletter integration saved!');
      setSelectedProvider(null);
      setApiKey('');
      setListId('');
      setWebhookUrl('');
    } catch (error) {
      console.error('Error saving newsletter config:', error);
      toast.error('Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Newsletter Integration</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!selectedProvider ? (
          <div className="space-y-3">
            <p className="text-gray-400 text-sm mb-4">
              Select a provider to connect your email list:
            </p>
            {NEWSLETTER_PROVIDERS.map((provider) => {
              const config = configs.find(c => c.provider === provider.id);
              return (
                <button
                  key={provider.id}
                  onClick={() => {
                    setSelectedProvider(provider.id);
                    if (config) {
                      setApiKey(config.api_key || '');
                      setListId(config.list_id || '');
                      setWebhookUrl(config.webhook_url || '');
                    }
                  }}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${
                    config?.is_active
                      ? 'border-green-500/50 bg-green-500/10'
                      : 'border-white/20 hover:border-white/40'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{provider.icon}</span>
                    <div>
                      <h3 className="font-medium text-white">{provider.name}</h3>
                      {config?.is_active && (
                        <p className="text-xs text-green-400">Connected</p>
                      )}
                    </div>
                  </div>
                  <PencilIcon className="h-5 w-5 text-gray-400" />
                </button>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            <button
              onClick={() => setSelectedProvider(null)}
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
            >
              ← Back to providers
            </button>

            <div className="p-4 bg-white/5 rounded-xl">
              <h3 className="font-medium text-white mb-1">
                {NEWSLETTER_PROVIDERS.find(p => p.id === selectedProvider)?.name}
              </h3>
              <p className="text-sm text-gray-400">
                Enter your API credentials to connect.
              </p>
            </div>

            {selectedProvider === 'zapier' ? (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Webhook URL</label>
                <input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://hooks.zapier.com/..."
                  className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Create a Zap with "Webhooks by Zapier" as the trigger
                </p>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">API Key</label>
                  <input
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter your API key"
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">List/Audience ID</label>
                  <input
                    type="text"
                    value={listId}
                    onChange={(e) => setListId(e.target.value)}
                    placeholder="Enter your list ID"
                    className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  />
                </div>
              </>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setSelectedProvider(null)}
                className="flex-1 px-4 py-3 bg-white/5 border border-white/20 text-white rounded-xl font-medium hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Import Modal
const ImportModal: React.FC<{
  onClose: () => void;
  onImport: (url: string) => void;
}> = ({ onClose, onImport }) => {
  const [url, setUrl] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onImport(url);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Import Links</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Link-in-Bio URL
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://linktr.ee/username"
              className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
              required
            />
            <p className="text-xs text-gray-500 mt-2">
              Supports Linktree, Beacons, Stan Store, and other link-in-bio pages
            </p>
          </div>

          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <CloudArrowUpIcon className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-white font-medium">How it works</p>
                <p className="text-xs text-gray-400 mt-1">
                  We'll scan your existing link-in-bio page and import all your links and images automatically.
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 border border-white/20 text-white rounded-xl font-medium hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
            >
              Import
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Add Social Modal
const AddSocialModal: React.FC<{
  onClose: () => void;
  onAdd: (social: { platform: string; handle: string; rumble_type?: 'c' | 'user' }) => void;
  existingPlatforms: string[];
}> = ({ onClose, onAdd, existingPlatforms }) => {
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [handle, setHandle] = useState('');
  const [rumbleType, setRumbleType] = useState<'c' | 'user'>('c');

  const availablePlatforms = SOCIAL_PLATFORMS.filter(p => !existingPlatforms.includes(p.id));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedPlatform && handle) {
      onAdd({ 
        platform: selectedPlatform, 
        handle: handle.replace(/^@/, ''),
        ...(selectedPlatform === 'rumble' && { rumble_type: rumbleType })
      });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Add Social Link</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {!selectedPlatform ? (
          <div className="space-y-3">
            <p className="text-gray-400 text-sm mb-4">
              Select a platform to add:
            </p>
            {availablePlatforms.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                You've added all available social platforms!
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {availablePlatforms.map((platform) => (
                  <button
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform.id)}
                    className="p-4 rounded-xl border-2 border-white/20 hover:border-white/40 text-left transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center text-blue-400">
                        {platform.icon}
                      </div>
                      <span className="font-medium text-white">{platform.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <button
              type="button"
              onClick={() => setSelectedPlatform(null)}
              className="text-sm text-blue-400 hover:text-blue-300 flex items-center gap-1"
            >
              ← Back to platforms
            </button>

            <div className="p-4 bg-white/5 rounded-xl flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-blue-400">
                {SOCIAL_PLATFORMS.find(p => p.id === selectedPlatform)?.icon}
              </div>
              <div>
                <h3 className="font-medium text-white">
                  {SOCIAL_PLATFORMS.find(p => p.id === selectedPlatform)?.name}
                </h3>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {selectedPlatform === 'discord' ? 'Server Invite Code' : 'Username / Handle'}
              </label>
              <div className="flex items-center">
                {selectedPlatform !== 'discord' && <span className="text-gray-500 mr-2">@</span>}
                <input
                  type="text"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder={selectedPlatform === 'discord' ? 'abc123' : 'username'}
                  className="flex-1 bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                  required
                />
              </div>
              {selectedPlatform === 'discord' && (
                <p className="text-gray-500 text-xs mt-1.5">Paste your Discord server invite code (from discord.gg/your-code)</p>
              )}
            </div>

            {/* Rumble Type Selector */}
            {selectedPlatform === 'rumble' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Rumble Account Type</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setRumbleType('c')}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      rumbleType === 'c'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/5 border border-white/20 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <div className="text-sm">Channel</div>
                    <div className="text-xs opacity-70">rumble.com/c/name</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRumbleType('user')}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      rumbleType === 'user'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white/5 border border-white/20 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <div className="text-sm">User</div>
                    <div className="text-xs opacity-70">rumble.com/user/name</div>
                  </button>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 bg-white/5 border border-white/20 text-white rounded-xl font-medium hover:bg-white/10 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
              >
                Add Social
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

// Available collab platforms
const COLLAB_PLATFORMS = [
  { id: 'instagram', name: 'Instagram', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
  { id: 'tiktok', name: 'TikTok', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg> },
  { id: 'youtube', name: 'YouTube', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg> },
  { id: 'twitter', name: 'X (Twitter)', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
  { id: 'facebook', name: 'Facebook', icon: <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
];

// Add Service Modal
const AddServiceModal: React.FC<{
  service?: ServiceOffering;
  socialLinks: SocialAccount[];
  forcedType?: 'instagram_collab' | 'sponsorship';
  onUpdateFollowerCount: (socialId: string, count: number) => void;
  onClose: () => void;
  onSave: (service: Partial<ServiceOffering> & { service_type: string }) => void;
}> = ({ service, socialLinks, forcedType, onUpdateFollowerCount, onClose, onSave }) => {
  const [serviceType, setServiceType] = useState<'instagram_collab' | 'sponsorship'>(forcedType || service?.service_type as any || 'instagram_collab');
  const [title, setTitle] = useState(service?.title || (serviceType === 'sponsorship' ? 'Discuss Sponsorship' : 'Collaborate with me'));
  const [pricing, setPricing] = useState(service ? (service.pricing / 100).toString() : (serviceType === 'sponsorship' ? '0' : '250'));
  const [totalFollowers, setTotalFollowers] = useState(service?.total_followers?.toString() || '');
  const [benefits, setBenefits] = useState<string[]>(
    service?.benefits || (serviceType === 'sponsorship' 
      ? [] // No defaults for sponsorship - talent adds their own
      : [
          'Personalized video mention',
          'Story share to followers',
          'Permanent post on feed'
        ])
  );
  const [newBenefit, setNewBenefit] = useState('');
  const [description, setDescription] = useState(service?.description || '');
  const [isActive, setIsActive] = useState(service?.is_active !== false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    service?.platforms || ['instagram']
  );
  
  // Coupon and Recurring fields (admin-controlled)
  const [couponCode, setCouponCode] = useState(service?.coupon_code || '');
  const [couponDiscountAmount, setCouponDiscountAmount] = useState(service?.coupon_discount_amount?.toString() || '');
  const [couponDiscountType, setCouponDiscountType] = useState<'percentage' | 'fixed'>(service?.coupon_discount_type || 'percentage');
  const [isRecurring, setIsRecurring] = useState(service?.is_recurring || false);
  // Note: recurring_interval is chosen by the CUSTOMER at checkout, not stored on the service
  
  // Contact email for sponsorship inquiries
  const [contactEmail, setContactEmail] = useState(service?.contact_email || '');

  const togglePlatform = (platformId: string) => {
    if (selectedPlatforms.includes(platformId)) {
      // Don't allow removing all platforms
      if (selectedPlatforms.length > 1) {
        setSelectedPlatforms(selectedPlatforms.filter(p => p !== platformId));
      }
    } else {
      setSelectedPlatforms([...selectedPlatforms, platformId]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      service_type: serviceType,
      title,
      pricing: Math.round(parseFloat(pricing) * 100), // Convert to cents
      video_length_seconds: 60, // Default value
      benefits,
      platforms: selectedPlatforms,
      description,
      is_active: isActive,
      total_followers: parseInt(totalFollowers) || 0,
      // Coupon and recurring fields
      coupon_code: couponCode || null,
      coupon_discount_amount: couponDiscountAmount ? parseFloat(couponDiscountAmount) : null,
      coupon_discount_type: couponDiscountType,
      is_recurring: isRecurring,
      // Note: recurring_interval is set by the CUSTOMER at checkout, not the talent
      // Sponsorship contact email
      contact_email: serviceType === 'sponsorship' ? (contactEmail || null) : null,
    });
  };

  const addBenefit = () => {
    if (newBenefit.trim()) {
      setBenefits([...benefits, newBenefit.trim()]);
      setNewBenefit('');
    }
  };

  const removeBenefit = (index: number) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a1a] border border-white/20 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {service ? 'Edit Service' : 'Add Service'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Service Type Selector */}
          {!service && !forcedType && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">Service Type</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setServiceType('instagram_collab');
                    setTitle('Collaborate with me');
                    setPricing('250');
                    setBenefits(['Personalized video mention', 'Story share to followers', 'Permanent post on feed']);
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    serviceType === 'instagram_collab'
                      ? 'bg-pink-500/20 border-pink-500'
                      : 'bg-white/5 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg viewBox="0 0 24 24" fill="currentColor" className={`w-6 h-6 ${serviceType === 'instagram_collab' ? 'text-pink-400' : 'text-gray-400'}`}>
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    <span className={`text-sm font-medium ${serviceType === 'instagram_collab' ? 'text-white' : 'text-gray-400'}`}>
                      Social Collab
                    </span>
                  </div>
                </button>
                
                <button
                  type="button"
                  onClick={() => {
                    setServiceType('sponsorship');
                    setTitle('Discuss Sponsorship');
                    setPricing('0');
                    setBenefits([]); // No defaults for sponsorship
                  }}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    serviceType === 'sponsorship'
                      ? 'bg-green-500/20 border-green-500'
                      : 'bg-white/5 border-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={`w-6 h-6 ${serviceType === 'sponsorship' ? 'text-green-400' : 'text-gray-400'}`}>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span className={`text-sm font-medium ${serviceType === 'sponsorship' ? 'text-white' : 'text-gray-400'}`}>
                      Sponsorship
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Title - Optional for all service types */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Card Title <span className="text-gray-500 text-xs">(optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={serviceType === 'sponsorship' ? 'Discuss Sponsorship' : 'Collaborate with me'}
              className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-pink-500"
            />
          </div>

          {/* Contact Email - only for sponsorship */}
          {serviceType === 'sponsorship' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Send Inquiries To
                <span className="text-gray-500 font-normal ml-1">(optional)</span>
              </label>
              <input
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder="sponsorships@youremail.com"
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Sponsorship form submissions will be sent to this email address
              </p>
            </div>
          )}

          {/* Pricing - only for collabs */}
          {serviceType !== 'sponsorship' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Price</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  value={pricing}
                  onChange={(e) => setPricing(e.target.value)}
                  min="1"
                  step="1"
                  className="w-full bg-white/5 border border-white/20 rounded-xl pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-pink-500"
                  required
                />
              </div>
            </div>
          )}

          {/* Total Followers - only for collabs */}
          {serviceType !== 'sponsorship' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Total Followers</label>
              <p className="text-xs text-gray-500 mb-2">Your combined follower count across all platforms</p>
              <input
                type="number"
                value={totalFollowers}
                onChange={(e) => setTotalFollowers(e.target.value)}
                placeholder="e.g. 500000"
                className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-pink-500"
              />
              {parseInt(totalFollowers) > 0 && (
                <p className="text-xs text-pink-400 mt-1">
                  {parseInt(totalFollowers) >= 1000000 
                    ? `${(parseInt(totalFollowers) / 1000000).toFixed(1)}M followers`
                    : parseInt(totalFollowers) >= 1000
                      ? `${(parseInt(totalFollowers) / 1000).toFixed(1)}K followers`
                      : `${totalFollowers} followers`}
                </p>
              )}
            </div>
          )}

          {/* Platforms - only for collabs */}
          {serviceType !== 'sponsorship' && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Platforms Included</label>
            <p className="text-xs text-gray-500 mb-3">Select which social platforms this collab covers.</p>
            <div className="space-y-2">
              {COLLAB_PLATFORMS.map((platform) => {
                const socialAccount = socialLinks.find(s => s.platform === platform.id);
                const isSelected = selectedPlatforms.includes(platform.id);
                
                return (
                  <div key={platform.id} className="space-y-2">
                    <button
                      type="button"
                      onClick={() => togglePlatform(platform.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                        isSelected
                          ? 'bg-pink-500/20 border-pink-500'
                          : 'bg-white/5 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div className={`${isSelected ? 'text-pink-400' : 'text-gray-400'}`}>
                        {platform.icon}
                      </div>
                      <span className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-gray-400'}`}>
                        {platform.name}
                      </span>
                      {socialAccount && (
                        <span className="text-xs text-gray-500">@{socialAccount.handle}</span>
                      )}
                      {isSelected && (
                        <CheckIcon className="h-4 w-4 text-pink-400 ml-auto" />
                      )}
                    </button>
                    
                    
                    {/* Warning if platform selected but no social account linked */}
                    {isSelected && !socialAccount && (
                      <div className="ml-8 flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2">
                        <span className="text-xs text-yellow-400">⚠️ Add @{platform.name.toLowerCase()} handle in Social tab first</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          )}

          {/* Benefits */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">What they get</label>
            <div className="space-y-2 mb-3">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                  <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" />
                  <span className="flex-1 text-white text-sm">{benefit}</span>
                  <button
                    type="button"
                    onClick={() => removeBenefit(index)}
                    className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newBenefit}
                onChange={(e) => setNewBenefit(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addBenefit())}
                placeholder="Add a benefit..."
                className="flex-1 bg-white/5 border border-white/20 rounded-xl px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:border-pink-500 text-sm"
              />
              <button
                type="button"
                onClick={addBenefit}
                className="px-4 py-2 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
              >
                Add
              </button>
            </div>
          </div>

          {/* Description (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Description (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Additional details about your collaboration service..."
              rows={3}
              className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-pink-500 resize-none"
            />
          </div>

          {/* Coupon Section - Collapsible */}
          {serviceType !== 'sponsorship' && (
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setCouponCode(couponCode ? '' : ' ')} // Toggle visibility
                className="w-full p-4 flex items-center justify-between bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="text-white font-medium">Coupon / Discount</span>
                  {couponCode && couponCode.trim() && (
                    <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                  )}
                </div>
                <svg className={`w-5 h-5 text-gray-400 transition-transform ${couponCode ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {couponCode !== '' && (
                <div className="p-4 space-y-4 border-t border-white/10">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Coupon Code</label>
                    <input
                      type="text"
                      value={couponCode.trim()}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="e.g. SAVE20"
                      className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500 uppercase"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Discount Amount</label>
                      <input
                        type="number"
                        value={couponDiscountAmount}
                        onChange={(e) => setCouponDiscountAmount(e.target.value)}
                        placeholder={couponDiscountType === 'percentage' ? '20' : '50'}
                        min="0"
                        className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Discount Type</label>
                      <select
                        value={couponDiscountType}
                        onChange={(e) => setCouponDiscountType(e.target.value as 'percentage' | 'fixed')}
                        className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500"
                      >
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed ($)</option>
                      </select>
                    </div>
                  </div>
                  
                  {couponCode.trim() && couponDiscountAmount && (
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                      <p className="text-green-400 text-sm">
                        💰 Customers using <span className="font-bold">{couponCode.trim()}</span> will get{' '}
                        {couponDiscountType === 'percentage' 
                          ? `${couponDiscountAmount}% off` 
                          : `$${couponDiscountAmount} off`}
                        {pricing && ` (${couponDiscountType === 'percentage' 
                          ? `$${(parseFloat(pricing) * (1 - parseFloat(couponDiscountAmount) / 100)).toFixed(0)}` 
                          : `$${Math.max(0, parseFloat(pricing) - parseFloat(couponDiscountAmount)).toFixed(0)}`} final price)`}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Recurring Payment Toggle */}
          {serviceType !== 'sponsorship' && (
            <div className="border border-white/10 rounded-xl overflow-hidden">
              <div className="p-4 flex items-center justify-between bg-white/5">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <div>
                    <h4 className="font-medium text-white">Allow Recurring Payment</h4>
                    <p className="text-xs text-gray-400">Let customers choose to subscribe</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isRecurring}
                    onChange={(e) => setIsRecurring(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>
              
              {isRecurring && (
                <div className="p-4 border-t border-white/10">
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
                    <p className="text-blue-400 text-sm">
                      🔄 Customers will choose their billing frequency (weekly, monthly, etc.) at checkout. 
                      They can cancel anytime.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
            <div>
              <h4 className="font-medium text-white">Active</h4>
              <p className="text-sm text-gray-400">Show this service on your bio page</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 bg-white/5 border border-white/20 text-white rounded-xl font-medium hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-xl font-medium hover:from-pink-600 hover:to-purple-600 transition-colors"
            >
              {service ? 'Save Changes' : 'Create Service'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Events Manager Modal - manages multiple events, shows table of all events
const AddEventModal: React.FC<{
  events: BioEvent[];
  talentId: string;
  onClose: () => void;
  onEventsChange: (events: BioEvent[]) => void;
}> = ({ events, talentId, onClose, onEventsChange }) => {
  const [sourceType, setSourceType] = useState<'manual' | 'ical' | 'rss'>('manual');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [location, setLocation] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [buttonText, setButtonText] = useState('Get Tickets');
  const [sourceUrl, setSourceUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [isMultiDay, setIsMultiDay] = useState(false);

  const loadEventForEdit = (event: BioEvent) => {
    setEditingEventId(event.id || null);
    setSourceType(event.source_type);
    setTitle(event.title);
    setDescription(event.description || '');
    setEventDate(event.event_date || '');
    setEventEndDate(event.event_end_date || '');
    setIsMultiDay(!!event.event_end_date);
    setEventTime(event.event_time || '');
    setLocation(event.location || '');
    setRegistrationUrl(event.registration_url || '');
    setButtonText(event.button_text || 'Get Tickets');
    setSourceUrl(event.source_url || '');
    setImageUrl(event.image_url || '');
  };

  const clearForm = () => {
    setEditingEventId(null);
    setTitle('');
    setDescription('');
    setEventDate('');
    setEventEndDate('');
    setIsMultiDay(false);
    setEventTime('');
    setLocation('');
    setRegistrationUrl('');
    setButtonText('Get Tickets');
    setSourceUrl('');
    setImageUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (sourceType === 'manual' && !title) {
      toast.error('Please enter an event title');
      return;
    }
    
    if ((sourceType === 'ical' || sourceType === 'rss') && !sourceUrl) {
      toast.error('Please enter a feed URL');
      return;
    }

    setSaving(true);

    const eventData: Record<string, any> = {
      talent_id: talentId,
      title: sourceType === 'manual' ? title : `${sourceType.toUpperCase()} Feed`,
      description: description || null,
      event_date: eventDate || null,
      event_end_date: (isMultiDay && eventEndDate) ? eventEndDate : null,
      event_time: eventTime || null,
      location: location || null,
      registration_url: registrationUrl || null,
      button_text: buttonText,
      source_type: sourceType,
      source_url: sourceUrl || null,
      image_url: imageUrl || null,
      is_active: true,
    };

    try {
      if (editingEventId) {
        const { error } = await supabase.rpc('update_bio_event', {
          p_id: editingEventId,
          p_updates: eventData,
        });
        if (error) throw error;
        onEventsChange(events.map(ev => 
          ev.id === editingEventId ? { ...ev, ...eventData } : ev
        ));
        toast.success('Event updated!');
      } else {
        const { data: newId, error } = await supabase.rpc('create_bio_event', {
          p_event: { ...eventData, display_order: events.length },
        });
        if (error) throw error;
        onEventsChange([...events, { ...eventData, id: newId, display_order: events.length } as BioEvent]);
        toast.success('Event added!');
      }
      clearForm();
    } catch (error: any) {
      console.error('Failed to save event:', error);
      toast.error(`Failed to save event: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId: string) => {
    if (!window.confirm('Delete this event?')) return;
    
    try {
      await supabase.rpc('delete_bio_event', { p_id: eventId });
      onEventsChange(events.filter(ev => ev.id !== eventId));
      toast.success('Event deleted');
      if (editingEventId === eventId) clearForm();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  // Sort events by date (soonest first)
  const sortedEvents = [...events].sort((a, b) => {
    if (!a.event_date) return 1;
    if (!b.event_date) return -1;
    return new Date(a.event_date).getTime() - new Date(b.event_date).getTime();
  });

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-white/10 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Manage Events</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-400 mb-4">
          Only the next upcoming event will be shown on your bio page.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Source Type Selection */}
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setSourceType('manual')}
              className={`p-2 rounded-lg transition-all text-center text-sm ${
                sourceType === 'manual'
                  ? 'bg-orange-500/20 border border-orange-500 text-white'
                  : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
              }`}
            >
              ✏️ Manual
            </button>
            <button
              type="button"
              onClick={() => setSourceType('ical')}
              className={`p-2 rounded-lg transition-all text-center text-sm ${
                sourceType === 'ical'
                  ? 'bg-orange-500/20 border border-orange-500 text-white'
                  : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
              }`}
            >
              📅 iCal
            </button>
            <button
              type="button"
              onClick={() => setSourceType('rss')}
              className={`p-2 rounded-lg transition-all text-center text-sm ${
                sourceType === 'rss'
                  ? 'bg-orange-500/20 border border-orange-500 text-white'
                  : 'bg-white/5 border border-white/10 text-gray-400 hover:border-white/30'
              }`}
            >
              📡 RSS
            </button>
          </div>

          {/* Feed URL for iCal/RSS */}
          {(sourceType === 'ical' || sourceType === 'rss') && (
            <div>
              <input
                type="url"
                value={sourceUrl}
                onChange={(e) => setSourceUrl(e.target.value)}
                placeholder={sourceType === 'ical' ? 'https://calendar.google.com/...' : 'https://example.com/events.rss'}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
              />
              <p className="text-xs text-gray-500 mt-1">Most recent event from feed will be shown</p>
            </div>
          )}

          {/* Manual Event Fields */}
          {sourceType === 'manual' && (
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Event Title *"
                className="col-span-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
              />
              <input
                type="date"
                value={eventDate}
                onChange={(e) => setEventDate(e.target.value)}
                placeholder="Start Date"
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500/50"
              />
              <input
                type="time"
                value={eventTime}
                onChange={(e) => setEventTime(e.target.value)}
                className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500/50"
              />
              {/* Multi-day toggle */}
              <div className="col-span-2 flex items-center justify-between py-1">
                <span className="text-sm text-gray-400">Multi-day event</span>
                <button
                  type="button"
                  onClick={() => { setIsMultiDay(!isMultiDay); if (isMultiDay) setEventEndDate(''); }}
                  className={`w-10 h-5 rounded-full transition-colors ${isMultiDay ? 'bg-orange-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-4 h-4 bg-white rounded-full shadow transition-transform ${isMultiDay ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>
              {isMultiDay && (
                <input
                  type="date"
                  value={eventEndDate}
                  onChange={(e) => setEventEndDate(e.target.value)}
                  min={eventDate || undefined}
                  placeholder="End Date"
                  className="col-span-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500/50"
                />
              )}
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Location"
                className="col-span-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
              />
              <input
                type="url"
                value={registrationUrl}
                onChange={(e) => setRegistrationUrl(e.target.value)}
                placeholder="Ticket/Registration URL"
                className="col-span-2 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
              />
              {/* Image Upload */}
              <div className="col-span-2">
                <label className="block text-xs text-gray-400 mb-1">Event Image (optional)</label>
                {imageUrl ? (
                  <div className="relative">
                    <img 
                      src={imageUrl} 
                      alt="Event" 
                      className="w-full h-32 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => setImageUrl('')}
                      className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="block cursor-pointer">
                    <div className="flex items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-lg hover:border-orange-500/50 transition-colors bg-white/5">
                      {uploadingImage ? (
                        <div className="flex items-center gap-2 text-gray-400">
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm">Uploading...</span>
                        </div>
                      ) : (
                        <div className="text-center">
                          <svg className="w-8 h-8 text-gray-500 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-xs text-gray-400">Click to upload image</span>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setUploadingImage(true);
                          try {
                            const result = await uploadImageToWasabi(file, 'event-images');
                            if (result.imageUrl) {
                              setImageUrl(result.imageUrl);
                              toast.success('Image uploaded!');
                            } else {
                              toast.error('Failed to upload image');
                            }
                          } catch (error) {
                            console.error('Upload error:', error);
                            toast.error('Failed to upload image');
                          } finally {
                            setUploadingImage(false);
                          }
                        }
                      }}
                    />
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Button Text */}
          <input
            type="text"
            value={buttonText}
            onChange={(e) => setButtonText(e.target.value)}
            placeholder="Button Text (default: Get Tickets)"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500/50"
          />

          <div className="flex gap-2">
            {editingEventId && (
              <button
                type="button"
                onClick={clearForm}
                className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm hover:bg-white/20 transition-colors"
              >
                Cancel Edit
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-amber-600 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : editingEventId ? 'Update Event' : 'Add Event'}
            </button>
          </div>
        </form>

        {/* Events Table */}
        {events.length > 0 && (
          <div className="mt-6 border-t border-white/10 pt-4">
            <h3 className="text-sm font-medium text-gray-400 mb-3">Your Events ({events.length})</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {sortedEvents.map((event, index) => (
                <div
                  key={event.id}
                  className={`flex items-center justify-between p-2 rounded-lg ${
                    index === 0 ? 'bg-orange-500/20 border border-orange-500/30' : 'bg-white/5'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {index === 0 && <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded">NEXT</span>}
                      <span className="text-white text-sm truncate">{event.title}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {event.event_date ? new Date(event.event_date).toLocaleDateString() : 'No date'}
                      {event.event_end_date && ` – ${new Date(event.event_end_date).toLocaleDateString()}`}
                      {event.location && ` • ${event.location}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => loadEventForEdit(event)}
                      className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded transition-colors"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(event.id!)}
                      className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-white/10 text-white rounded-lg font-medium hover:bg-white/20 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default BioDashboard;
